// src/components/IntegrationSettings.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Settings, Link2, ExternalLink, Trash2, Check, AlertCircle, Copy } from "react-feather";
import Navigation from "./Navigation";
import LoadingSpinner from "./LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { useShowAiIntelligence, useDefenderXdr, useDefenderIdentity, usePurviewDlp } from "../contexts/FeatureFlagsContext";
import DefenderIdentityCard from "./DefenderIdentityCard";
import JiraConfigurationModal from "./JiraConfigurationModal";
import ServiceNowConfigurationModal from "./ServiceNowConfigurationModal";
import IntegrationAssignmentPanel from "./IntegrationAssignmentPanel";
import SourceCodeConnectionsPanel from "./SourceCodeConnectionsPanel";
import AiProviderConnectorPanel, { ANTHROPIC_COMPLIANCE_FIELDS } from "./AiProviderConnectorPanel";
import UnavailableNote from "./common/UnavailableNote";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import { CONSENT_REDIRECT_URI } from "../config";
import { formatUtcDate, formatUtcDateTime, parseUtcDate } from "../utils/dateTime";
import { KnowBe4SyncStatus, KnowBe4TestResult } from "./KnowBe4Diagnostics";
import { FortinetLastScan, FortinetTestResult, FortinetTraceSwitch } from "./FortinetDiagnostics";
import { gcpSaveValidationErrors } from "../utils/gcpConnectorValidation";

// Security Intelligence card key → GRC connector source key (backend EgressCatalog). When a source
// is unavailable on this installation (Restricted / AirGapped), its card stays visible with
// UnavailableNote and its connect / sync / test / check-permissions / new-connection save controls
// are disabled. Editing or removing a saved connection stays enabled (local data only).
export const GRC_CARD_SOURCES = {
  okta: "OKTA", aws: "AWS", gcp: "GCP", tenable: "TENABLE", splunk: "SPLUNK", wiz: "WIZ",
  snowflake: "SNOWFLAKE", openai: "OPENAI", anthropic: "ANTHROPIC",
};

// Microsoft-based and ticketing integrations → the egress-catalog gate that decides them (a module
// id or a connector source key). Same rule as GRC_CARD_SOURCES: the card stays visible with
// UnavailableNote and its outbound controls are disabled. The Microsoft 365 / Azure, Defender for
// Cloud Apps, KnowBe4 and Azure DevOps cards are all saved through the one Microsoft connection.
// Defender XDR and Defender for Identity are toggles inside the Microsoft panel; Jira and ServiceNow
// are the ITSM cards (and their configuration modals).

export const PLATFORM_CARD_GATES = {
  microsoft: { source: "MICROSOFT" },
  cloudapps: { source: "MICROSOFT" },
  knowbe4: { source: "MICROSOFT" },
  azdevops: { source: "MICROSOFT" },
  defenderXdr: { module: "defenderXdr" },
  defenderIdentity: { module: "defenderIdentity" },
  jira: { module: "ticketing" },
  servicenow: { module: "ticketing" },
};

// Defender XDR per-class coverage (GET /defender-xdr/config findingClasses). A quiet class is neutral
// information, never degraded health: the wording is "no new findings since", never "sync failed",
// and the lines carry the same neutral styling as "Last successful sync". The (none) / (other)
// sentinels are internal markers and are never shown. Created30d / OpenRisks are last-read
// snapshots and are not rendered as numbers.
const FINDING_CLASS_SENTINELS = new Set(["(none)", "(other)"]);
export const DLP_OCTOBER_NOTE =
  "Microsoft changed how DLP alerts are surfaced in October 2026; new DLP findings may have stopped arriving for that reason.";

// UTC, "Oct 15, 2026" — the same format as the risk's not-reported date.
const formatFindingClassDate = (value) => {
  const d = parseUtcDate(value);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : null;
};

export const defenderFindingClassLines = (classes) => (Array.isArray(classes) ? classes : [])
  .filter(c => c && typeof c.class === "string" && c.class && !FINDING_CLASS_SENTINELS.has(c.class))
  .flatMap(c => {
    const date = formatFindingClassDate(c.newestFindingAt);
    const lines = [{
      key: `class-${c.class}`,
      testId: "defender-xdr-class-line",
      text: date
        ? `No new ${c.class} findings since ${date}`
        : `${c.class} findings have been seen, but none carried a usable creation date`,
    }];
    if (c.class === "DLP" && c.created30d === 0) {
      lines.push({ key: "dlp-october-note", testId: "defender-xdr-dlp-note", text: DLP_OCTOBER_NOTE });
    }
    return lines;
  });

// What AI Intelligence's Google Cloud discovery needs, shown in the Google Cloud panel's
// Required access box. Hand-maintained copy of backend/Services/AiDiscovery/Gcp/
// GcpDiscoveryRequirements.cs (DisplayGrants and Apis, same order and wording); deriving it from
// the backend was declined. IntegrationSettings.test.js reads the C# file and fails on any drift.
// The connector's Test button probes only the entries marked required.
export const GCP_AI_DISCOVERY_GRANTS = [
  { grant: "roles/cloudasset.viewer", enables: "The Cloud Asset Inventory search", whereHeld: "The connector's scope parent (the organization, or the scanned project)", required: true },
  { grant: "serviceusage.services.use (in roles/serviceusage.serviceUsageConsumer)", enables: "Required by Cloud Asset Inventory of its caller", whereHeld: "The project that owns the connector's service account, where the APIs are enabled", required: true },
  { grant: "roles/aiplatform.viewer", enables: "Reasoning engine and endpoint detail", whereHeld: "The connector's scope parent, which covers each project hosting a Vertex AI resource", required: true },
  { grant: "iam.serviceAccounts.get (in roles/iam.serviceAccountViewer)", enables: "Containment state read-back", whereHeld: "The project that owns each agent's service account (the {project} in …@{project}.iam.gserviceaccount.com)", required: false },
  { grant: "resourcemanager.projects.getIamPolicy (in roles/iam.securityReviewer, which the posture setup already grants at the connector's scope)", enables: "Role bindings of each agent's service account", whereHeld: "Each project hosting a Vertex AI agent, granted there or inherited from a folder or the organization", required: false },
  { grant: "roles/policyanalyzer.activityAnalysisViewer", enables: "Last authentication (Activity Analyzer)", whereHeld: "The project that owns each service account (derived from its email)", required: false },
  { grant: "roles/recommender.iamViewer", enables: "Policy insights (unused roles)", whereHeld: "Each project hosting a Vertex AI agent, read for that agent's service account", required: false },
  { grant: "roles/monitoring.viewer", enables: "Vertex AI token usage", whereHeld: "Each project hosting a discovered Vertex AI agent or endpoint", required: false },
];

export const GCP_AI_DISCOVERY_APIS = [
  { serviceId: "cloudasset.googleapis.com", purpose: "Finding reasoning engines and endpoints", required: true },
  { serviceId: "aiplatform.googleapis.com", purpose: "The engine's service account; the endpoint's deployed models and their service accounts", required: true },
  { serviceId: "iam.googleapis.com", purpose: "Containment state read-back; containment and reversal; the Test grant row", required: false },
  { serviceId: "policyanalyzer.googleapis.com", purpose: "When each service account last authenticated", required: false },
  { serviceId: "recommender.googleapis.com", purpose: "Roles Google reports as unused (policy insights)", required: false },
  { serviceId: "monitoring.googleapis.com", purpose: "Vertex AI token usage", required: false },
];

// Every IAM action AI Intelligence's AWS work calls, shown in the AWS panel's AI Intelligence box.
// Hand-maintained copy of backend/Services/AiDiscovery/AwsDiscoveryRequirements.cs (Actions, same
// order and wording; tier is the C# AwsActionTier name). IntegrationSettings.test.js reads the C# file
// and fails on any drift. The connector's Test button probes only the DiscoveryCritical entries.
export const AWS_AI_DISCOVERY_ACTIONS = [
  { action: "bedrock:ListAgents", enables: "Finding Bedrock agents in each scanned region", tier: "DiscoveryCritical", caveat: null },
  { action: "bedrock:GetAgent", enables: "Each agent's detail, including the IAM role it runs as", tier: "DiscoveryCritical", caveat: null },
  { action: "bedrock:ListAgentActionGroups", enables: "Action groups configured for each agent", tier: "DiscoveryCritical", caveat: null },
  { action: "bedrock:ListAgentKnowledgeBases", enables: "Knowledge-base associations configured for each agent", tier: "DiscoveryCritical", caveat: null },
  { action: "sagemaker:ListEndpoints", enables: "Finding SageMaker endpoints as model deployments", tier: "DiscoveryCritical", caveat: null },
  { action: "bedrock:ListTagsForResource", enables: "Owner tags on an agent", tier: "Enrichment", caveat: null },
  { action: "bedrock:GetAgentActionGroup", enables: "The Lambda executor configured for an action group", tier: "Enrichment", caveat: null },
  { action: "lambda:GetFunction", enables: "That Lambda's configuration, including the role it runs as", tier: "Enrichment", caveat: null },
  { action: "iam:ListAttachedRolePolicies", enables: "Managed policies attached to an agent execution role or an action-group Lambda role", tier: "Enrichment", caveat: null },
  { action: "iam:ListRolePolicies", enables: "Inline policies on those same roles", tier: "Enrichment", caveat: null },
  { action: "iam:GetPolicy", enables: "A managed policy's current version", tier: "Enrichment", caveat: null },
  { action: "iam:GetPolicyVersion", enables: "A managed policy's permission document", tier: "Enrichment", caveat: null },
  { action: "iam:GetRolePolicy", enables: "An inline policy's permission document", tier: "Enrichment", caveat: null },
  { action: "iam:GenerateServiceLastAccessedDetails", enables: "Starting an Access Advisor report for an agent's role", tier: "BehaviouralUsage", caveat: null },
  { action: "iam:GetServiceLastAccessedDetails", enables: "Reading that report: which services the role has actually used", tier: "BehaviouralUsage", caveat: null },
  { action: "iam:GetRole", enables: "An agent role's last-used time, for dormancy findings", tier: "BehaviouralUsage", caveat: null },
  { action: "cloudwatch:ListMetrics", enables: "Finding which Bedrock models have token metrics", tier: "BehaviouralUsage", caveat: null },
  { action: "cloudwatch:GetMetricData", enables: "Bedrock token counts per model per day", tier: "BehaviouralUsage", caveat: null },
  { action: "ce:GetCostAndUsage", enables: "Bedrock spend alongside those token counts", tier: "BehaviouralUsage", caveat: "Cost Explorer must also be enabled on the AWS account, separately from any IAM grant, and activation can take up to 24 hours. Granting this action alone does not fix 'User not enabled for cost explorer access'." },
  { action: "iam:GetUser", enables: "Identifying which principal the connector's keys belong to", tier: "ReadinessCheck", caveat: null },
  { action: "iam:SimulatePrincipalPolicy", enables: "The Test button's readiness check itself", tier: "ReadinessCheck", caveat: null },
];

// How each AWS tier is shown: its badge label, its tone (critical only where absence stops
// discovery), and what leaving the tier out costs. Array order is the display order.
const AWS_TIER_DISPLAY = [
  { tier: "DiscoveryCritical", label: "Discovery-critical", tone: "critical", consequence: "Without any one of these, discovery stops and the AWS estate shows as empty." },
  { tier: "Enrichment", label: "Enrichment", tone: "optional", consequence: "Without these, agents and endpoints are still discovered, with less detail." },
  { tier: "BehaviouralUsage", label: "Behavioural & usage", tone: "optional", consequence: "Separate features that each degrade on their own; discovery is unaffected." },
  { tier: "ReadinessCheck", label: "Readiness check", tone: "optional", consequence: "Without these, the connector's Test button cannot answer; discovery is unaffected." },
];

// Requirement marker for the AI Intelligence discovery entries above. tone is "critical" | "optional":
// the colour says whether absence stops discovery, the label carries the detail.
const RequirementBadge = ({ label, tone }) => (
  <span className={`inline-block shrink-0 text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 mr-1.5 ${tone === "critical" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500"}`}>
    {label}
  </span>
);

const IntegrationSettings = () => {
  // ── ITSM state ──────────────────────────────────────────────────────────────
  const [jiraStatus, setJiraStatus] = useState(null);
  const [serviceNowStatus, setServiceNowStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJiraModal, setShowJiraModal] = useState(false);
  const [showServiceNowModal, setShowServiceNowModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectingServiceNow, setDisconnectingServiceNow] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);

  // ── Microsoft connector state ────────────────────────────────────────────────
  const [connection, setConnection] = useState(null);
  const [connLoading, setConnLoading] = useState(false);
  // KnowBe4 panel: Test Connection result (null | { error: true } | KnowBe4TestResult) and the
  // support-only detailed-logging toggle.
  const [knowBe4Testing, setKnowBe4Testing] = useState(false);
  const [knowBe4TestResult, setKnowBe4TestResult] = useState(null);
  const [knowBe4LoggingSaving, setKnowBe4LoggingSaving] = useState(false);
  const [knowBe4LoggingError, setKnowBe4LoggingError] = useState(false);
  const [connSaving, setConnSaving] = useState(false);
  // The Microsoft connection's save error is shown beside the Save button that was clicked, not in the
  // page banner: the Cloud Apps, KnowBe4 and Azure DevOps forms sit far below it, so a banner error
  // went unseen. Cleared when a save starts, so a successful save leaves no stale message.
  const [connSaveError, setConnSaveError] = useState(null);
  const [connForm, setConnForm] = useState({ tenantId: "", tenantName: "", subscriptionId: "", graphActivityWorkspaceId: "", secureScoreEnabled: true, mfaEvidenceEnabled: true, defenderEnabled: false, nhiEnabled: false, nhiAgentIdEnabled: false, aiDiscoveryEnabled: false, knowBe4Enabled: false, knowBe4ApiKey: "", knowBe4ApiKeySet: false, knowBe4Region: "us", azDevOpsEnabled: false, azDevOpsConnectionId: null, aiDiscoverySubsEnabled: false, aiDiscoverySubscriptionIdsText: "", cloudAppsAlertsEnabled: false, cloudAppsDiscoveryEnabled: false, cloudAppsPortalUrl: "", cloudAppsApiToken: "", cloudAppsApiTokenSet: false, responseEnabled: false, purviewDlpEnabled: false });
  const [connFormDirty, setConnFormDirty] = useState(false);
  const [roleCmdCopied, setRoleCmdCopied] = useState(false);
  const [msDisconnecting, setMsDisconnecting] = useState(false);
  const showAiIntelligence = useShowAiIntelligence();
  // Features:DefenderXdr — off in Production, so the whole Defender XDR surface (capability card,
  // toggle, sync panel, and its API calls) is hidden there. The backend 404s the routes regardless.
  const defenderXdrFeature = useDefenderXdr();
  // Features:DefenderIdentity — the identity inventory (Phase 1). Same gating as XDR.
  const defenderIdentityFeature = useDefenderIdentity();
  // Features:PurviewDlp — off by default: the DLP read needs a Graph consent
  // (SecurityAlert.Read.All) most tenants have not granted, so the toggle stays hidden until
  // the backend says the feature is on.
  const purviewDlpFeature = usePurviewDlp();

  // Promise-based type-Confirm dialog for destructive connection removals. Handlers await it
  // exactly like window.confirm: `if (!(await requestConfirmAsync(title, msg))) return;`
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, resolve }
  const requestConfirmAsync = useCallback(
    (title, message) => new Promise((resolve) => setConfirmModal({ title, message, resolve })),
    []);
  const closeConfirmModal = (result) => {
    confirmModal?.resolve(result);
    setConfirmModal(null);
  };
  const [connSaveSuccess, setConnSaveSuccess] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResults, setDiagResults] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [permsNoteDismissed, setPermsNoteDismissed] = useState(false);

  // ── Microsoft Surface Scan (Defender for Cloud) state ────────────────────────
  const [esmConfigError, setEsmConfigError] = useState(false);
  const [msScanEnabled, setMsScanEnabled] = useState(false);
  const [msScanSaving, setMsScanSaving] = useState(false);
  const [defenderStatus, setDefenderStatus] = useState(null);
  const [testingDefender, setTestingDefender] = useState(false);
  const [scanningNow, setScanningNow] = useState(false);
  const [scanQueued, setScanQueued] = useState(false);

  // ── Okta connector state ─────────────────────────────────────────────────────
  const [oktaConnectors, setOktaConnectors] = useState([]);
  const [oktaFormOpen, setOktaFormOpen] = useState(false);
  const [oktaForm, setOktaForm] = useState({ displayName: "", oktaOrgUrl: "", oktaApiToken: "" });
  const [oktaEditId, setOktaEditId] = useState(null);
  const [oktaSaving, setOktaSaving] = useState(false);
  const [oktaSyncingIds, setOktaSyncingIds] = useState(new Set());
  const [oktaDeleteIds, setOktaDeleteIds] = useState(new Set());

  // ── AWS connector state ──────────────────────────────────────────────────────
  const [awsConnectors, setAwsConnectors] = useState([]);
  const [awsFormOpen, setAwsFormOpen] = useState(false);
  const [awsForm, setAwsForm] = useState({ displayName: "", awsAccessKeyId: "", awsSecretAccessKey: "", awsRegion: "us-east-1", awsAccountId: "" });
  const [awsEditId, setAwsEditId] = useState(null);
  const [awsSaving, setAwsSaving] = useState(false);
  const [awsSyncingIds, setAwsSyncingIds] = useState(new Set());
  const [awsDeleteIds, setAwsDeleteIds] = useState(new Set());

  // ── Google Cloud connector state ─────────────────────────────────────────────
  // The service account key lives in gcpForm only until save or cancel; it is never read back.
  const [gcpConnectors, setGcpConnectors] = useState([]);
  const [gcpFormOpen, setGcpFormOpen] = useState(false);
  const [gcpForm, setGcpForm] = useState({ displayName: "", gcpScopeType: "ORGANIZATION", gcpScopeId: "", gcpServiceAccountKeyJson: "" });
  const [gcpEditId, setGcpEditId] = useState(null);
  const [gcpSaving, setGcpSaving] = useState(false);
  const [gcpSyncingIds, setGcpSyncingIds] = useState(new Set());
  const [gcpDeleteIds, setGcpDeleteIds] = useState(new Set());

  // ── Tenable connector state ──────────────────────────────────────────────────
  const [tenableConnectors, setTenableConnectors] = useState([]);
  const [tenableFormOpen, setTenableFormOpen] = useState(false);
  const [tenableForm, setTenableForm] = useState({ displayName: "", tenableAccessKey: "", tenableSecretKey: "" });
  const [tenableEditId, setTenableEditId] = useState(null);
  const [tenableSaving, setTenableSaving] = useState(false);
  const [tenableSyncingIds, setTenableSyncingIds] = useState(new Set());
  const [tenableDeleteIds, setTenableDeleteIds] = useState(new Set());

  // ── Splunk connector state ───────────────────────────────────────────────────
  const [splunkConnectors, setSplunkConnectors] = useState([]);
  const [splunkFormOpen, setSplunkFormOpen] = useState(false);
  const [splunkForm, setSplunkForm] = useState({ displayName: "", splunkHost: "", splunkPort: "8089", splunkToken: "", splunkUsername: "", splunkPassword: "", splunkIndex: "", splunkAllowSelfSignedCert: false });
  const [splunkEditId, setSplunkEditId] = useState(null);
  const [splunkSaving, setSplunkSaving] = useState(false);
  const [splunkSyncingIds, setSplunkSyncingIds] = useState(new Set());
  const [splunkDeleteIds, setSplunkDeleteIds] = useState(new Set());
  const [splunkAuthMode, setSplunkAuthMode] = useState("token"); // "token" | "userpass"

  // ── Wiz connector state ──────────────────────────────────────────────────────
  const [wizConnectors, setWizConnectors] = useState([]);
  const [openAiConnectors, setOpenAiConnectors] = useState([]);
  const [anthropicConnectors, setAnthropicConnectors] = useState([]);
  const [wizFormOpen, setWizFormOpen] = useState(false);
  const [wizForm, setWizForm] = useState({ displayName: "", wizClientId: "", wizClientSecret: "", wizApiEndpoint: "https://api.app.wiz.io/graphql" });
  const [wizEditId, setWizEditId] = useState(null);
  const [wizSaving, setWizSaving] = useState(false);
  const [wizSyncingIds, setWizSyncingIds] = useState(new Set());
  const [wizDeleteIds, setWizDeleteIds] = useState(new Set());

  // ── Fortinet connector state ─────────────────────────────────────────────────
  const [fortinetConfig, setFortinetConfig] = useState(null);
  const [fortinetLoading, setFortinetLoading] = useState(false);
  const [fortinetSaving, setFortinetSaving] = useState(false);
  const [fortinetDeleting, setFortinetDeleting] = useState(false);
  const [fortinetSaved, setFortinetSaved] = useState(false);
  const [fortinetError, setFortinetError] = useState(null);
  const [fgUrl, setFgUrl] = useState("");
  const [fgKey, setFgKey] = useState("");
  const [showFgKey, setShowFgKey] = useState(false);
  const [fmgUrl, setFmgUrl] = useState("");
  const [fmgKey, setFmgKey] = useState("");
  const [showFmgKey, setShowFmgKey] = useState(false);
  const [fortinetAdom, setFortinetAdom] = useState("root");
  const [fortinetFrequency, setFortinetFrequency] = useState(24);
  const [fortinetEnabled, setFortinetEnabled] = useState(true);
  const [fortinetAllowSelfSignedCert, setFortinetAllowSelfSignedCert] = useState(false);
  // Test connection / Scan now / trace switch (POST /fortinet/test-connection, /scan, /trace).
  const [fortinetTesting, setFortinetTesting] = useState(false);
  const [fortinetTestResult, setFortinetTestResult] = useState(null); // { diagnostics, managedDevices, signalCount } | { message }
  const [fortinetScanning, setFortinetScanning] = useState(false);
  const [fortinetScanStarted, setFortinetScanStarted] = useState(false);
  const [fortinetTraceSaving, setFortinetTraceSaving] = useState(false);

  // ── Snowflake connector state ────────────────────────────────────────────────
  const [snowflakeConfig, setSnowflakeConfig] = useState(null);
  const [snowflakeLoading, setSnowflakeLoading] = useState(false);
  const [snowflakeSaving, setSnowflakeSaving] = useState(false);
  const [snowflakeDeleting, setSnowflakeDeleting] = useState(false);
  const [snowflakeSaved, setSnowflakeSaved] = useState(false);
  const [snowflakeSyncing, setSnowflakeSyncing] = useState(false);
  const [snowflakeError, setSnowflakeError] = useState(null);
  const [sfAccountId, setSfAccountId] = useState("");
  const [sfUsername, setSfUsername] = useState("");
  const [sfPassword, setSfPassword] = useState("");
  const [sfWarehouse, setSfWarehouse] = useState("");
  const [sfDatabase, setSfDatabase] = useState("");
  const [sfSchema, setSfSchema] = useState("FINDINGS");
  const [sfTableName, setSfTableName] = useState("SECURITY_FINDINGS");
  const [sfEnabled, setSfEnabled] = useState(true);
  const [sfServiceRole, setSfServiceRole] = useState("");
  const [sfAuthMode, setSfAuthMode] = useState(0);          // 0=Password, 1=KeyPair
  const [sfPrivateKey, setSfPrivateKey] = useState("");
  const [sfEnableCustomTable, setSfEnableCustomTable] = useState(true);
  const [sfEnableNativeScan, setSfEnableNativeScan] = useState(true);
  const [sfEnableTrustCenter, setSfEnableTrustCenter] = useState(false);
  const [sfPermissions, setSfPermissions] = useState(null); // null | { canConnect, accountUsage, trustCenter, customTable }
  const [sfCheckingPerms, setSfCheckingPerms] = useState(false);
  const [sfCopyingSql, setSfCopyingSql] = useState(false);

  // ── Defender XDR connector state ─────────────────────────────────────────────
  const [defenderXdrConfig, setDefenderXdrConfig] = useState(null);
  const [defenderXdrLoading, setDefenderXdrLoading] = useState(false);
  const [defenderXdrSaving, setDefenderXdrSaving] = useState(false);
  const [defenderXdrEnabled, setDefenderXdrEnabled] = useState(false);
  const [defenderXdrSyncing, setDefenderXdrSyncing] = useState(false);
  const [defenderXdrTestLoading, setDefenderXdrTestLoading] = useState(false);
  const [defenderXdrTestResult, setDefenderXdrTestResult] = useState(null);
  const [defenderXdrError, setDefenderXdrError] = useState(null);
  const [defenderXdrSaved, setDefenderXdrSaved] = useState(false);

  // ── Defender for Identity inventory toggle ───────────────────────────────────
  // The panel below the toggle owns its own sync/test state (DefenderIdentityCard); this file
  // carries only the saved value, the draft value, and the save call.
  const [defenderIdentityConfig, setDefenderIdentityConfig] = useState(null);
  const [defenderIdentityEnabled, setDefenderIdentityEnabled] = useState(false);
  const [defenderIdentitySaving, setDefenderIdentitySaving] = useState(false);
  const [defenderIdentityError, setDefenderIdentityError] = useState(null);
  const [defenderIdentityRefresh, setDefenderIdentityRefresh] = useState(0);

  // ── Source-code connections (GitHub/GitLab) state ───────────────────────────
  const [sourceCodeConnections, setSourceCodeConnections] = useState([]);

  // ── Expanded panel state ─────────────────────────────────────────────────────
  const [expandedSection, setExpandedSection] = useState(null); // null | "microsoft" | "okta" | "aws" | "gcp" | "tenable" | "splunk" | "wiz" | "fortinet" | "azdevops" | "snowflake" | "sourcecode"
  // The connection save error belongs to the panel it was raised in: the Cloud Apps, KnowBe4, Azure DevOps
  // and Microsoft panels share one save, so without this a Cloud Apps error reappears under KnowBe4's button.
  useEffect(() => { setConnSaveError(null); }, [expandedSection]);
  const location = useLocation();

  const apiCall = useApi();
  const { canManageSecurityInsights, canManageIntegrations, canManageOrganizationRisks, canAdministerOrganization, isSuperAdmin, isSupportAdmin, canManageFortinetTracing } = useRBAC();
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canITSM = canManageIntegrations(selectedOrg?.organizationId);
  // Mirrors CanManageIntegrationsAsync (ORG, DEAL or BU RISK_MANAGER+, or a SELF-scoped
  // IntegrationAssignment). Gates the Microsoft, Defender XDR/Identity, Azure DevOps, KnowBe4,
  // Cloud App Discovery and source-code connection controls, and the Security Intelligence section.
  const canIntegrations = canManageIntegrations(selectedOrg?.organizationId);
  // Mirrors CanManageSecurityInsights (RISK_MANAGER+ at ORG only), which is narrower than
  // canManageIntegrations. Every endpoint these controls call checks it:
  // - POST/PUT/DELETE /grc-connectors and POST /grc-connectors/{id}/sync (the Okta, AWS, Google
  //   Cloud, Tenable, Splunk, Wiz and Snowflake connectors, and the OpenAI/Anthropic provider panels)
  // - GET /grc-connectors/{id}/diagnostic (the AWS and Google Cloud row Test buttons)
  // - GET /grc-connectors/{id}/snowflake-onboarding-sql and snowflake-check-permissions
  const canSecurityInsights = canManageSecurityInsights(selectedOrg?.organizationId);
  const { isModuleAvailable, isSourceAvailable } = useModuleAvailability();
  // True when the card's GRC source cannot be reached on this installation (non-GRC cards: false).
  const sourceBlocked = (cardKey) => !!GRC_CARD_SOURCES[cardKey] && !isSourceAvailable(GRC_CARD_SOURCES[cardKey]);
  // Same for the Microsoft-based and ticketing integrations (PLATFORM_CARD_GATES).
  const platformBlocked = (cardKey) => {
    const gate = PLATFORM_CARD_GATES[cardKey];
    if (!gate) return false;
    return gate.module ? !isModuleAvailable(gate.module) : !isSourceAvailable(gate.source);
  };
  const msBlocked = platformBlocked("microsoft");
  const xdrBlocked = platformBlocked("defenderXdr");
  const identityBlocked = platformBlocked("defenderIdentity");
  const ticketingBlocked = platformBlocked("jira");
  const surfaceScanAvailable = isModuleAvailable("surfaceScan");
  const canManageAssignments = canManageOrganizationRisks(selectedOrg?.organizationId);
  // POST and DELETE /fortinet/config check CanAdministerOrganization (ADMIN at ORG), narrower than
  // security insights.
  const canAdministerOrg = canAdministerOrganization(selectedOrg?.organizationId);
  // POST /fortinet/trace checks CanManageFortinetTracing (SuperAdmin only, not Support admin).
  const canFortinetTrace = canManageFortinetTracing();

  // ── ITSM fetch functions ─────────────────────────────────────────────────────
  const fetchJiraStatus = useCallback(async () => {
    try {
      const status = await apiCall("GET", "/integrations/jira/status");
      setJiraStatus(status);
    } catch (err) {
      setJiraStatus({ isConfigured: false });
    }
  }, [apiCall]);

  const fetchServiceNowStatus = useCallback(async () => {
    try {
      const status = await apiCall("GET", "/integrations/servicenow/status");
      setServiceNowStatus(status);
    } catch (err) {
      setServiceNowStatus({ isConfigured: false });
    }
  }, [apiCall]);

  // ── Microsoft fetch ──────────────────────────────────────────────────────────
  const fetchConnection = useCallback(async () => {
    setConnLoading(true);
    try {
      const data = await apiCall("GET", "/security-insights/connection");
      setConnection(data);
      if (data) setConnForm({ tenantId: data.tenantId || "", tenantName: data.tenantName || "", subscriptionId: data.subscriptionId || "", graphActivityWorkspaceId: data.graphActivityWorkspaceId || "", secureScoreEnabled: data.secureScoreEnabled ?? true, mfaEvidenceEnabled: data.mfaEvidenceEnabled ?? true, defenderEnabled: data.defenderEnabled ?? false, nhiEnabled: data.nhiEnabled ?? false, nhiAgentIdEnabled: data.nhiAgentIdEnabled ?? false, aiDiscoveryEnabled: data.aiDiscoveryEnabled ?? false, knowBe4Enabled: data.knowBe4Enabled ?? false, knowBe4ApiKey: "", knowBe4ApiKeySet: data.knowBe4ApiKeySet ?? false, knowBe4Region: data.knowBe4Region || "us", azDevOpsEnabled: data.azDevOpsEnabled ?? false, azDevOpsConnectionId: data.azDevOpsConnectionId ?? null, aiDiscoverySubsEnabled: (data.aiDiscoverySubscriptionIds || []).length > 0, aiDiscoverySubscriptionIdsText: (data.aiDiscoverySubscriptionIds || []).join("\n"), cloudAppsAlertsEnabled: data.cloudAppsAlertsEnabled ?? false, cloudAppsDiscoveryEnabled: data.cloudAppsDiscoveryEnabled ?? false, cloudAppsPortalUrl: data.cloudAppsPortalUrl || "", cloudAppsApiToken: "", cloudAppsApiTokenSet: data.cloudAppsApiTokenSet ?? false, responseEnabled: data.responseEnabled ?? false, purviewDlpEnabled: data.purviewDlpEnabled ?? false });
    } catch { /* ignore */ } finally { setConnLoading(false); }
  }, [apiCall]);

  // Removes the Microsoft connection entirely (tenant migration path): the next connection
  // starts in Pending so the admin-consent flow re-appears for the new directory.
  const disconnectMicrosoft = async () => {
    if (!(await requestConfirmAsync("Remove Microsoft Connection",
      "Are you sure you want to remove the Microsoft connection? This will delete all Microsoft-sourced " +
      "security controls and associated risks, including AI asset risks. You will need to re-enter " +
      "details and re-run admin consent to reconnect."))) return;
    setMsDisconnecting(true);
    try {
      await apiCall("DELETE", "/security-insights/connection");
      setConnection(null);
      setConnForm({ tenantId: "", tenantName: "", subscriptionId: "", graphActivityWorkspaceId: "", secureScoreEnabled: true, mfaEvidenceEnabled: true, defenderEnabled: false, nhiEnabled: false, nhiAgentIdEnabled: false, aiDiscoveryEnabled: false, knowBe4Enabled: false, knowBe4ApiKey: "", knowBe4ApiKeySet: false, knowBe4Region: "us", azDevOpsEnabled: false, azDevOpsConnectionId: null, aiDiscoverySubsEnabled: false, aiDiscoverySubscriptionIdsText: "", cloudAppsAlertsEnabled: false, cloudAppsDiscoveryEnabled: false, cloudAppsPortalUrl: "", cloudAppsApiToken: "", cloudAppsApiTokenSet: false, responseEnabled: false, purviewDlpEnabled: false });
      setConnFormDirty(false);
      setDiagResults(null);
    } catch { /* surface nothing — fetchConnection below re-syncs state */ }
    finally { setMsDisconnecting(false); fetchConnection(); }
  };

  // ── Microsoft Surface Scan (Defender for Cloud) fetch ─────────────────────────
  const fetchEsmConfig = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/esm/config");
      setMsScanEnabled(data?.msEnabled ?? false);
      setEsmConfigError(false);
    } catch { setEsmConfigError(true); }
  }, [apiCall]);

  // ── GRC connector fetch ──────────────────────────────────────────────────────
  const fetchConnectors = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/grc-connectors");
      setOktaConnectors((data || []).filter(c => c.sourceSystem === "OKTA"));
      setAwsConnectors((data || []).filter(c => c.sourceSystem === "AWS"));
      setGcpConnectors((data || []).filter(c => c.sourceSystem === "GCP"));
      setTenableConnectors((data || []).filter(c => c.sourceSystem === "TENABLE"));
      setSplunkConnectors((data || []).filter(c => c.sourceSystem === "SPLUNK"));
      setWizConnectors((data || []).filter(c => c.sourceSystem === "WIZ"));
      setOpenAiConnectors((data || []).filter(c => c.sourceSystem === "OPENAI"));
      setAnthropicConnectors((data || []).filter(c => c.sourceSystem === "ANTHROPIC"));
    } catch { /* non-critical */ }
  }, [apiCall]);

  // ── Source-code connections fetch ────────────────────────────────────────────
  const fetchSourceCodeConnections = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/source-code-connections");
      setSourceCodeConnections(data || []);
    } catch { /* non-critical */ }
  }, [apiCall]);

  // ── Fortinet fetch / save / delete ──────────────────────────────────────────
  const fetchFortinetConfig = useCallback(async () => {
    setFortinetLoading(true);
    try {
      const data = await apiCall("GET", "/fortinet/config");
      setFortinetConfig(data);
      if (data) {
        setFgUrl(data.fortigateUrl || "");
        setFmgUrl(data.fortimanagerUrl || "");
        setFortinetAdom(data.fortimanagerAdom || "root");
        setFortinetFrequency(data.scanFrequencyHours || 24);
        setFortinetEnabled(data.isEnabled ?? true);
        setFortinetAllowSelfSignedCert(data.allowSelfSignedCert ?? false);
      }
    } catch { /* non-critical */ } finally { setFortinetLoading(false); }
  }, [apiCall]);

  const handleFortinetSave = useCallback(async () => {
    setFortinetSaving(true); setFortinetError(null); setFortinetSaved(false);
    try {
      const payload = {
        fortigateUrl: fgUrl.trim() || null,
        fortigateApiKey: fgKey.trim() || null,
        fortimanagerUrl: fmgUrl.trim() || null,
        fortimanagerApiKey: fmgKey.trim() || null,
        fortimanagerAdom: fortinetAdom.trim() || "root",
        isEnabled: fortinetEnabled,
        scanFrequencyHours: Number(fortinetFrequency) || 24,
        allowSelfSignedCert: fortinetAllowSelfSignedCert,
      };
      const saved = await apiCall("POST", "/fortinet/config", payload);
      setFortinetConfig(saved); setFgKey(""); setFmgKey("");
      setFortinetSaved(true); setTimeout(() => setFortinetSaved(false), 3000);
    } catch { setFortinetError("Failed to save Fortinet configuration."); }
    finally { setFortinetSaving(false); }
  }, [apiCall, fgUrl, fgKey, fmgUrl, fmgKey, fortinetAdom, fortinetEnabled, fortinetFrequency, fortinetAllowSelfSignedCert]);

  const handleFortinetDelete = useCallback(async () => {
    if (!(await requestConfirmAsync("Remove Fortinet Connection",
      "Are you sure you want to remove the Fortinet connection? This will stop automated scanning and " +
      "delete all associated risks."))) return;
    setFortinetDeleting(true); setFortinetError(null);
    try {
      await apiCall("DELETE", "/fortinet/config");
      setFortinetConfig(null);
      setFgUrl(""); setFgKey(""); setFmgUrl(""); setFmgKey("");
      setFortinetAdom("root"); setFortinetFrequency(24); setFortinetEnabled(true);
      setFortinetAllowSelfSignedCert(false);
    } catch { setFortinetError("Failed to remove Fortinet configuration."); }
    finally { setFortinetDeleting(false); }
  }, [apiCall, fortinetAllowSelfSignedCert]);

  // Unsaved edits: a form field differs from the last loaded or saved config (compared the way
  // handleFortinetSave normalises them), or either API-key input is non-empty.
  const fortinetDirty = !!fortinetConfig && (
    fgUrl.trim() !== (fortinetConfig.fortigateUrl || "") ||
    fmgUrl.trim() !== (fortinetConfig.fortimanagerUrl || "") ||
    (fortinetAdom.trim() || "root") !== (fortinetConfig.fortimanagerAdom || "root") ||
    (Number(fortinetFrequency) || 24) !== (fortinetConfig.scanFrequencyHours || 24) ||
    fortinetEnabled !== (fortinetConfig.isEnabled ?? true) ||
    fortinetAllowSelfSignedCert !== (fortinetConfig.allowSelfSignedCert ?? false) ||
    fgKey !== "" || fmgKey !== "");

  const handleFortinetTest = useCallback(async () => {
    if (fortinetDirty) { setFortinetTestResult({ message: "Save your settings before testing." }); return; }
    setFortinetTesting(true); setFortinetTestResult(null);
    try {
      const data = await apiCall("POST", "/fortinet/test-connection");
      setFortinetTestResult({
        diagnostics: data?.diagnostics || [],
        managedDevices: data?.managedDevices ?? null,
        signalCount: data?.signalCount ?? 0,
      });
    } catch (err) {
      setFortinetTestResult({ message: err?.response?.data?.message || "Test failed — see server logs." });
    } finally { setFortinetTesting(false); }
  }, [apiCall, fortinetDirty]);

  const handleFortinetScan = useCallback(async () => {
    setFortinetScanning(true); setFortinetScanStarted(false); setFortinetError(null);
    try {
      await apiCall("POST", "/fortinet/scan");
      setFortinetScanStarted(true);
    } catch (err) { setFortinetError(err?.response?.data?.message || "Failed to start Fortinet scan."); }
    finally { setFortinetScanning(false); }
  }, [apiCall]);

  const handleFortinetTrace = useCallback(async (enabled) => {
    setFortinetTraceSaving(true); setFortinetError(null);
    try {
      const updated = await apiCall("POST", "/fortinet/trace", { enabled });
      if (updated) setFortinetConfig(updated);
    } catch (err) { setFortinetError(err?.response?.data?.message || "Failed to change diagnostic tracing."); }
    finally { setFortinetTraceSaving(false); }
  }, [apiCall]);

  // ── Snowflake fetch / save / delete / sync (GRC connector, sourceSystem=SNOWFLAKE) ────────────
  const fetchSnowflakeConfig = useCallback(async () => {
    setSnowflakeLoading(true);
    try {
      const data = await apiCall("GET", "/grc-connectors");
      const connector = (data || []).find(c => c.sourceSystem === "SNOWFLAKE") || null;
      setSnowflakeConfig(connector);
      if (connector) {
        setSfAccountId(connector.snowflakeAccountIdentifier || "");
        setSfUsername(connector.snowflakeUsername || "");
        setSfWarehouse(connector.snowflakeWarehouse || "");
        setSfDatabase(connector.snowflakeDatabase || "");
        setSfSchema(connector.snowflakeSchema || "FINDINGS");
        setSfTableName(connector.snowflakeTableName || "SECURITY_FINDINGS");
        setSfEnabled(connector.isActive ?? true);
        setSfAuthMode(connector.snowflakeAuthMode === "KeyPair" ? 1 : 0);
        setSfServiceRole(connector.snowflakeServiceRole || "");
        setSfEnableCustomTable(connector.snowflakeEnableCustomTableScan ?? true);
        setSfEnableNativeScan(connector.snowflakeEnableNativeRiskScan ?? true);
        setSfEnableTrustCenter(connector.snowflakeEnableTrustCenter ?? false);
      }
    } catch { /* non-critical */ } finally { setSnowflakeLoading(false); }
  }, [apiCall]);

  const handleSnowflakeSave = useCallback(async () => {
    if (!sfAccountId.trim() || !sfUsername.trim() || !sfWarehouse.trim())
      return setSnowflakeError("Account Identifier, Username, and Warehouse are required.");
    if (sfEnableCustomTable && !sfDatabase.trim())
      return setSnowflakeError("Database is required when Custom Table Scan is enabled.");
    setSnowflakeSaving(true); setSnowflakeError(null); setSnowflakeSaved(false);
    try {
      const body = {
        sourceSystem: "SNOWFLAKE",
        displayName: "Snowflake",
        isActive: sfEnabled,
        snowflakeAccountIdentifier: sfAccountId.trim(),
        snowflakeUsername: sfUsername.trim(),
        snowflakeServiceRole: sfServiceRole.trim() || null,
        snowflakeAuthMode: sfAuthMode === 1 ? "KeyPair" : "Password",
        ...(sfAuthMode === 0 && sfPassword.trim() ? { snowflakePassword: sfPassword.trim() } : {}),
        ...(sfAuthMode === 1 && sfPrivateKey.trim() ? { snowflakePrivateKey: sfPrivateKey.trim() } : {}),
        snowflakeWarehouse: sfWarehouse.trim(),
        snowflakeDatabase: sfDatabase.trim(),
        snowflakeSchema: sfSchema.trim() || "PUBLIC",
        snowflakeTableName: sfTableName.trim() || "SECURITY_RISKS",
        snowflakeEnableCustomTableScan: sfEnableCustomTable,
        snowflakeEnableNativeRiskScan: sfEnableNativeScan,
        snowflakeEnableTrustCenter: sfEnableTrustCenter,
      };
      const saved = snowflakeConfig?.id
        ? await apiCall("PUT", `/grc-connectors/${snowflakeConfig.id}`, body)
        : await apiCall("POST", "/grc-connectors", body);
      setSnowflakeConfig(saved); setSfPassword(""); setSfPrivateKey("");
      setSnowflakeSaved(true); setTimeout(() => setSnowflakeSaved(false), 3000);
    } catch (err) { setSnowflakeError(err?.response?.data?.message || "Failed to save Snowflake configuration."); }
    finally { setSnowflakeSaving(false); }
  }, [apiCall, snowflakeConfig, sfAccountId, sfUsername, sfServiceRole, sfPassword, sfPrivateKey, sfAuthMode, sfWarehouse, sfDatabase, sfSchema, sfTableName, sfEnabled, sfEnableCustomTable, sfEnableNativeScan, sfEnableTrustCenter]);

  const handleSnowflakeDelete = useCallback(async () => {
    if (!snowflakeConfig?.id) return;
    if (!(await requestConfirmAsync("Remove Snowflake Connector",
      "Are you sure you want to remove the Snowflake connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setSnowflakeDeleting(true); setSnowflakeError(null);
    try {
      await apiCall("DELETE", `/grc-connectors/${snowflakeConfig.id}`);
      setSnowflakeConfig(null);
      setSfAccountId(""); setSfUsername(""); setSfPassword(""); setSfPrivateKey(""); setSfWarehouse("");
      setSfDatabase(""); setSfSchema("PUBLIC"); setSfTableName("SECURITY_RISKS");
      setSfEnabled(true); setSfAuthMode(0); setSfServiceRole("");
      setSfEnableCustomTable(true); setSfEnableNativeScan(true); setSfEnableTrustCenter(false);
      setSfPermissions(null);
    } catch { setSnowflakeError("Failed to remove Snowflake configuration."); }
    finally { setSnowflakeDeleting(false); }
  }, [apiCall, snowflakeConfig]);

  const handleSnowflakeSync = useCallback(async () => {
    if (!snowflakeConfig?.id) return;
    setSnowflakeSyncing(true); setSnowflakeError(null);
    try {
      await apiCall("POST", `/grc-connectors/${snowflakeConfig.id}/sync`);
      setTimeout(fetchSnowflakeConfig, 3000);
    } catch { setSnowflakeError("Sync request failed."); }
    finally { setSnowflakeSyncing(false); }
  }, [apiCall, snowflakeConfig, fetchSnowflakeConfig]);


  const handleCheckPermissions = useCallback(async () => {
    if (!snowflakeConfig?.id) return;
    setSfCheckingPerms(true); setSfPermissions(null);
    try {
      const result = await apiCall("POST", `/grc-connectors/${snowflakeConfig.id}/snowflake-check-permissions`);
      setSfPermissions(result);
    } catch { setSfPermissions({ canConnect: false, accountUsage: false, trustCenter: false, customTable: false }); }
    finally { setSfCheckingPerms(false); }
  }, [apiCall, snowflakeConfig]);

  // ── Defender XDR fetch / save / test / sync ──────────────────────────────────
  const fetchDefenderXdrConfig = useCallback(async () => {
    if (!defenderXdrFeature) return;
    setDefenderXdrLoading(true);
    try {
      const data = await apiCall("GET", "/defender-xdr/config");
      setDefenderXdrConfig(data);
      setDefenderXdrEnabled(data?.defenderXdrEnabled ?? false);
    } catch { /* non-critical */ } finally { setDefenderXdrLoading(false); }
  }, [apiCall, defenderXdrFeature]);

  const fetchDefenderIdentityConfig = useCallback(async () => {
    if (!defenderIdentityFeature) return;
    try {
      const data = await apiCall("GET", "/defender-identity/config");
      setDefenderIdentityConfig(data);
      setDefenderIdentityEnabled(data?.defenderIdentityEnabled ?? false);
    } catch { /* non-critical */ }
  }, [apiCall, defenderIdentityFeature]);

  const handleDefenderIdentitySave = useCallback(async () => {
    setDefenderIdentitySaving(true); setDefenderIdentityError(null);
    try {
      await apiCall("PUT", "/defender-identity/config", { defenderIdentityEnabled });
      await fetchDefenderIdentityConfig();
      setDefenderIdentityRefresh(n => n + 1);
    } catch (err) { setDefenderIdentityError(err?.response?.data?.message || "Failed to save the Defender for Identity setting."); }
    finally { setDefenderIdentitySaving(false); }
  }, [apiCall, defenderIdentityEnabled, fetchDefenderIdentityConfig]);

  const handleDefenderXdrSave = useCallback(async () => {
    setDefenderXdrSaving(true); setDefenderXdrError(null); setDefenderXdrSaved(false);
    try {
      await apiCall("PUT", "/defender-xdr/config", { defenderXdrEnabled });
      await fetchDefenderXdrConfig();
      setDefenderXdrSaved(true); setTimeout(() => setDefenderXdrSaved(false), 3000);
    } catch (err) { setDefenderXdrError(err?.response?.data?.message || "Failed to save Defender XDR configuration."); }
    finally { setDefenderXdrSaving(false); }
  }, [apiCall, defenderXdrEnabled, fetchDefenderXdrConfig]);

  const handleDefenderXdrTest = useCallback(async () => {
    setDefenderXdrTestLoading(true); setDefenderXdrTestResult(null);
    try {
      const result = await apiCall("POST", "/defender-xdr/test-connection");
      setDefenderXdrTestResult(result);
    } catch (err) { setDefenderXdrTestResult({ success: false, message: err?.response?.data?.message || "Test failed." }); }
    finally { setDefenderXdrTestLoading(false); }
  }, [apiCall]);

  const handleDefenderXdrSync = useCallback(async () => {
    setDefenderXdrSyncing(true); setDefenderXdrError(null);
    try {
      await apiCall("POST", "/defender-xdr/sync");
      setTimeout(fetchDefenderXdrConfig, 5000);
    } catch (err) { setDefenderXdrError(err?.response?.data?.message || "Sync request failed."); }
    finally { setDefenderXdrSyncing(false); }
  }, [apiCall, fetchDefenderXdrConfig]);

  const handleCopySetupSql = useCallback(async () => {
    if (!snowflakeConfig?.id) return;
    setSfCopyingSql(true);
    try {
      const sql = await apiCall("GET", `/grc-connectors/${snowflakeConfig.id}/snowflake-onboarding-sql`);
      await navigator.clipboard.writeText(typeof sql === "string" ? sql : JSON.stringify(sql));
    } catch { /* clipboard may be blocked */ }
    finally { setSfCopyingSql(false); }
  }, [apiCall, snowflakeConfig]);

  // ── Microsoft save / helpers ─────────────────────────────────────────────────
  const handleSaveConnection = useCallback(async () => {
    setConnSaveError(null);
    // Disabling a source cascades: its controls are removed and every risk generated from them
    // is closed. That's destructive — require the typed Confirm before saving.
    const sourceLabels = [
      ["secureScoreEnabled", "Secure Score"],
      ["mfaEvidenceEnabled", "Identity / MFA"],
      ["defenderEnabled", "Defender & Sentinel"],
      ["knowBe4Enabled", "KnowBe4"],
      ["azDevOpsEnabled", "Azure DevOps SDLC"],
      ["nhiEnabled", "Non-Human Identities"],
      ["nhiAgentIdEnabled", "Entra Agent ID"],
      ["cloudAppsAlertsEnabled", "Defender for Cloud Apps (Alerts)"],
      ["cloudAppsDiscoveryEnabled", "Defender for Cloud Apps (Discovery)"],
    ];
    const disabling = sourceLabels
      .filter(([key]) => connection?.[key] && !connForm[key])
      .map(([, label]) => label);
    if (disabling.length > 0
        && !(await requestConfirmAsync("Disable Security Sources",
          `Are you sure you want to disable ${disabling.join(", ")}? This will delete all of ${disabling.length === 1 ? "its" : "their"} security controls and associated risks.`)))
      return;

    if (connForm.aiDiscoveryEnabled && connForm.aiDiscoverySubsEnabled
        && !(connForm.aiDiscoverySubscriptionIdsText || "").split(/[\s,]+/).some(x => x.trim())) {
      setConnSaveError("Enter at least one additional AI discovery subscription id, or uncheck \u201CAdditional AI Asset Discovery subscriptions\u201D.");
      return;
    }

    if (connForm.cloudAppsDiscoveryEnabled
        && (!connForm.cloudAppsPortalUrl.trim()
            || !(connForm.cloudAppsApiToken.trim() || connForm.cloudAppsApiTokenSet))) {
      setConnSaveError("Enter the MCAS portal URL and API token to enable Cloud App Discovery.");
      return;
    }

    setConnSaving(true); setConnSaveSuccess(false);
    try {
      const newKey = connForm.knowBe4ApiKey.trim();
      const newCloudAppsToken = connForm.cloudAppsApiToken.trim();
            const body = { tenantId: connForm.tenantId.trim(), tenantName: connForm.tenantName.trim(), subscriptionId: connForm.subscriptionId.trim() || null, graphActivityWorkspaceId: connForm.graphActivityWorkspaceId.trim(), secureScoreEnabled: connForm.secureScoreEnabled, mfaEvidenceEnabled: connForm.mfaEvidenceEnabled, defenderEnabled: connForm.defenderEnabled, nhiEnabled: connForm.nhiEnabled, nhiAgentIdEnabled: connForm.nhiAgentIdEnabled, aiDiscoveryEnabled: connForm.aiDiscoveryEnabled, knowBe4Enabled: connForm.knowBe4Enabled, ...(newKey ? { knowBe4ApiKey: newKey } : {}), knowBe4Region: connForm.knowBe4Region || "us", azDevOpsEnabled: connForm.azDevOpsEnabled, azDevOpsConnectionId: connForm.azDevOpsConnectionId, responseEnabled: connForm.responseEnabled, purviewDlpEnabled: connForm.purviewDlpEnabled, aiDiscoverySubscriptionIds: (() => { if (!connForm.aiDiscoverySubsEnabled) return null; const subs = (connForm.aiDiscoverySubscriptionIdsText || "").split(/[\s,]+/).map(x => x.trim()).filter(Boolean); return subs.length > 0 ? subs : null; })(), cloudAppsAlertsEnabled: connForm.cloudAppsAlertsEnabled, cloudAppsDiscoveryEnabled: connForm.cloudAppsDiscoveryEnabled, cloudAppsPortalUrl: connForm.cloudAppsPortalUrl.trim() || null, ...(newCloudAppsToken ? { cloudAppsApiToken: newCloudAppsToken } : {}) };
      const saved = await apiCall("POST", "/security-insights/connection", body);
      setConnection(saved); setConnFormDirty(false); setConnSaveSuccess(true);
      // A previous Test Connection result described the old key/region: do not let it vouch for these.
      setKnowBe4TestResult(null);
      setTimeout(() => setConnSaveSuccess(false), 3000);
      // A new key, a region change or disabling KnowBe4 clears its sync state server-side: re-read
      // the connection so the KnowBe4 banner shows that, not the state from before the save.
      await fetchConnection();
    } catch (err) { setConnSaveError(err?.response?.data?.message || "Failed to save connection"); }
    finally { setConnSaving(false); }
  }, [apiCall, connForm, connection, requestConfirmAsync, fetchConnection]);

  // POST /security-insights/knowbe4/test-connection tests the saved key and region (no key in the body).
  const handleKnowBe4Test = useCallback(async () => {
    setKnowBe4Testing(true); setKnowBe4TestResult(null);
    try {
      const result = await apiCall("POST", "/security-insights/knowbe4/test-connection");
      setKnowBe4TestResult(result || { error: true });
    } catch { setKnowBe4TestResult({ error: true }); }
    finally { setKnowBe4Testing(false); }
  }, [apiCall]);

  // PUT /security-insights/knowbe4/diagnostic-logging: SuperAdmin / SupportAdmin only (backend
  // CanToggleIntegrationDiagnosticLogging). enabled = true means on for 24 hours.
  const handleKnowBe4DiagnosticLogging = useCallback(async (enabled) => {
    setKnowBe4LoggingSaving(true); setKnowBe4LoggingError(false);
    try {
      const result = await apiCall("PUT", "/security-insights/knowbe4/diagnostic-logging", { enabled });
      setConnection(c => (c ? { ...c, knowBe4DiagnosticLoggingUntil: result?.knowBe4DiagnosticLoggingUntil ?? null } : c));
    } catch { setKnowBe4LoggingError(true); }
    finally { setKnowBe4LoggingSaving(false); }
  }, [apiCall]);

  const connSaveErrorBar = connSaveError && (
    <div role="alert" data-testid="conn-save-error"
      className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
      <p className="flex-1 text-xs text-red-800">{connSaveError}</p>
      <button type="button" onClick={() => setConnSaveError(null)} className="text-red-600 text-xs underline">Dismiss</button>
    </div>
  );

  const updateConnForm = (field, value) => { setConnForm(p => ({ ...p, [field]: value })); setConnFormDirty(true); };

  // ── Microsoft Surface Scan (Defender for Cloud) save / test / scan ───────────
  const handleMsScanSave = useCallback(async () => {
    setMsScanSaving(true); setError(null);
    try {
      // Re-fetch immediately before saving rather than relying on the mount-time snapshot, so a save
      // doesn't revert domains/settings changed elsewhere since this page was opened.
      const latest = await apiCall("GET", "/esm/config").catch(() => null);
      if (!latest) {
        setError("Failed to load current Surface Scan configuration — save aborted to avoid overwriting domains.");
        return;
      }
      const payload = {
        domains: latest.domains || [],
        isEnabled: latest.isEnabled ?? true,
        scanFrequencyHours: latest.scanFrequencyHours || 24,
        msEnabled: msScanEnabled,
        easmEnabled: latest.easmEnabled ?? false,
        ransomwareMonitoringEnabled: latest.ransomwareMonitoringEnabled ?? true,
      };
      await apiCall("POST", "/esm/config", payload);
      setEsmConfigError(false);
    } catch { setError("Failed to save Surface Scan settings."); }
    finally { setMsScanSaving(false); }
  }, [apiCall, msScanEnabled]);

  const handleTestDefender = useCallback(async () => {
    setTestingDefender(true); setDefenderStatus(null);
    try {
      const result = await apiCall("GET", "/esm/defender-status");
      setDefenderStatus(result);
    } catch (e) { setDefenderStatus({ ok: false, reason: String(e) }); }
    finally { setTestingDefender(false); }
  }, [apiCall]);

  const handleMsScanNow = useCallback(async () => {
    setScanningNow(true); setScanQueued(false); setError(null);
    try {
      await handleMsScanSave(); // persist msEnabled to DB before scan reads it
      await apiCall("POST", "/esm/scan");
      setScanQueued(true);
    } catch { setError("Failed to trigger scan."); }
    finally { setScanningNow(false); }
  }, [apiCall, handleMsScanSave]);

  const handleDiagnostic = useCallback(async () => {
    setDiagLoading(true); setDiagResults(null);
    try {
      const data = await apiCall("GET", "/security-insights/diagnostic");
      if (data.autoMarkedConnected) await fetchConnection();
      const results = [...(data.diagnostics || [])];
      if (defenderXdrFeature && defenderXdrEnabled) {
        try {
          const xdr = await apiCall("POST", "/defender-xdr/test-connection");
          results.push({ source: "Defender XDR", ok: xdr.success, detail: xdr.message, reason: xdr.reason });
        } catch (err) {
          results.push({ source: "Defender XDR", ok: false, detail: err?.response?.data?.message || "Test failed" });
        }
      }
      setDiagResults(results);
    }
    catch (err) { setDiagResults([{ source: "Connection", ok: false, detail: err?.response?.data?.message || "Failed" }]); }
    finally { setDiagLoading(false); }
  }, [apiCall, fetchConnection, defenderXdrEnabled, defenderXdrFeature]);

  const handleAdminConsent = useCallback(async () => {
    setConsentLoading(true); setConsentError(null);
    try {
      const redirectUri = encodeURIComponent(CONSENT_REDIRECT_URI);
      const data = await apiCall("GET", `/security-insights/consent-url?redirectUri=${redirectUri}`);
      if (data?.url) {
        // Same-tab navigation: Microsoft returns to CONSENT_REDIRECT_URI (the /settings/integrations/
        // consent-complete route), which forwards back here with ?consent=success and we re-verify.
        // localStorage (selected org) persists across this round-trip.
        window.location.assign(data.url);
      } else {
        setConsentError("No consent URL returned from server.");
        setConsentLoading(false);
      }
    } catch (err) {
      setConsentError(err?.response?.data?.message || "Failed to get admin consent URL.");
      setConsentLoading(false);
    }
  }, [apiCall]);

  // Response actions ride a SEPARATE app registration ("Axaris AI Response Actions") with its own consent,
  // so read-only discovery stays read-only for tenants that never opt in. Same round-trip as the
  // discovery consent, different app.
  const handleResponseConsent = useCallback(async () => {
    setConsentLoading(true); setConsentError(null);
    try {
      const redirectUri = encodeURIComponent(CONSENT_REDIRECT_URI);
      const data = await apiCall("GET", `/security-insights/consent-url?redirectUri=${redirectUri}&app=response`);
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        setConsentError("No response consent URL returned from server.");
        setConsentLoading(false);
      }
    } catch (err) {
      setConsentError(err?.response?.data?.message || "Failed to get the response consent URL.");
      setConsentLoading(false);
    }
  }, [apiCall]);

  // After returning from the admin-consent round-trip (?consent=success|error), verify the
  // connection (flips status to Connected on success) and clear the query param so a refresh
  // doesn't re-trigger it.
  const consentHandledRef = useRef(false);
  useEffect(() => {
    if (consentHandledRef.current) return;
    const sp = new URLSearchParams(location.search);
    const consent = sp.get("consent");
    if (!consent) return;
    consentHandledRef.current = true;
    if (consent === "success") {
      setConsentError(null);
      handleDiagnostic();
    } else if (consent === "error") {
      setConsentError(sp.get("desc") || "Admin consent was not completed.");
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [location.search, handleDiagnostic]);

  // ── Okta functions ───────────────────────────────────────────────────────────
  const saveOktaConnector = async () => {
    if (!oktaForm.displayName || !oktaForm.oktaOrgUrl || !oktaForm.oktaApiToken)
      return alert("Display name, Okta Org URL, and API Token are required.");
    setOktaSaving(true);
    try {
      const body = { sourceSystem: "OKTA", displayName: oktaForm.displayName, isActive: true, oktaOrgUrl: oktaForm.oktaOrgUrl, oktaApiToken: oktaForm.oktaApiToken };
      if (oktaEditId) await apiCall("PUT", `/grc-connectors/${oktaEditId}`, body);
      else            await apiCall("POST", "/grc-connectors", body);
      setOktaFormOpen(false); setOktaEditId(null);
      setOktaForm({ displayName: "", oktaOrgUrl: "", oktaApiToken: "" });
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Failed to save connector."); }
    finally { setOktaSaving(false); }
  };

  const syncOktaConnector = async (id) => {
    if (oktaSyncingIds.has(id)) return;
    setOktaSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiCall("POST", `/grc-connectors/${id}/sync`);
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Sync failed."); }
    finally { setOktaSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const deleteOktaConnector = async (id) => {
    if (!(await requestConfirmAsync("Remove Okta Connector",
      "Are you sure you want to remove the Okta connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setOktaDeleteIds(prev => new Set(prev).add(id));
    try {
      await apiCall("DELETE", `/grc-connectors/${id}`);
      setOktaConnectors(prev => prev.filter(c => c.id !== id));
    } catch { alert("Failed to delete connector."); }
    // Reconcile with the backend regardless of the response — a slow cascade can delete the row
    // server-side but still return an error, leaving the optimistic removal above un-run.
    finally { setOktaDeleteIds(prev => { const s = new Set(prev); s.delete(id); return s; }); fetchConnectors(); }
  };

  // ── AWS functions ────────────────────────────────────────────────────────────
  const [awsDiagResults, setAwsDiagResults] = useState({});
  const [awsTestingIds, setAwsTestingIds] = useState(new Set());
  const testAwsConnector = async (id) => {
    setAwsTestingIds(prev => new Set(prev).add(id));
    try {
      const data = await apiCall("GET", `/grc-connectors/${id}/diagnostic`);
      setAwsDiagResults(prev => ({ ...prev, [id]: data?.diagnostics || [] }));
    } catch (err) {
      setAwsDiagResults(prev => ({ ...prev, [id]: [{ source: "Diagnostic", ok: false, detail: err?.response?.data?.message || "Test failed — see server logs." }] }));
    } finally {
      setAwsTestingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const saveAwsConnector = async () => {
    if (!awsForm.displayName || !awsForm.awsAccessKeyId || !awsForm.awsSecretAccessKey || !awsForm.awsAccountId)
      return alert("Display name, Access Key ID, Secret Access Key, and Account ID are required.");
    if (!/^\d{12}$/.test(awsForm.awsAccountId))
      return alert("Account ID must be a 12-digit number with no dashes or spaces (e.g. 123456789012).");
    setAwsSaving(true);
    try {
      const body = { sourceSystem: "AWS", displayName: awsForm.displayName, isActive: true, awsAccessKeyId: awsForm.awsAccessKeyId, awsSecretAccessKey: awsForm.awsSecretAccessKey, awsRegion: awsForm.awsRegion || "us-east-1", awsAccountId: awsForm.awsAccountId || null };
      if (awsEditId) await apiCall("PUT", `/grc-connectors/${awsEditId}`, body);
      else           await apiCall("POST", "/grc-connectors", body);
      setAwsFormOpen(false); setAwsEditId(null);
      setAwsForm({ displayName: "", awsAccessKeyId: "", awsSecretAccessKey: "", awsRegion: "us-east-1", awsAccountId: "" });
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Failed to save AWS connector."); }
    finally { setAwsSaving(false); }
  };

  const syncAwsConnector = async (id) => {
    if (awsSyncingIds.has(id)) return;
    setAwsSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiCall("POST", `/grc-connectors/${id}/sync`);
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "AWS sync failed."); }
    finally { setAwsSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const deleteAwsConnector = async (id) => {
    if (!(await requestConfirmAsync("Remove AWS Connector",
      "Are you sure you want to remove the AWS connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setAwsDeleteIds(prev => new Set(prev).add(id));
    try {
      await apiCall("DELETE", `/grc-connectors/${id}`);
      setAwsConnectors(prev => prev.filter(c => c.id !== id));
    } catch { alert("Failed to delete AWS connector."); }
    // Reconcile with the backend regardless of the response — a slow cascade can delete the row
    // server-side but still return an error, leaving the optimistic removal above un-run.
    finally { setAwsDeleteIds(prev => { const s = new Set(prev); s.delete(id); return s; }); fetchConnectors(); }
  };

  // ── Google Cloud functions ───────────────────────────────────────────────────
  const emptyGcpForm = { displayName: "", gcpScopeType: "ORGANIZATION", gcpScopeId: "", gcpServiceAccountKeyJson: "" };
  const closeGcpForm = () => { setGcpFormOpen(false); setGcpEditId(null); setGcpForm(emptyGcpForm); };

  const saveGcpConnector = async () => {
    const error = gcpSaveValidationErrors(gcpForm, !!gcpEditId);
    if (error) { alert(error); return; }
    setGcpSaving(true);
    try {
      const key = gcpForm.gcpServiceAccountKeyJson.trim() === "" ? "" : gcpForm.gcpServiceAccountKeyJson;
      const body = { sourceSystem: "GCP", displayName: gcpForm.displayName, isActive: true, gcpScopeType: gcpForm.gcpScopeType, gcpScopeId: gcpForm.gcpScopeId.trim(), gcpServiceAccountKeyJson: key };
      if (gcpEditId) await apiCall("PUT", `/grc-connectors/${gcpEditId}`, body);
      else await apiCall("POST", "/grc-connectors", body);
      closeGcpForm();
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Failed to save Google Cloud connector."); }
    finally { setGcpSaving(false); }
  };

  const loadGcpKeyFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setGcpForm(p => ({ ...p, gcpServiceAccountKeyJson: typeof reader.result === "string" ? reader.result : "" }));
    reader.onerror = () => alert("Could not read the key file.");
    reader.readAsText(file);
  };

  const syncGcpConnector = async (id) => {
    if (gcpSyncingIds.has(id)) return;
    setGcpSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiCall("POST", `/grc-connectors/${id}/sync`);
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Google Cloud sync failed."); }
    finally { setGcpSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const deleteGcpConnector = async (id) => {
    if (!(await requestConfirmAsync("Remove Google Cloud Connector",
      "Are you sure you want to remove the Google Cloud connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setGcpDeleteIds(prev => new Set(prev).add(id));
    try {
      await apiCall("DELETE", `/grc-connectors/${id}`);
      setGcpConnectors(prev => prev.filter(c => c.id !== id));
    } catch { alert("Failed to delete Google Cloud connector."); }
    // Reconcile with the backend regardless of the response, the same as the AWS delete.
    finally { setGcpDeleteIds(prev => { const s = new Set(prev); s.delete(id); return s; }); fetchConnectors(); }
  };

  // ── Tenable functions ────────────────────────────────────────────────────────
  const saveTenableConnector = async () => {
    if (!tenableForm.displayName || !tenableForm.tenableAccessKey || !tenableForm.tenableSecretKey)
      return alert("Display name, Access Key, and Secret Key are required.");
    setTenableSaving(true);
    try {
      const body = { sourceSystem: "TENABLE", displayName: tenableForm.displayName, isActive: true, tenableAccessKey: tenableForm.tenableAccessKey, tenableSecretKey: tenableForm.tenableSecretKey };
      if (tenableEditId) await apiCall("PUT", `/grc-connectors/${tenableEditId}`, body);
      else               await apiCall("POST", "/grc-connectors", body);
      setTenableFormOpen(false); setTenableEditId(null);
      setTenableForm({ displayName: "", tenableAccessKey: "", tenableSecretKey: "" });
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Failed to save Tenable connector."); }
    finally { setTenableSaving(false); }
  };

  const syncTenableConnector = async (id) => {
    if (tenableSyncingIds.has(id)) return;
    setTenableSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiCall("POST", `/grc-connectors/${id}/sync`);
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Tenable sync failed."); }
    finally { setTenableSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const deleteTenableConnector = async (id) => {
    if (!(await requestConfirmAsync("Remove Tenable Connector",
      "Are you sure you want to remove the Tenable connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setTenableDeleteIds(prev => new Set(prev).add(id));
    try {
      await apiCall("DELETE", `/grc-connectors/${id}`);
      setTenableConnectors(prev => prev.filter(c => c.id !== id));
    } catch { alert("Failed to delete Tenable connector."); }
    finally { setTenableDeleteIds(prev => { const s = new Set(prev); s.delete(id); return s; }); fetchConnectors(); }
  };

  // ── Splunk connector CRUD ────────────────────────────────────────────────────
  const saveSplunkConnector = async () => {
    if (!splunkForm.displayName || !splunkForm.splunkHost)
      return alert("Display Name and Splunk Host are required.");
    if (splunkAuthMode === "token" && !splunkForm.splunkToken)
      return alert("Bearer Token is required when using token authentication.");
    if (splunkAuthMode === "userpass" && (!splunkForm.splunkUsername || !splunkForm.splunkPassword))
      return alert("Username and Password are required when using credential authentication.");

    setSplunkSaving(true);
    try {
      const body = {
        sourceSystem:  "SPLUNK",
        displayName:   splunkForm.displayName,
        isActive:      true,
        splunkHost:    splunkForm.splunkHost,
        splunkPort:    splunkForm.splunkPort || "8089",
        splunkToken:   splunkAuthMode === "token"    ? splunkForm.splunkToken    : "",
        splunkUsername: splunkAuthMode === "userpass" ? splunkForm.splunkUsername : "",
        splunkPassword: splunkAuthMode === "userpass" ? splunkForm.splunkPassword : "",
        splunkIndex:   splunkForm.splunkIndex || "",
        splunkAllowSelfSignedCert: splunkForm.splunkAllowSelfSignedCert || false,
      };
      if (splunkEditId) await apiCall("PUT", `/grc-connectors/${splunkEditId}`, body);
      else              await apiCall("POST", "/grc-connectors", body);
      setSplunkFormOpen(false); setSplunkEditId(null);
      setSplunkForm({ displayName: "", splunkHost: "", splunkPort: "8089", splunkToken: "", splunkUsername: "", splunkPassword: "", splunkIndex: "", splunkAllowSelfSignedCert: false });
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Failed to save Splunk connector."); }
    finally { setSplunkSaving(false); }
  };

  const syncSplunkConnector = async (id) => {
    if (splunkSyncingIds.has(id)) return;
    setSplunkSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiCall("POST", `/grc-connectors/${id}/sync`);
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Splunk sync failed."); }
    finally { setSplunkSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const deleteSplunkConnector = async (id) => {
    if (!(await requestConfirmAsync("Remove Splunk Connector",
      "Are you sure you want to remove the Splunk connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setSplunkDeleteIds(prev => new Set(prev).add(id));
    try {
      await apiCall("DELETE", `/grc-connectors/${id}`);
      setSplunkConnectors(prev => prev.filter(c => c.id !== id));
    } catch { alert("Failed to delete Splunk connector."); }
    finally { setSplunkDeleteIds(prev => { const s = new Set(prev); s.delete(id); return s; }); fetchConnectors(); }
  };

  // ── Wiz connector CRUD ───────────────────────────────────────────────────────
  const saveWizConnector = async () => {
    if (!wizForm.displayName || !wizForm.wizClientId || !wizForm.wizClientSecret)
      return alert("Display Name, Client ID, and Client Secret are required.");
    setWizSaving(true);
    try {
      const body = {
        sourceSystem: "WIZ",
        displayName:  wizForm.displayName,
        isActive:     true,
        wizClientId:     wizForm.wizClientId,
        wizClientSecret: wizForm.wizClientSecret,
        wizApiEndpoint:  wizForm.wizApiEndpoint || "https://api.app.wiz.io/graphql",
      };
      if (wizEditId) await apiCall("PUT", `/grc-connectors/${wizEditId}`, body);
      else           await apiCall("POST", "/grc-connectors", body);
      setWizFormOpen(false); setWizEditId(null);
      setWizForm({ displayName: "", wizClientId: "", wizClientSecret: "", wizApiEndpoint: "https://api.app.wiz.io/graphql" });
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Failed to save Wiz connector."); }
    finally { setWizSaving(false); }
  };

  const syncWizConnector = async (id) => {
    if (wizSyncingIds.has(id)) return;
    setWizSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiCall("POST", `/grc-connectors/${id}/sync`);
      await fetchConnectors();
    } catch (err) { alert(err?.response?.data?.message || "Wiz sync failed."); }
    finally { setWizSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  };

  const deleteWizConnector = async (id) => {
    if (!(await requestConfirmAsync("Remove Wiz Connector",
      "Are you sure you want to remove the Wiz connector? This will delete all of its security " +
      "controls and associated risks."))) return;
    setWizDeleteIds(prev => new Set(prev).add(id));
    try {
      await apiCall("DELETE", `/grc-connectors/${id}`);
      setWizConnectors(prev => prev.filter(c => c.id !== id));
    } catch { alert("Failed to delete Wiz connector."); }
    finally { setWizDeleteIds(prev => { const s = new Set(prev); s.delete(id); return s; }); fetchConnectors(); }
  };

  // ── ITSM disconnect ──────────────────────────────────────────────────────────
  const handleDisconnectJira = async () => {
    if (!window.confirm("Are you sure you want to disconnect Jira? This will remove the integration for your organization.")) return;
    setDisconnecting(true);
    try {
      await apiCall("DELETE", "/integrations/jira");
      setJiraStatus({ isConfigured: false });
    } catch (err) {
      setError("Failed to disconnect Jira integration");
    } finally { setDisconnecting(false); }
  };

  const handleDisconnectServiceNow = async () => {
    if (!window.confirm("Are you sure you want to disconnect ServiceNow? This will remove the integration for your organization.")) return;
    setDisconnectingServiceNow(true);
    try {
      await apiCall("DELETE", "/integrations/servicenow");
      setServiceNowStatus({ isConfigured: false });
    } catch (err) {
      setError("Failed to disconnect ServiceNow integration");
    } finally { setDisconnectingServiceNow(false); }
  };

  const handleJiraConfigured = () => { setShowJiraModal(false); fetchJiraStatus(); };
  const handleServiceNowConfigured = () => { setShowServiceNowModal(false); fetchServiceNowStatus(); };

  // ── OAuth callback handling ──────────────────────────────────────────────────
  useEffect(() => {
    const completeOAuthFlow = async () => {
      const params = new URLSearchParams(window.location.search);
      const errorParam = params.get("error");
      if (errorParam) {
        setError(errorParam);
        window.history.replaceState({}, "", "/settings/integrations");
        return;
      }
      const jiraCode = params.get("code");
      const jiraState = params.get("state");
      if (jiraCode && jiraState) {
        const instanceUrl = sessionStorage.getItem("jira_instance_url");
        const storedState = sessionStorage.getItem("jira_oauth_state");
        if (!instanceUrl) { setError("OAuth session expired. Please try connecting again."); window.history.replaceState({}, "", "/settings/integrations"); return; }
        if (jiraState !== storedState) { setError("Invalid OAuth state. Please try connecting again."); window.history.replaceState({}, "", "/settings/integrations"); return; }
        window.history.replaceState({}, "", "/settings/integrations");
        setCompleting(true);
        try {
          await apiCall("POST", "/integrations/jira/complete", { code: jiraCode, instanceUrl, state: jiraState });
          sessionStorage.removeItem("jira_oauth_state");
          sessionStorage.removeItem("jira_instance_url");
          await fetchJiraStatus();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to complete Jira authorization");
        } finally { setCompleting(false); }
        return;
      }
      const serviceNowCode = params.get("servicenow_code");
      const serviceNowState = params.get("servicenow_state");
      if (serviceNowCode && serviceNowState) {
        const instanceUrl = sessionStorage.getItem("servicenow_instance_url");
        const storedState = sessionStorage.getItem("servicenow_oauth_state");
        if (!instanceUrl) { setError("OAuth session expired. Please try connecting again."); window.history.replaceState({}, "", "/settings/integrations"); return; }
        if (serviceNowState !== storedState) { setError("Invalid OAuth state. Please try connecting again."); window.history.replaceState({}, "", "/settings/integrations"); return; }
        window.history.replaceState({}, "", "/settings/integrations");
        setCompleting(true);
        try {
          await apiCall("POST", "/integrations/servicenow/complete", { code: serviceNowCode, instanceUrl, state: serviceNowState });
          sessionStorage.removeItem("servicenow_oauth_state");
          sessionStorage.removeItem("servicenow_instance_url");
          await fetchServiceNowStatus();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to complete ServiceNow authorization");
        } finally { setCompleting(false); }
      }
    };
    completeOAuthFlow();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-expand a section when navigated here with state (e.g. from Snowflake dashboard) ──
  useEffect(() => {
    const section = location.state?.expandSection;
    if (section) setExpandedSection(section);
  }, [location.state]);

  // ── Mount: load all data ─────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchJiraStatus(), fetchServiceNowStatus(), fetchConnection(), fetchEsmConfig(), fetchConnectors(), fetchFortinetConfig(), fetchSnowflakeConfig(), fetchSourceCodeConnections(), fetchDefenderXdrConfig(), fetchDefenderIdentityConfig()]);
      setLoading(false);
    };
    init();
  }, [fetchJiraStatus, fetchServiceNowStatus, fetchConnection, fetchEsmConfig, fetchConnectors, fetchFortinetConfig, fetchSnowflakeConfig, fetchSourceCodeConnections, fetchDefenderXdrConfig]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const connStatus = connection?.connectionStatus;
  // Always false when the feature is off, so Save Settings never tries to PUT a 404 route.
  const defenderXdrDirty = defenderXdrFeature && defenderXdrEnabled !== (defenderXdrConfig?.defenderXdrEnabled ?? false);
  const defenderIdentityDirty = defenderIdentityFeature && defenderIdentityEnabled !== (defenderIdentityConfig?.defenderIdentityEnabled ?? false);

  // The "additional permissions" banners are shown ONLY when a Test Connection run actually flagged
  // the relevant source as failing (a real permission error / 403) — not as a static reminder.
  // diagResults rows look like { source, ok, status, detail }; ok === false means a hard failure
  // (ok === null means "skipped/unavailable", e.g. P2 absent, which we intentionally do NOT alert on).
  const nhiDiagFailed = Array.isArray(diagResults)
    && diagResults.some(r => typeof r?.source === "string" && r.source.includes("Service Principals") && r.ok === false);
  const agentDiagFailed = Array.isArray(diagResults)
    && diagResults.some(r => typeof r?.source === "string" && r.source.includes("Agent ID") && r.ok === false);
  // reason distinguishes what the failed probe proved: "tenant" = the token demonstrably carries
  // the required role yet the API refused, so re-consenting cannot help (provisioning/licensing);
  // anything else (incl. old backends that send no reason) = consent-flavored, show the re-grant CTA.
  const xdrDiagRow = Array.isArray(diagResults)
    ? diagResults.find(r => typeof r?.source === "string" && r.source.includes("Defender XDR"))
    : null;
  const xdrDiagFailed = !!xdrDiagRow && xdrDiagRow.ok === false;
  const xdrTenantIssue = xdrDiagFailed && xdrDiagRow.reason === "tenant";

  // ── Scroll-to-panel ref + effect (must be before any early return) ───────────
  const expandedPanelRef = useRef(null);
  useEffect(() => {
    if (expandedSection && expandedPanelRef.current) {
      expandedPanelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [expandedSection]);

  // ── Loading screen ───────────────────────────────────────────────────────────
  if (loading || completing) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-theme-primary flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner />
            {completing && <p className="mt-4 text-gray-600">Completing authorization...</p>}
          </div>
        </div>
      </>
    );
  }

  // ── Helper: card toggle ──────────────────────────────────────────────────────
  const toggleSection = (key) => setExpandedSection(prev => prev === key ? null : key);

  // ── Sync spinner SVG ─────────────────────────────────────────────────────────
  const SyncSpinSvg = () => (
    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/>
    </svg>
  );
  const SyncIconSvg = () => (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
    </svg>
  );

  // ── Expanded panel: Microsoft ────────────────────────────────────────────────
  const MicrosoftPanel = () => (
    <div className="space-y-4 pt-2">
      {connSaveSuccess && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-xs font-medium flex items-center gap-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
          Connection settings saved successfully.{connStatus === "Pending" && " Complete admin consent below to finish setup."}
        </div>
      )}

      {canIntegrations && connection && connStatus === "Pending" && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-800 mb-1">Admin Consent Required</div>
              <p className="text-xs text-amber-700 leading-relaxed">
                Your Azure tenant is configured but Axaris AI has not yet been granted permission to read security data.
                A Global Administrator must grant admin consent so the app can access Microsoft Graph APIs (Secure Score, Identity, Defender).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-9">
            <button onClick={handleAdminConsent} disabled={consentLoading || msBlocked}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors">
              {consentLoading ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Opening…</> : "Grant Admin Consent"}
            </button>
            <button onClick={handleDiagnostic} disabled={diagLoading || msBlocked}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-amber-400 text-amber-800 rounded-lg text-xs font-semibold hover:bg-amber-100 disabled:opacity-50 transition-colors">
              {diagLoading ? <><span className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />Verifying…</> : "I've granted consent — Verify"}
            </button>
          </div>
          {consentError && <div className="ml-9 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{consentError}</div>}
          <div className="ml-9 mt-2 text-xs text-amber-700 space-y-1">
            <div className="font-semibold mb-1">Steps to complete:</div>
            <div>1. Click <strong>Grant Admin Consent</strong> — a new tab opens to Microsoft login.</div>
            <div>2. Sign in with a Global Administrator account for your Azure tenant.</div>
            <div>3. Review the requested permissions and click <strong>Accept</strong>.</div>
            <div>4. Close the Microsoft tab and return here.</div>
            <div>5. Go to Security Insights and click <strong>Refresh Data</strong> to verify and pull data.</div>
          </div>
        </div>
      )}

      {canIntegrations && connection && connStatus === "Connected" && connection.nhiEnabled && nhiDiagFailed && !permsNoteDismissed && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🔑</span>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-blue-800 mb-1">NHI Permission Issue Detected</div>
                <button onClick={() => setPermsNoteDismissed(true)} aria-label="Dismiss"
                  className="text-blue-400 hover:text-blue-700 text-sm leading-none px-1">✕</button>
              </div>
              <p className="text-xs text-blue-700 leading-relaxed">
                Test Connection reports the Non-Human Identity (NHI) source can't be read. NHI analysis needs
                (<code className="bg-blue-100 px-1 rounded">Application.Read.All</code>,{" "}
                <code className="bg-blue-100 px-1 rounded">RoleManagement.Read.Directory</code>,{" "}
                <code className="bg-blue-100 px-1 rounded">DelegatedPermissionGrant.ReadWrite.All</code>).
                A Global Administrator must re-grant admin consent.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-9">
            <button onClick={handleAdminConsent} disabled={consentLoading || msBlocked}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {consentLoading ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Opening…</> : "Re-grant Admin Consent"}
            </button>
          </div>
        </div>
      )}

      {canIntegrations && connection && connStatus === "Connected" && connection.nhiAgentIdEnabled && agentDiagFailed && !permsNoteDismissed && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🧠</span>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-blue-800 mb-1">Agent ID Permission Issue Detected</div>
                <button onClick={() => setPermsNoteDismissed(true)} aria-label="Dismiss"
                  className="text-blue-400 hover:text-blue-700 text-sm leading-none px-1">✕</button>
              </div>
              <p className="text-xs text-blue-700 leading-relaxed">
                Test Connection reports the Entra Agent ID source can't be read. Agent ID analysis reads agent identities, blueprints, and agent users via{" "}
                <code className="bg-blue-100 px-1 rounded">Application.Read.All</code> /{" "}
                <code className="bg-blue-100 px-1 rounded">Directory.Read.All</code>. Agent <strong>risk detection</strong> also
                requires <code className="bg-blue-100 px-1 rounded">IdentityRiskyAgent.Read.All</code> and{" "}
                <strong>Entra ID P2</strong> (without P2 the agent risk control is simply skipped, not failed).
                A Global Administrator must re-grant admin consent.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-9">
            <button onClick={handleAdminConsent} disabled={consentLoading || msBlocked}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {consentLoading ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Opening…</> : "Re-grant Admin Consent"}
            </button>
          </div>
        </div>
      )}

      {canIntegrations && connection && connStatus === "Connected" && defenderXdrFeature && defenderXdrEnabled && xdrDiagFailed && xdrTenantIssue && !permsNoteDismissed && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-2">
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🏢</span>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-amber-800 mb-1">Defender XDR Unavailable in Your Microsoft Tenant</div>
                <button onClick={() => setPermsNoteDismissed(true)} aria-label="Dismiss"
                  className="text-amber-400 hover:text-amber-700 text-sm leading-none px-1">✕</button>
              </div>
              <p className="text-xs text-amber-700 leading-relaxed">
                Permissions and admin consent are in place, but your Microsoft tenant refused the Defender XDR API —
                re-granting consent will not fix this. This usually means the Defender service isn't provisioned yet
                (sign in to <span className="font-mono">security.microsoft.com</span> as a tenant admin and confirm the
                Incidents page loads) or the tenant lacks an active Defender / Microsoft 365 E5 license.
              </p>
              {xdrDiagRow?.detail && (
                <p className="mt-1.5 text-[11px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-1.5">{xdrDiagRow.detail}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {canIntegrations && connection && connStatus === "Connected" && defenderXdrFeature && defenderXdrEnabled && xdrDiagFailed && !xdrTenantIssue && !permsNoteDismissed && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🔍</span>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-blue-800 mb-1">Defender XDR Permission Issue Detected</div>
                <button onClick={() => setPermsNoteDismissed(true)} aria-label="Dismiss"
                  className="text-blue-400 hover:text-blue-700 text-sm leading-none px-1">✕</button>
              </div>
              <p className="text-xs text-blue-700 leading-relaxed">
                Test Connection reports the Defender XDR APIs can't be read. Defender XDR needs{" "}
                <code className="bg-blue-100 px-1 rounded">Incident.Read.All</code> (Microsoft Threat Protection) plus{" "}
                <code className="bg-blue-100 px-1 rounded">Alert.Read.All</code>,{" "}
                <code className="bg-blue-100 px-1 rounded">Machine.Read.All</code>,{" "}
                <code className="bg-blue-100 px-1 rounded">Vulnerability.Read.All</code> and{" "}
                <code className="bg-blue-100 px-1 rounded">SecurityRecommendation.Read.All</code> (WindowsDefenderATP).
                A Global Administrator must re-grant admin consent.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-9">
            <button onClick={handleAdminConsent} disabled={consentLoading || msBlocked}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {consentLoading ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Opening…</> : "Re-grant Admin Consent"}
            </button>
          </div>
        </div>
      )}

      {/* Status + Capabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-xl border-2 p-5 ${connStatus === "Connected" ? "border-green-300 bg-green-50" : "border-dashed border-gray-200 bg-white"}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${connStatus === "Connected" ? "bg-green-100" : "bg-gray-100"}`}>
              {connStatus === "Connected" ? "✅" : connStatus === "Pending" ? "🔐" : "🔌"}
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-gray-800 mb-1">{connStatus === "Connected" ? "Microsoft Connected" : connStatus === "Pending" ? "Awaiting Admin Consent" : "Not Connected"}</div>
              {connLoading ? <div className="text-xs text-gray-400">Loading…</div> : connection ? (<>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${connStatus === "Connected" ? "bg-green-500" : connStatus === "Error" ? "bg-red-500" : "bg-yellow-500"}`} />
                  <span className={`text-xs font-medium ${connStatus === "Connected" ? "text-green-700" : "text-gray-600"}`}>{connStatus}</span>
                </div>
                <div className="text-xs text-gray-500">{connection.tenantName || connection.tenantId}</div>
                {connection.connectedAt && <div className="text-xs text-gray-400 mt-0.5">Connected {formatUtcDate(connection.connectedAt)}</div>}
                {/* Quiet re-consent path for healthy connections — needed whenever new permissions
                    are added to the app registration (consent uses .default, so re-running the flow
                    picks them up). Mirrors the response app's re-grant link. */}
                {canIntegrations && connStatus === "Connected" && (
                  <button onClick={handleAdminConsent} disabled={consentLoading || msBlocked}
                    title="Re-run the Microsoft admin consent flow — required after new permissions are added to the Axaris app registration."
                    className="mt-1.5 text-xs underline text-green-800 hover:text-green-950 disabled:opacity-50">
                    {consentLoading ? "Opening…" : "Re-grant admin consent"}
                  </button>
                )}
              </>) : <div className="text-xs text-gray-500">No connector configured. Enter your Azure tenant details below.</div>}
            </div>
            {canIntegrations && connection && (
              <button
                onClick={disconnectMicrosoft}
                disabled={msDisconnecting}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-lg px-2 py-1.5 flex-shrink-0"
                title="Remove this Microsoft connection (tenant, toggles, and stored keys). Use when moving to a different directory — you will re-enter details and re-run admin consent."
              >
                <Trash2 size={12} />{msDisconnecting ? "Removing…" : "Disconnect"}
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Capability Detection</span></div>
          <div className="p-3 space-y-1.5">
            {[
              { icon: "👤", label: "Entra ID / Graph", desc: "Users, MFA, roles, Conditional Access", avail: () => !!(connection?.mfaEvidenceEnabled) },
              { icon: "🛡", label: "Secure Score", desc: "200+ control recommendations", avail: () => !!(connection?.secureScoreEnabled) },
              { icon: "💻", label: "Defender for Endpoint", desc: "Device health, vulnerabilities, patches", avail: () => !!(connection?.defenderEnabled) },
              { icon: "☁", label: "Defender for Cloud", desc: "Cloud misconfigs, regulatory compliance", avail: () => !!(connection?.defenderEnabled) },
              { icon: "📊", label: "Sentinel / Log Analytics", desc: "Incidents, detection rules, log retention", avail: () => !!(connection?.defenderEnabled) },
              { icon: "🤖", label: "Non-Human Identity", desc: "Service principals, app credentials, OAuth scopes, federation trust", avail: () => !!(connection?.nhiEnabled) },
              { icon: "🧠", label: "Entra Agent ID", desc: "Agent identities, sponsorship, blueprints, agent risk detection", avail: () => !!(connection?.nhiAgentIdEnabled) },
              { icon: "☁️", label: "Cloud App Alerts", desc: "Defender for Cloud Apps alerts — exfiltration, unsanctioned app activity", avail: () => !!(connection?.cloudAppsAlertsEnabled) },
              { icon: "🔍", label: "Cloud App Discovery", desc: "Shadow IT inventory — discovered apps, sanctioning, risk scoring", avail: () => !!(connection?.cloudAppsDiscoveryEnabled) },
              ...(defenderXdrFeature ? [{ icon: "🔍", label: "Defender XDR", desc: "Incidents, alerts, CVEs, recommendations, attack paths, identity threats", avail: () => !!(defenderXdrConfig?.defenderXdrEnabled) }] : []),
              ...(defenderIdentityFeature ? [{ icon: "🪪", label: "Defender for Identity", desc: "Identity inventory — accounts, privileges, status, last activity", avail: () => !!(defenderIdentityConfig?.defenderIdentityEnabled) }] : []),
            ].map((cap, i) => {
              const avail = cap.avail();
              return (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-lg border ${avail ? "border-green-200 bg-green-50" : "border-gray-100 bg-gray-50 opacity-60"}`}>
                  <span className="w-6 h-6 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 text-sm">{cap.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800">{cap.label}</div>
                    <div className="text-xs text-gray-400">{cap.desc}</div>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${avail ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}>{avail ? "Available" : "N/A"}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Connector Settings form */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">Connector Settings</span>
          {canIntegrations && <button
            onClick={async () => {
              // The Microsoft connection save is blocked where the source is unavailable; the Defender
              // toggles are local settings and still save (a saved "on" can be turned off).
              if (connFormDirty && !msBlocked) await handleSaveConnection();
              if (defenderXdrDirty) await handleDefenderXdrSave();
              if (defenderIdentityDirty) await handleDefenderIdentitySave();
            }}
            disabled={connSaving || defenderXdrSaving || defenderIdentitySaving || (!connFormDirty && !defenderXdrDirty && !defenderIdentityDirty) || (msBlocked && !defenderXdrDirty && !defenderIdentityDirty)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {(connSaving || defenderXdrSaving || defenderIdentitySaving) ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : "Save Settings"}
          </button>}
        </div>
        {connSaveErrorBar}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Azure Tenant ID <span className="text-red-500">*</span></label>
            <input type="text" value={connForm.tenantId} onChange={e => updateConnForm("tenantId", e.target.value)} disabled={!canIntegrations} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 font-mono" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tenant Name</label>
            <input type="text" value={connForm.tenantName} onChange={e => updateConnForm("tenantName", e.target.value)} disabled={!canIntegrations} placeholder="e.g. Contoso Ltd"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Azure Subscription ID <span className="text-gray-400">(Defender, AI Discovery)</span></label>
            <input type="text" value={connForm.subscriptionId} onChange={e => updateConnForm("subscriptionId", e.target.value)} disabled={!canIntegrations} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 font-mono" />
            {(connForm.defenderEnabled || connForm.aiDiscoveryEnabled) && connForm.subscriptionId.trim() && connection?.clientId && (
              <div className="mt-2 p-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                <p className="text-[11px] text-indigo-900 mb-1.5">
                  One-time setup: an Azure admin must grant Axaris read access to this subscription
                  ({connForm.aiDiscoveryEnabled ? "Reader" : "Security Reader"} role — Microsoft does not allow apps to grant themselves subscription access):
                </p>
                <div className="flex items-start gap-1.5">
                  <code className="flex-1 text-[10px] font-mono text-indigo-800 bg-white border border-indigo-100 rounded px-2 py-1.5 whitespace-pre-wrap break-all">
                    {`az role assignment create --assignee ${connection.clientId} --role ${connForm.aiDiscoveryEnabled ? "Reader" : "\"Security Reader\""} --scope /subscriptions/${connForm.subscriptionId.trim()}`}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`az role assignment create --assignee ${connection.clientId} --role ${connForm.aiDiscoveryEnabled ? "Reader" : '"Security Reader"'} --scope /subscriptions/${connForm.subscriptionId.trim()}`);
                      setRoleCmdCopied(true);
                      setTimeout(() => setRoleCmdCopied(false), 2000);
                    }}
                    className="p-1.5 rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                    title="Copy command"
                  >
                    {roleCmdCopied ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
                <p className="text-[10px] text-indigo-700 mt-1">
                  Or in the Azure portal: subscription → Access control (IAM) → Add role assignment. Verify with the Test Connections diagnostic below.
                </p>
                <p className="text-[10px] text-indigo-700 mt-1">
                  <strong>Grant admin consent first</strong> — consent registers the Axaris app in your directory.
                  If this command reports "cannot find user or service principal", consent hasn't been granted yet.
                </p>
              </div>
            )}
            {showAiIntelligence && connForm.aiDiscoveryEnabled && (
              <div className="mt-2">
                <label className="flex items-center gap-2 cursor-pointer mb-1">
                  <input
                    type="checkbox"
                    checked={connForm.aiDiscoverySubsEnabled}
                    onChange={e => updateConnForm("aiDiscoverySubsEnabled", e.target.checked)}
                    disabled={!canIntegrations}
                    className="rounded accent-indigo-600"
                  />
                  <span className="text-xs font-medium text-gray-600">
                    Additional AI Asset Discovery subscriptions <span className="text-gray-400 font-normal">(one per line)</span>
                  </span>
                </label>
                {connForm.aiDiscoverySubsEnabled && (
                <textarea
                  value={connForm.aiDiscoverySubscriptionIdsText}
                  onChange={e => updateConnForm("aiDiscoverySubscriptionIdsText", e.target.value)}
                  disabled={!canIntegrations}
                  rows={2}
                  placeholder={"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\nyyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy"}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 font-mono"
                />
                )}
                {connForm.aiDiscoverySubsEnabled && connection?.clientId && (connForm.aiDiscoverySubscriptionIdsText || "").trim() && (
                  <div className="mt-1.5 p-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                    <p className="text-[11px] text-indigo-900 mb-1.5">
                      Each listed subscription needs the Reader role (one command per subscription;
                      the Test Connections diagnostic below reports each separately):
                    </p>
                    {(connForm.aiDiscoverySubscriptionIdsText || "").split(/[\s,]+/).map(x => x.trim()).filter(Boolean).map(sub => (
                      <code key={sub} className="block text-[10px] font-mono text-indigo-800 bg-white border border-indigo-100 rounded px-2 py-1.5 mb-1 whitespace-pre-wrap break-all">
                        {`az role assignment create --assignee ${connection.clientId} --role Reader --scope /subscriptions/${sub}`}
                      </code>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Response actions ride a SEPARATE app registration with its own admin consent, so
                discovery stays read-only for tenants that never opt in. The grant link only
                appears once the toggle is SAVED — consent for an unsaved toggle would grant write
                to a tenant whose connection row still says response is off. */}
            {showAiIntelligence && connForm.responseEnabled && (
              connection?.responseConsentStatus === "Connected" ? (
                // Granted-and-verified is the calm state: green confirmation, no call to action.
                // Re-consent (secret rotation, revoked grant) is still reachable via a quiet link.
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-[11px] text-emerald-900">
                    <b>Response consent granted.</b> The Axaris AI Response Actions app is consented in
                    this tenant — verified by the diagnostic. Disabling an Entra identity is available on AI
                    Intelligence asset pages when <b>Enable response actions</b> is on in AI Intelligence settings.{" "}
                    {connection?.responseClientId && (
                      <button onClick={handleResponseConsent} disabled={consentLoading || !canIntegrations || msBlocked}
                        className="underline text-emerald-800 hover:text-emerald-950 disabled:opacity-50">
                        Re-grant consent
                      </button>
                    )}
                  </p>
                </div>
              ) : (
                <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-[11px] text-red-900 mb-1.5">
                    Response actions (disable an agent identity) use the separate <b>Axaris AI Response Actions</b> app
                    with write permission — granted by its own admin consent, independent of the read-only
                    discovery consent. Consent has not been verified yet — grant it, then run the
                    diagnostic below.
                  </p>
                  {connection?.responseEnabled ? (
                    connection?.responseClientId ? (
                      <button onClick={handleResponseConsent} disabled={consentLoading || !canIntegrations || msBlocked}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
                        {consentLoading ? "Redirecting…" : "Grant response admin consent"}
                      </button>
                    ) : (
                      <p className="text-[11px] text-red-900 font-medium">
                        The Axaris AI Response Actions app registration is not configured in this environment.
                      </p>
                    )
                  ) : (
                    <p className="text-[11px] text-red-900 font-medium">
                      Save the connection first, then grant the response consent.
                    </p>
                  )}
                </div>
              )
            )}
          </div>
          {/* Runtime actions: the Log Analytics workspace the customer's Graph activity diagnostic
              setting sends to. Saved as "" when cleared, never null: the server reads null as
              "keep the stored value". */}
          {showAiIntelligence && (
            <div>
              <label htmlFor="graph-activity-workspace-id" className="block text-xs font-medium text-gray-600 mb-1">
                Log Analytics workspace ID <span className="text-gray-400">(Runtime actions)</span>
              </label>
              <input id="graph-activity-workspace-id" type="text" value={connForm.graphActivityWorkspaceId}
                onChange={e => updateConnForm("graphActivityWorkspaceId", e.target.value)} disabled={!canIntegrations}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 font-mono" />
              <p data-testid="graph-activity-workspace-help" className="text-[10px] text-gray-500 mt-1">
                Paste the workspace&apos;s <strong>Workspace ID</strong> (a GUID) from the Overview page of the Log Analytics
                workspace in the Azure portal. This is not the workspace name, and not its full resource ID.
              </p>
              <p className="text-[10px] text-gray-500 mt-1">
                The <strong>Microsoft Graph activity logs</strong> diagnostic setting must be sending to this workspace.
                Axaris can only see Graph calls from the point that setting was switched on; there is no history before it.
                Clear this field to stop reading.
              </p>
            </div>
          )}
          <div className="flex flex-col gap-2 justify-center">
            <label className="text-xs font-medium text-gray-600 mb-0.5">Enabled Sources</label>
            {[{ key: "secureScoreEnabled", label: "Secure Score" }, { key: "mfaEvidenceEnabled", label: "MFA / Identity" }, { key: "defenderEnabled", label: "Defender for Cloud" }, { key: "nhiEnabled", label: "Non-Human Identity (NHI)" }, { key: "nhiAgentIdEnabled", label: "Entra Agent ID" }, { key: "cloudAppsAlertsEnabled", label: "Cloud App Alerts (Defender for Cloud Apps)" }, ...(showAiIntelligence ? [{ key: "aiDiscoveryEnabled", label: "AI Asset Discovery" }, { key: "responseEnabled", label: "AI Response Actions — Entra (write)" }] : []), ...(purviewDlpFeature ? [{ key: "purviewDlpEnabled", label: "Purview DLP (data loss)" }] : [])].map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={connForm[opt.key]} onChange={e => updateConnForm(opt.key, e.target.checked)} disabled={!canIntegrations} className="rounded accent-indigo-600" />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
            {/* Purview needs a permission the Axaris app does not hold by default, so the toggle
                alone is not enough. Saying so here stops a tenant enabling it, seeing three Not
                Assessed controls, and assuming the integration is broken. */}
            {purviewDlpFeature && connForm.purviewDlpEnabled && (
              <div className="mt-1 p-2 bg-amber-50 border border-amber-100 rounded-lg text-[11px] text-amber-800 leading-snug"
                   data-testid="purview-consent-note">
                Purview DLP needs one extra Microsoft Graph permission: <span className="font-semibold">SecurityAlert.Read.All</span>.
                Grant it to the Axaris app registration and re-consent in this tenant, then run Refresh Data on
                Security Insights. Until it is granted the three DLP controls read <span className="font-semibold">Not
                Assessed</span> with the reason — they are never counted as passing.
              </div>
            )}
            {defenderXdrFeature && (
            <div className="flex items-start gap-2" data-testid="defender-xdr-toggle">
              {/* Unavailable: turning it on is disabled; a saved "on" can still be turned off (local). */}
              <input type="checkbox" checked={defenderXdrEnabled} aria-label="Defender XDR"
                onChange={e => setDefenderXdrEnabled(e.target.checked)}
                disabled={!canIntegrations || (xdrBlocked && !defenderXdrConfig?.defenderXdrEnabled)}
                className="rounded accent-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs text-gray-700">Defender XDR</span>
                {xdrBlocked && <UnavailableNote />}
                <div className="text-xs text-gray-400 leading-snug">SOC operations — incidents, alerts, CVEs, recommendations, exposure, identity threats</div>
              </div>
            </div>
            )}
            {defenderXdrFeature && defenderXdrConfig?.defenderXdrEnabled && (
              <div className="mt-1 p-2 bg-gray-50 border border-gray-100 rounded-lg space-y-1.5" data-testid="defender-xdr-panel">
                {canIntegrations && (
                  <div className="flex items-center gap-2">
                    <button onClick={handleDefenderXdrSync} disabled={defenderXdrSyncing || xdrBlocked}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                      {defenderXdrSyncing ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Starting…</> : "Sync Now"}
                    </button>
                    <button onClick={handleDefenderXdrTest} disabled={defenderXdrTestLoading || xdrBlocked}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
                      {defenderXdrTestLoading ? <><span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Testing…</> : "Test Connection"}
                    </button>
                  </div>
                )}
                <div className="text-[11px] text-gray-500">
                  {defenderXdrConfig?.defenderXdrLastSuccessfulSyncAt
                    ? `Last successful sync: ${formatUtcDateTime(defenderXdrConfig.defenderXdrLastSuccessfulSyncAt)}`
                    : "Not synced yet — syncs nightly, or use Sync Now."}
                </div>
                {defenderFindingClassLines(defenderXdrConfig?.findingClasses).map(line => (
                  <div key={line.key} className="text-[11px] text-gray-500" data-testid={line.testId}>{line.text}</div>
                ))}
                {defenderXdrConfig?.defenderXdrConsecutiveFailureCount > 0 && (
                  <div className="text-[11px] text-red-700">
                    {defenderXdrConfig.defenderXdrConsecutiveFailureCount} consecutive sync failure{defenderXdrConfig.defenderXdrConsecutiveFailureCount > 1 ? "s" : ""}
                    {defenderXdrConfig.defenderXdrLastFailedDetector ? ` (${defenderXdrConfig.defenderXdrLastFailedDetector})` : ""}
                    {defenderXdrConfig.defenderXdrLastFailureAt ? ` — last at ${formatUtcDateTime(defenderXdrConfig.defenderXdrLastFailureAt)}` : ""}
                  </div>
                )}
                {defenderXdrTestResult && (
                  <div className={`text-[11px] ${defenderXdrTestResult.success ? "text-green-700" : "text-red-700"}`}>{defenderXdrTestResult.message}</div>
                )}
                {defenderXdrError && <div className="text-[11px] text-red-700">{defenderXdrError}</div>}
                {defenderXdrSaved && <div className="text-[11px] text-green-700">Defender XDR settings saved.</div>}
              </div>
            )}
            {defenderIdentityFeature && (
            <div className="flex items-start gap-2" data-testid="defender-identity-toggle">
              <input type="checkbox" checked={defenderIdentityEnabled} aria-label="Defender for Identity"
                onChange={e => setDefenderIdentityEnabled(e.target.checked)}
                disabled={!canIntegrations || (identityBlocked && !defenderIdentityConfig?.defenderIdentityEnabled)}
                className="rounded accent-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs text-gray-700">Defender for Identity — identity inventory</span>
                {identityBlocked && <UnavailableNote />}
                <div className="text-xs text-gray-400 leading-snug">Who the identities are: accounts from the Defender XDR IdentityInfo table (MDI sensors and Entra ID). Inventory only — creates no risks.</div>
              </div>
            </div>
            )}
            {defenderIdentityFeature && (
              <DefenderIdentityCard
                enabled={!!defenderIdentityConfig?.defenderIdentityEnabled}
                canManage={canIntegrations}
                unavailable={identityBlocked}
                refreshToken={defenderIdentityRefresh} />
            )}
            {defenderIdentityError && <div className="text-[11px] text-red-700">{defenderIdentityError}</div>}
          </div>
        </div>

      </div>

      {/* Connection Diagnostic */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="ms-connection-diagnostic">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">Connection Diagnostic</span>
          {canIntegrations && <button onClick={handleDiagnostic} disabled={diagLoading || msBlocked}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
            {diagLoading ? <><span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Testing…</> : "Test Connection"}
          </button>}
        </div>
        <div className="p-4">
          {diagResults ? (
            <div className="space-y-2">
              {diagResults.map((r, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg text-xs ${r.ok === true ? "bg-green-50 border border-green-200" : r.ok === false ? "bg-red-50 border border-red-200" : "bg-gray-50 border border-gray-200"}`}>
                  <span className="flex-shrink-0 mt-0.5">{r.ok === true ? "✅" : r.ok === false ? "❌" : "⚪"}</span>
                  <div>
                    <div className={`font-semibold ${r.ok === true ? "text-green-800" : r.ok === false ? "text-red-800" : "text-gray-600"}`}>{r.source}</div>
                    <div className={`mt-0.5 ${r.ok === true ? "text-green-700" : r.ok === false ? "text-red-700" : "text-gray-500"}`}>{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-xs text-gray-400 text-center py-4">Click <strong>Test Connection</strong> to verify each API source is reachable.</div>}
        </div>
      </div>

      {/* Required App Permissions */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Required App Permissions</span></div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Microsoft Graph (Application)</div>
            <div className="space-y-1.5">
              {[{ perm: "SecurityEvents.Read.All", note: "Secure Score data" }, { perm: "AuditLog.Read.All", note: "MFA registration details" }, { perm: "UserAuthenticationMethod.Read.All", note: "Auth method fallback" }, { perm: "Application.Read.All", note: "NHI / Agent ID — service principals, app credentials & blueprints" }, { perm: "RoleManagement.Read.Directory", note: "NHI — privileged role detection" }, { perm: "DelegatedPermissionGrant.ReadWrite.All", note: "NHI — OAuth permission grants" }, { perm: "Directory.Read.All", note: "Agent ID — agent users & directory reads" }, { perm: "IdentityRiskyAgent.Read.All", note: "Agent ID — risky agent detection (requires Entra ID P2)" }, { perm: "SecurityAlert.Read.All", note: "Cloud App Alerts and Purview DLP — security alerts" }, { perm: "ThreatHunting.Read.All", note: "Graph advanced hunting — local AI agent inventory (requires Defender for Endpoint)" }, { perm: "AuditLogsQuery.Read.All", note: "AI data-leak findings (Purview audit log)" }].map(p => (
                <div key={p.perm} className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs">
                  <span className="font-mono text-indigo-700 font-semibold">{p.perm}</span>
                  <span className="text-gray-400 ml-2">— {p.note}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Azure RBAC (Subscription)</div>
            <div className="space-y-1.5">
              {[{ perm: "Security Reader", note: "Defender for Cloud assessments" }, { perm: "Reader", note: "ARM subscription access" }].map(p => (
                <div key={p.perm} className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs">
                  <span className="font-mono text-indigo-700 font-semibold">{p.perm}</span>
                  <span className="text-gray-400 ml-2">— {p.note}</span>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-400 mt-2 italic">All Graph permissions require Admin Consent in Azure portal.</div>
          </div>
          {/* Defender XDR and Defender for Identity call the Defender APIs, not Graph — their app
              roles live on two separate resources (DefenderXdrService, DefenderIdentityHuntingClient). */}
          {(defenderXdrFeature || defenderIdentityFeature) && (
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Defender XDR APIs (Application)</div>
            <div className="space-y-1.5">
              {[
                ...(defenderXdrFeature ? [
                  { perm: "Incident.Read.All", note: "Microsoft Threat Protection — Defender XDR incidents" },
                  { perm: "Alert.Read.All", note: "WindowsDefenderATP — Defender XDR alerts" },
                  { perm: "Machine.Read.All", note: "WindowsDefenderATP — devices" },
                  { perm: "Vulnerability.Read.All", note: "WindowsDefenderATP — CVEs" },
                  { perm: "SecurityRecommendation.Read.All", note: "WindowsDefenderATP — security recommendations" },
                ] : []),
                ...(defenderIdentityFeature ? [
                  { perm: "AdvancedHunting.Read.All", note: "Microsoft Threat Protection — Defender for Identity inventory" },
                ] : []),
              ].map(p => (
                <div key={p.perm} className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs">
                  <span className="font-mono text-indigo-700 font-semibold">{p.perm}</span>
                  <span className="text-gray-400 ml-2">— {p.note}</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Surface Scan integration (Defender for Cloud) — part of the Surface Scan module, so it is
          hidden where that module is unavailable. */}
      {surfaceScanAvailable && (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Surface Scan Integration</span></div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            Include Microsoft Defender for Cloud posture findings (CVEs, open ports, cert issues) in this
            organization's Surface Scan.
          </p>

          {esmConfigError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-800">Could not load current Surface Scan settings — the toggle below may not reflect the saved value. Refresh the page to try again.</p>
            </div>
          )}

          {connStatus !== "Connected" ? (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">Connect Microsoft 365 / Azure above first to enable this.</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Connected</span>
              <span className="text-xs text-gray-600">
                {connection?.tenantName
                  ? <><strong>{connection.tenantName}</strong> — Microsoft Defender for Cloud</>
                  : "Microsoft Defender for Cloud connection active"}
              </span>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={msScanEnabled} onChange={e => setMsScanEnabled(e.target.checked)}
              disabled={!canManageAssignments || connStatus !== "Connected"} className="rounded accent-indigo-600" />
            <span className="text-xs text-gray-700">Include Microsoft Defender findings in surface scan</span>
          </label>

          {defenderStatus && (
            <div className={`p-3 rounded-lg border text-xs font-mono whitespace-pre-wrap break-all ${defenderStatus.ok ? "bg-gray-50 border-gray-200 text-gray-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              {JSON.stringify(defenderStatus, null, 2)}
            </div>
          )}

          {scanQueued && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                Scan started — findings will appear in the{" "}
                <a href="/DarkWebEsmDashboard" className="font-medium underline">Surface Scan dashboard</a>{" "}
                in a few minutes.
              </p>
            </div>
          )}

          {canManageAssignments && (
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={handleMsScanSave} disabled={msScanSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {msScanSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : "Save Settings"}
              </button>
              {connStatus === "Connected" && (
                <button onClick={handleTestDefender} disabled={testingDefender}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
                  {testingDefender ? <><span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Testing…</> : "Test Defender API"}
                </button>
              )}
              {msScanEnabled && connStatus === "Connected" && (
                <button onClick={handleMsScanNow} disabled={scanningNow || msScanSaving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-700 border border-indigo-300 rounded-lg text-xs font-semibold hover:bg-indigo-50 disabled:opacity-50 transition-colors">
                  {scanningNow ? <><span className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />Starting…</> : "Scan Now"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );

  // ── Expanded panel: Okta ─────────────────────────────────────────────────────
  const OktaPanel = () => (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">Okta Identity Connector</span>
        {canSecurityInsights && (
          <button disabled={sourceBlocked("okta")} onClick={() => { setOktaEditId(null); setOktaForm({ displayName: "", oktaOrgUrl: "", oktaApiToken: "" }); setOktaFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add Okta Connector
          </button>
        )}
      </div>

      {canSecurityInsights && oktaFormOpen && (
        <div className="px-4 py-4 mb-3 border border-sky-100 rounded-xl bg-sky-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
              <input value={oktaForm.displayName} onChange={e => setOktaForm(p => ({...p, displayName: e.target.value}))} placeholder="e.g. Acme Okta Prod"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Okta Org URL <span className="text-red-500">*</span></label>
              <input value={oktaForm.oktaOrgUrl} onChange={e => setOktaForm(p => ({...p, oktaOrgUrl: e.target.value}))} placeholder="https://yourorg.okta.com"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">API Token <span className="text-red-500">*</span></label>
              <input type="password" value={oktaForm.oktaApiToken} onChange={e => setOktaForm(p => ({...p, oktaApiToken: e.target.value}))} placeholder="SSWS token from Okta Admin"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveOktaConnector} disabled={oktaSaving || (sourceBlocked("okta") && !oktaEditId)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold transition-all ${oktaSaving ? "bg-sky-300 text-white cursor-not-allowed" : "bg-sky-600 text-white hover:bg-sky-700 active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}>
              {oktaSaving ? <><SyncSpinSvg /> Saving…</> : oktaEditId ? "Update Connector" : "Save Connector"}
            </button>
            <button onClick={() => setOktaFormOpen(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
            <span className="text-xs text-gray-400 ml-auto">Token needs <code className="font-mono bg-gray-100 px-1 rounded">okta.policies.read</code> and <code className="font-mono bg-gray-100 px-1 rounded">okta.users.read</code> scopes</span>
          </div>
        </div>
      )}

      <div className="mb-3">
        {oktaConnectors.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-4">No Okta connectors configured.{canSecurityInsights && <> Click <strong>Add Okta Connector</strong> to get started.</>}</div>
        ) : (
          <div className="space-y-2">
            {oktaConnectors.map(c => {
              const syncing = oktaSyncingIds.has(c.id);
              const deleting = oktaDeleteIds.has(c.id);
              return (
                <div key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${deleting ? "opacity-40 scale-95 pointer-events-none" : c.status === "CONNECTED" ? "border-green-200 bg-green-50" : c.status === "ERROR" ? "border-red-200 bg-red-50" : c.status === "SYNCING" || syncing ? "border-sky-200 bg-sky-50" : "border-gray-200 bg-gray-50"}`}>
                  <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-base flex-shrink-0">🔐</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-800">{c.displayName}</div>
                    {c.oktaOrgUrl && <div className="text-xs text-gray-500 font-mono truncate">{c.oktaOrgUrl}</div>}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${c.status === "CONNECTED" ? "bg-green-100 text-green-700" : c.status === "ERROR" ? "bg-red-100 text-red-700" : syncing ? "bg-sky-100 text-sky-700" : "bg-gray-200 text-gray-500"}`}>
                        {syncing ? "Syncing…" : c.status}
                      </span>
                      {c.lastSyncAt && <span className="text-xs text-gray-400">Last sync: {formatUtcDateTime(c.lastSyncAt)}</span>}
                    </div>
                    {c.syncError && <div className="text-xs text-red-600 mt-0.5 truncate">{c.syncError}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {canSecurityInsights && (<>
                      <button onClick={() => syncOktaConnector(c.id)} disabled={syncing || sourceBlocked("okta")} title="Pull latest Okta signals and update controls"
                        className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-all ${syncing ? "bg-sky-50 text-sky-400 border-sky-200 cursor-not-allowed" : "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-600 hover:text-white hover:border-sky-600 hover:shadow-md active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                        {syncing ? <><SyncSpinSvg /> Syncing…</> : <><SyncIconSvg /> Sync Now</>}
                      </button>
                      <button onClick={() => { setOktaEditId(c.id); setOktaForm({ displayName: c.displayName, oktaOrgUrl: c.oktaOrgUrl || "", oktaApiToken: "" }); setOktaFormOpen(true); }} title="Edit connector settings"
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
                      <button onClick={() => deleteOktaConnector(c.id)} disabled={deleting} title="Remove connector"
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Remove</button>
                    </>)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-sky-50 border border-sky-100 rounded-lg p-3">
        <div className="text-xs font-semibold text-sky-700 mb-1.5">Required Okta API Token Scopes</div>
        <div className="grid grid-cols-2 gap-1">
          {[
            { scope: "okta.policies.read", note: "MFA enroll + password + sign-on policies" },
            { scope: "okta.users.read", note: "Inactive + suspended account checks" },
            { scope: "okta.apps.read", note: "SSO application coverage" },
          ].map(p => (
            <div key={p.scope} className="bg-white border border-sky-100 rounded px-2 py-1.5 text-xs">
              <span className="font-mono text-sky-700 font-semibold">{p.scope}</span>
              <span className="text-gray-400 ml-1.5">— {p.note}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-sky-600 mt-2">Generate token: Okta Admin → Security → API → Tokens → Create Token. Assign a service account with Read-Only Admin role.</p>
      </div>
    </div>
  );

  // ── Expanded panel: AWS ──────────────────────────────────────────────────────
  const AwsPanel = () => {
    return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">AWS Security Hub Connector</span>
        {canSecurityInsights && (
          <button disabled={sourceBlocked("aws")} onClick={() => { setAwsEditId(null); setAwsForm({ displayName: "", awsAccessKeyId: "", awsSecretAccessKey: "", awsRegion: "us-east-1", awsAccountId: "" }); setAwsFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add AWS Connector
          </button>
        )}
      </div>

      {canSecurityInsights && awsFormOpen && (
        <div className="px-4 py-4 mb-3 border border-orange-100 rounded-xl bg-orange-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
              <input value={awsForm.displayName} onChange={e => setAwsForm(p => ({...p, displayName: e.target.value}))} placeholder="e.g. Acme AWS Production"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">AWS Region <span className="text-red-500">*</span></label>
              <input value={awsForm.awsRegion} onChange={e => setAwsForm(p => ({...p, awsRegion: e.target.value}))} placeholder="us-east-1"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Access Key ID <span className="text-red-500">*</span></label>
              <input value={awsForm.awsAccessKeyId} onChange={e => setAwsForm(p => ({...p, awsAccessKeyId: e.target.value}))} placeholder="AKIAIOSFODNN7EXAMPLE"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Secret Access Key <span className="text-red-500">*</span></label>
              <input type="password" value={awsForm.awsSecretAccessKey} onChange={e => setAwsForm(p => ({...p, awsSecretAccessKey: e.target.value}))} placeholder="••••••••••••••••••••"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Account ID <span className="text-gray-400 font-normal">(12 digits, no dashes)</span></label>
              <input value={awsForm.awsAccountId} onChange={e => setAwsForm(p => ({...p, awsAccountId: e.target.value.replace(/[^0-9]/g, "").slice(0, 12)}))} placeholder="123456789012"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveAwsConnector} disabled={awsSaving || (sourceBlocked("aws") && !awsEditId)}
              className="flex items-center gap-1.5 text-xs px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 active:scale-95 transition-all">
              {awsSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : (awsEditId ? "Update Connector" : "Save Connector")}
            </button>
            <button onClick={() => { setAwsFormOpen(false); setAwsEditId(null); }} className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-3">
        {awsConnectors.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-4">No AWS connectors configured.{canSecurityInsights && <> Click <strong>Add AWS Connector</strong> to get started.</>}</div>
        ) : (
          <div className="space-y-2">
            {awsConnectors.map(c => {
              const syncing = awsSyncingIds.has(c.id);
              const deleting = awsDeleteIds.has(c.id);
              return (
                <div key={c.id} className="px-3 py-2.5 bg-orange-50 border border-orange-100 rounded-xl">
                  <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800 truncate">{c.displayName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${c.status === "CONNECTED" ? "bg-green-100 text-green-700" : c.status === "ERROR" ? "bg-red-100 text-red-700" : c.status === "SYNCING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{c.status}</span>
                    </div>
                    {(c.awsRegion || c.awsAccountId) && <div className="text-xs text-gray-500 font-mono truncate">{[c.awsRegion, c.awsAccountId].filter(Boolean).join(" · ")}</div>}
                    {c.lastSyncAt && <div className="text-xs text-gray-400 mt-0.5">Last sync: {formatUtcDateTime(c.lastSyncAt)}</div>}
                    {c.syncError && <div className="text-xs text-red-500 mt-0.5 truncate" title={c.syncError}>{c.syncError}</div>}
                  </div>
                  {canSecurityInsights && (<>
                    <button onClick={() => testAwsConnector(c.id)} disabled={awsTestingIds.has(c.id) || sourceBlocked("aws")}
                      className="text-xs px-2.5 py-1.5 ml-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95 disabled:opacity-50">
                      {awsTestingIds.has(c.id) ? "Testing…" : "Test"}
                    </button>
                    <button onClick={() => syncAwsConnector(c.id)} disabled={syncing || sourceBlocked("aws")}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-all ml-2 ${syncing ? "bg-orange-50 text-orange-400 border-orange-200 cursor-not-allowed" : "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-600 hover:text-white hover:border-orange-600 hover:shadow-md active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                      {syncing ? <><SyncSpinSvg /> Syncing…</> : <><SyncIconSvg /> Sync Now</>}
                    </button>
                    <button onClick={() => { setAwsEditId(c.id); setAwsForm({ displayName: c.displayName, awsAccessKeyId: "", awsSecretAccessKey: "", awsRegion: c.awsRegion || "us-east-1", awsAccountId: c.awsAccountId || "" }); setAwsFormOpen(true); }}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
                    <button onClick={() => deleteAwsConnector(c.id)} disabled={deleting}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Remove</button>
                  </>)}
                  </div>
                  {awsDiagResults[c.id] && (
                    <div className="mt-2 pt-2 border-t border-orange-200 space-y-1">
                      {awsDiagResults[c.id].map((r, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs">
                          <span className={`font-bold shrink-0 ${r.ok === true ? "text-green-600" : r.ok === false ? "text-red-600" : "text-gray-400"}`}>
                            {r.ok === true ? "\u2713" : r.ok === false ? "\u2717" : "\u2022"}
                          </span>
                          <span className="font-semibold text-gray-700 shrink-0">{r.source}:</span>
                          <span className="text-gray-600">{r.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
        <div className="text-xs font-semibold text-orange-700 mb-1.5">Required AWS IAM Permissions</div>
        <div className="grid grid-cols-2 gap-1">
          {[
            { perm: "securityhub:GetFindings", note: "Pull Security Hub findings" },
            { perm: "securityhub:GetEnabledStandards", note: "List enabled standards" },
            { perm: "guardduty:ListDetectors", note: "Check GuardDuty status" },
            { perm: "guardduty:GetDetector", note: "Detector status details" },
            { perm: "cloudtrail:DescribeTrails", note: "Multi-region trail check" },
            { perm: "cloudtrail:GetTrailStatus", note: "Trail logging status" },
            { perm: "s3:GetAccountPublicAccessBlock", note: "Account-level S3 block" },
          ].map(p => (
            <div key={p.perm} className="bg-white border border-orange-100 rounded px-2 py-1.5 text-xs">
              <span className="font-mono text-orange-700 font-semibold">{p.perm}</span>
              <span className="text-gray-400 ml-1.5">— {p.note}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-orange-600 mt-2">Create a dedicated IAM user with read-only permissions. Use AWS IAM Console → Create User → Attach policies: SecurityAudit (AWS managed) covers all required permissions.</p>
      </div>

      {showAiIntelligence && (
        <div data-testid="aws-ai-discovery-box" className="bg-purple-50 border border-purple-100 rounded-lg p-3">
          <div className="text-xs font-semibold text-purple-700 mb-1.5">Additional Permissions for AI Intelligence (AWS discovery)</div>
          <p data-testid="aws-ai-discovery-principal" className="text-xs text-purple-700 font-semibold mb-1.5">
            Attach these to the IAM user whose access keys this connector uses. Axaris calls AWS with those keys
            directly and never assumes a role, so a policy attached to a role has no effect.
          </p>
          <p className="text-xs text-purple-600 mb-2">
            Used when AWS discovery is enabled under AI Intelligence → settings. All read-only.
            SecurityAudit does <span className="font-semibold">not</span> cover the Bedrock permissions — attach them as an
            additional inline policy on that same IAM user. Tag agents with{" "}
            <span className="font-mono">owner=&lt;email&gt;</span> to mark their accountable owner.
          </p>
          <div data-testid="aws-ai-discovery-actions" className="space-y-2">
            {AWS_TIER_DISPLAY.map(t => (
              <div key={t.tier} data-testid={`aws-ai-discovery-tier-${t.tier}`}>
                <div className="flex items-start mb-1">
                  <RequirementBadge label={t.label} tone={t.tone} />
                  <span className="text-xs text-purple-700">{t.consequence}</span>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {AWS_AI_DISCOVERY_ACTIONS.filter(a => a.tier === t.tier).map(a => (
                    <div key={a.action} data-testid={`aws-ai-discovery-action-${a.action}`} className={`bg-white border rounded px-2 py-1.5 text-xs ${t.tone === "critical" ? "border-purple-300" : "border-purple-100"}`}>
                      <span className="font-mono text-purple-700 font-semibold">{a.action}</span>
                      <span className="text-gray-400 ml-1.5">— {a.enables}</span>
                      {a.caveat && (
                        <div data-testid="aws-ai-discovery-caveat" className="text-amber-700 mt-0.5">{a.caveat}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p data-testid="aws-ai-discovery-test-note" className="text-xs text-purple-600 mt-2">
            The connector&apos;s Test button reports on the five Discovery-critical actions only, using the IAM policy
            simulator for the connector&apos;s principal against resource * with no request context. A simulation is not
            a live call. Test does not check the Enrichment or Behavioural &amp; usage actions, or whether Cost Explorer
            is enabled, so a passing Test does not confirm any of those.
          </p>
        </div>
      )}
    </div>
    );
  };

  // ── Expanded panel: Google Cloud ─────────────────────────────────────────────
  // The Test button calls the same per-connector diagnostic endpoint as the AWS cards (it answers for
  // Google Cloud with its own credential and containment-grant rows) and reuses the AWS card's
  // diagnostic state and row rendering.
  const GcpPanel = () => {
    const isOrgScope = gcpForm.gcpScopeType === "ORGANIZATION";
    return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">Google Cloud Connector</span>
        {canSecurityInsights && gcpConnectors.length === 0 && (
          <button disabled={sourceBlocked("gcp")} onClick={() => { setGcpEditId(null); setGcpForm(emptyGcpForm); setGcpFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add Google Cloud Connector
          </button>
        )}
      </div>

      {!canSecurityInsights && (
        <div className="text-xs text-gray-500 italic">Only an organization Risk Manager or above can change Google Cloud connectors.</div>
      )}
      {canSecurityInsights && gcpConnectors.length > 0 && !gcpFormOpen && (
        <div className="text-xs text-gray-500">One Google Cloud connector per organization. Use Organization scope to cover all projects.</div>
      )}

      {canSecurityInsights && gcpFormOpen && (
        <div className="px-4 py-4 mb-3 border border-blue-100 rounded-xl bg-blue-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
              <input value={gcpForm.displayName} onChange={e => setGcpForm(p => ({...p, displayName: e.target.value}))} placeholder="e.g. Acme Google Cloud"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Scope <span className="text-red-500">*</span></label>
              <select value={gcpForm.gcpScopeType} onChange={e => setGcpForm(p => ({...p, gcpScopeType: e.target.value}))}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="ORGANIZATION">Organization</option>
                <option value="PROJECT">Project</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{isOrgScope ? "Organization ID" : "Project ID"} <span className="text-red-500">*</span></label>
              <input value={gcpForm.gcpScopeId} onChange={e => setGcpForm(p => ({...p, gcpScopeId: e.target.value}))} placeholder={isOrgScope ? "123456789012" : "my-project-id"}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
              <p className="text-xs text-gray-400 mt-1">
                {isOrgScope ? "Numeric ID from Google Cloud console → IAM & Admin → Settings" : "The project ID, not the project name or number"}
              </p>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600">
                  Service Account Key (JSON) {!gcpEditId && <span className="text-red-500">*</span>}
                </label>
                <label className="text-xs px-2.5 py-1 rounded-lg border border-blue-200 text-blue-700 bg-white hover:bg-blue-100 font-semibold cursor-pointer transition-all">
                  Upload key file
                  <input type="file" accept=".json,application/json" onChange={loadGcpKeyFile} className="hidden" />
                </label>
              </div>
              <textarea value={gcpForm.gcpServiceAccountKeyJson} onChange={e => setGcpForm(p => ({...p, gcpServiceAccountKeyJson: e.target.value}))}
                rows={6} autoComplete="off" spellCheck={false}
                placeholder={gcpEditId ? "Leave blank to keep the current key" : "Paste the JSON key file, or upload it"}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveGcpConnector} disabled={gcpSaving || (sourceBlocked("gcp") && !gcpEditId)}
              className="flex items-center gap-1.5 text-xs px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 active:scale-95 transition-all">
              {gcpSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : (gcpEditId ? "Update Connector" : "Save Connector")}
            </button>
            <button onClick={closeGcpForm} className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-3">
        {gcpConnectors.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-4">
            No Google Cloud connector configured.{canSecurityInsights && <> Click <strong>Add Google Cloud Connector</strong> to get started.</>}
          </div>
        ) : (
          <div className="space-y-2">
            {gcpConnectors.map(c => {
              const syncing = gcpSyncingIds.has(c.id);
              const deleting = gcpDeleteIds.has(c.id);
              return (
                <div key={c.id} className="px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                  <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800 truncate">{c.displayName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${c.status === "CONNECTED" ? "bg-green-100 text-green-700" : c.status === "ERROR" ? "bg-red-100 text-red-700" : c.status === "SYNCING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{c.status}</span>
                    </div>
                    {c.gcpScopeId && <div className="text-xs text-gray-500 font-mono truncate">{c.gcpScopeType === "PROJECT" ? "Project" : "Organization"} {c.gcpScopeId}</div>}
                    {c.gcpClientEmail && <div className="text-xs text-gray-500 font-mono truncate" title={c.gcpClientEmail}>{c.gcpClientEmail}</div>}
                    {c.lastSyncAt && <div className="text-xs text-gray-400 mt-0.5">Last sync: {formatUtcDateTime(c.lastSyncAt)}</div>}
                    {c.syncError && <div className="text-xs text-red-500 mt-0.5 truncate" title={c.syncError}>{c.syncError}</div>}
                  </div>
                  {canSecurityInsights && (<>
                    <button onClick={() => testAwsConnector(c.id)} disabled={awsTestingIds.has(c.id) || sourceBlocked("gcp")}
                      className="text-xs px-2.5 py-1.5 ml-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95 disabled:opacity-50">
                      {awsTestingIds.has(c.id) ? "Testing…" : "Test"}
                    </button>
                    <button onClick={() => syncGcpConnector(c.id)} disabled={syncing || sourceBlocked("gcp")}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-all ml-2 ${syncing ? "bg-blue-50 text-blue-400 border-blue-200 cursor-not-allowed" : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 hover:shadow-md active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                      {syncing ? <><SyncSpinSvg /> Syncing…</> : <><SyncIconSvg /> Sync Now</>}
                    </button>
                    <button onClick={() => { setGcpEditId(c.id); setGcpForm({ displayName: c.displayName || "", gcpScopeType: c.gcpScopeType === "PROJECT" ? "PROJECT" : "ORGANIZATION", gcpScopeId: c.gcpScopeId || "", gcpServiceAccountKeyJson: "" }); setGcpFormOpen(true); }}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
                    <button onClick={() => deleteGcpConnector(c.id)} disabled={deleting}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Delete</button>
                  </>)}
                  </div>
                  {awsDiagResults[c.id] && (
                    <div className="mt-2 pt-2 border-t border-blue-200 space-y-1">
                      {awsDiagResults[c.id].map((r, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs">
                          <span className={`font-bold shrink-0 ${r.ok === true ? "text-green-600" : r.ok === false ? "text-red-600" : "text-gray-400"}`}>
                            {r.ok === true ? "✓" : r.ok === false ? "✗" : "•"}
                          </span>
                          <span className="font-semibold text-gray-700 shrink-0">{r.source}:</span>
                          <span className="text-gray-600">{r.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div data-testid="gcp-required-access" className="bg-blue-50 border border-blue-100 rounded-lg p-3">
        <div className="text-xs font-semibold text-blue-700 mb-1.5">Required access</div>
        <p className="text-xs text-blue-700 mb-1.5">The service account needs these roles on the organization or project being scanned:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {[
            { role: "roles/securitycenter.findingsViewer", note: "Read Security Command Center findings" },
            { role: "roles/securitycentermanagement.viewer", note: "Read SCC service enablement" },
            { role: "roles/iam.securityReviewer", note: "Read audit log configuration" },
            { role: "roles/logging.viewer", note: "Read log export sinks" },
            { role: "roles/orgpolicy.policyViewer", note: "Read organization policies" },
          ].map(r => (
            <div key={r.role} className="bg-white border border-blue-100 rounded px-2 py-1.5 text-xs">
              <span className="font-mono text-blue-700 font-semibold">{r.role}</span>
              <span className="text-gray-400 ml-1.5">— {r.note}</span>
            </div>
          ))}
        </div>
        {showAiIntelligence && (
        <div data-testid="gcp-ai-discovery-grants" className="mt-3">
          <div className="text-xs font-semibold text-blue-700 mb-1">AI Intelligence discovery</div>
          <p className="text-xs text-blue-700 mb-1.5">
            AI Intelligence discovery needs these grants in addition to the roles above. Discovery fails without the
            three marked Required; each Optional grant adds one capability and can be left out. They are not all held in
            the same place: each entry says where to grant it, and some belong on the project that owns the service
            account rather than on the organization or project being scanned.
          </p>
          <div className="grid grid-cols-1 gap-1">
            {GCP_AI_DISCOVERY_GRANTS.map(g => (
              <div key={g.grant} className={`bg-white border rounded px-2 py-1.5 text-xs ${g.required ? "border-blue-300" : "border-blue-100"}`}>
                <div className="flex items-start">
                  <RequirementBadge label={g.required ? "Required" : "Optional"} tone={g.required ? "critical" : "optional"} />
                  <span>
                    <span className="font-mono text-blue-700 font-semibold">{g.grant}</span>
                    <span className="text-gray-400 ml-1.5">— {g.enables}</span>
                  </span>
                </div>
                <div className="text-gray-500 mt-0.5">Held on: {g.whereHeld}</div>
              </div>
            ))}
          </div>
        </div>
        )}
        <p className="text-xs text-blue-600 mt-2">
          Enable these APIs in the project that owns the service account: Security Command Center API, Security Command
          Center Management API, Cloud Resource Manager API, Cloud Logging API, Organization Policy API.
        </p>
        {showAiIntelligence && (
        <div data-testid="gcp-ai-discovery-apis" className="mt-2">
          <p className="text-xs text-blue-600 mb-1">For AI Intelligence discovery, also enable these APIs in that same project:</p>
          <div className="grid grid-cols-1 gap-1">
            {GCP_AI_DISCOVERY_APIS.map(a => (
              <div key={a.serviceId} className={`bg-white border rounded px-2 py-1.5 text-xs flex items-start ${a.required ? "border-blue-300" : "border-blue-100"}`}>
                <RequirementBadge label={a.required ? "Required" : "Optional"} tone={a.required ? "critical" : "optional"} />
                <span>
                  <span className="font-mono text-blue-700 font-semibold">{a.serviceId}</span>
                  <span className="text-gray-400 ml-1.5">— {a.purpose}</span>
                </span>
              </div>
            ))}
          </div>
          <p data-testid="gcp-ai-discovery-test-note" className="text-xs text-blue-600 mt-2">
            The connector&apos;s Test button includes checks of the two required discovery APIs and the three required
            discovery grants, alongside its credential and containment checks. It does not verify the optional discovery
            grants or optional APIs, so a passing Test does not confirm that the optional capabilities work.
          </p>
        </div>
        )}
      </div>
    </div>
    );
  };

  // ── Expanded panel: Tenable ──────────────────────────────────────────────────
  const TenablePanel = () => (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">Tenable.io Vulnerability Connector</span>
        {canSecurityInsights && (
          <button disabled={sourceBlocked("tenable")} onClick={() => { setTenableEditId(null); setTenableForm({ displayName: "", tenableAccessKey: "", tenableSecretKey: "" }); setTenableFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add Tenable Connector
          </button>
        )}
      </div>

      {canSecurityInsights && tenableFormOpen && (
        <div className="px-4 py-4 mb-3 border border-teal-100 rounded-xl bg-teal-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
              <input value={tenableForm.displayName} onChange={e => setTenableForm(p => ({...p, displayName: e.target.value}))} placeholder="e.g. Acme Tenable.io"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Access Key <span className="text-red-500">*</span></label>
              <input value={tenableForm.tenableAccessKey} onChange={e => setTenableForm(p => ({...p, tenableAccessKey: e.target.value}))} placeholder="Tenable Access Key"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Secret Key <span className="text-red-500">*</span></label>
              <input type="password" value={tenableForm.tenableSecretKey} onChange={e => setTenableForm(p => ({...p, tenableSecretKey: e.target.value}))} placeholder="••••••••••••••••••••"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveTenableConnector} disabled={tenableSaving || (sourceBlocked("tenable") && !tenableEditId)}
              className="flex items-center gap-1.5 text-xs px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50 active:scale-95 transition-all">
              {tenableSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : (tenableEditId ? "Update Connector" : "Save Connector")}
            </button>
            <button onClick={() => { setTenableFormOpen(false); setTenableEditId(null); }} className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-3">
        {tenableConnectors.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-4">No Tenable connectors configured.{canSecurityInsights && <> Click <strong>Add Tenable Connector</strong> to get started.</>}</div>
        ) : (
          <div className="space-y-2">
            {tenableConnectors.map(c => {
              const syncing = tenableSyncingIds.has(c.id);
              const deleting = tenableDeleteIds.has(c.id);
              return (
                <div key={c.id} className="flex items-center justify-between px-3 py-2.5 bg-teal-50 border border-teal-100 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800 truncate">{c.displayName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${c.status === "CONNECTED" ? "bg-green-100 text-green-700" : c.status === "ERROR" ? "bg-red-100 text-red-700" : c.status === "SYNCING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{c.status}</span>
                    </div>
                    {c.lastSyncAt && <div className="text-xs text-gray-400 mt-0.5">Last sync: {formatUtcDateTime(c.lastSyncAt)}</div>}
                    {c.syncError && <div className="text-xs text-red-500 mt-0.5 truncate" title={c.syncError}>{c.syncError}</div>}
                  </div>
                  {canSecurityInsights && (<>
                    <button onClick={() => syncTenableConnector(c.id)} disabled={syncing || sourceBlocked("tenable")}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-all ml-2 ${syncing ? "bg-teal-50 text-teal-400 border-teal-200 cursor-not-allowed" : "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-600 hover:text-white hover:border-teal-600 hover:shadow-md active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                      {syncing ? <><SyncSpinSvg /> Syncing…</> : <><SyncIconSvg /> Sync Now</>}
                    </button>
                    <button onClick={() => { setTenableEditId(c.id); setTenableForm({ displayName: c.displayName, tenableAccessKey: "", tenableSecretKey: "" }); setTenableFormOpen(true); }}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
                    <button onClick={() => deleteTenableConnector(c.id)} disabled={deleting}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Remove</button>
                  </>)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-teal-50 border border-teal-100 rounded-lg p-3">
        <div className="text-xs font-semibold text-teal-700 mb-1.5">Tenable.io API Key Setup</div>
        <div className="grid grid-cols-2 gap-1">
          {[
            { scope: "Vulnerability Management", note: "Pull vuln findings and severity" },
            { scope: "Assets", note: "Asset coverage and scan status" },
            { scope: "Workbench access", note: "Aggregated risk data" },
          ].map(p => (
            <div key={p.scope} className="bg-white border border-teal-100 rounded px-2 py-1.5 text-xs">
              <span className="font-semibold text-teal-700">{p.scope}</span>
              <span className="text-gray-400 ml-1.5">— {p.note}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-teal-600 mt-2">Generate keys: Tenable.io → My Account → API Keys → Generate. Use a service account with Can View (Scan Operator) role for read-only access.</p>
      </div>
    </div>
  );

  // ── SplunkPanel ──────────────────────────────────────────────────────────────
  const SplunkPanel = () => (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">Splunk SIEM Connector</span>
        {canSecurityInsights && (
          <button disabled={sourceBlocked("splunk")} onClick={() => { setSplunkEditId(null); setSplunkForm({ displayName: "", splunkHost: "", splunkPort: "8089", splunkToken: "", splunkUsername: "", splunkPassword: "", splunkIndex: "", splunkAllowSelfSignedCert: false }); setSplunkAuthMode("token"); setSplunkFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#7C3AED" }}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add Splunk Connector
          </button>
        )}
      </div>

      {canSecurityInsights && splunkFormOpen && (
        <div className="px-4 py-4 mb-3 border rounded-xl" style={{ background: "#FAF5FF", borderColor: "#E9D5FF" }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
              <input value={splunkForm.displayName} onChange={e => setSplunkForm(p => ({ ...p, displayName: e.target.value }))}
                placeholder="e.g. Acme Splunk SIEM"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2" style={{ '--tw-ring-color': '#7C3AED' }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Splunk Host <span className="text-red-500">*</span></label>
              <input value={splunkForm.splunkHost} onChange={e => setSplunkForm(p => ({ ...p, splunkHost: e.target.value }))}
                placeholder="splunk.company.com"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Port <span className="text-gray-400">(default 8089)</span></label>
              <input value={splunkForm.splunkPort} onChange={e => setSplunkForm(p => ({ ...p, splunkPort: e.target.value }))}
                placeholder="8089"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 font-mono" />
            </div>
          </div>

          {/* Auth mode toggle */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Authentication Method</label>
            <div className="flex gap-2">
              {[{ key: "token", label: "Bearer Token" }, { key: "userpass", label: "Username / Password" }].map(m => (
                <button key={m.key} onClick={() => setSplunkAuthMode(m.key)}
                  className="text-xs px-3 py-1.5 rounded-lg border font-semibold transition-all"
                  style={splunkAuthMode === m.key
                    ? { background: "#7C3AED", color: "#fff", borderColor: "#7C3AED" }
                    : { background: "#fff", color: "#6B7280", borderColor: "#E5E7EB" }}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {splunkAuthMode === "token" ? (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Bearer Token <span className="text-red-500">*</span></label>
              <input type="password" value={splunkForm.splunkToken} onChange={e => setSplunkForm(p => ({ ...p, splunkToken: e.target.value }))}
                placeholder="••••••••••••••••••••••••••••••••"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 font-mono" />
              <p className="text-xs text-gray-400 mt-1">Settings → Tokens → New Token in Splunk Web.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Username <span className="text-red-500">*</span></label>
                <input value={splunkForm.splunkUsername} onChange={e => setSplunkForm(p => ({ ...p, splunkUsername: e.target.value }))}
                  placeholder="splunk_api_user"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password <span className="text-red-500">*</span></label>
                <input type="password" value={splunkForm.splunkPassword} onChange={e => setSplunkForm(p => ({ ...p, splunkPassword: e.target.value }))}
                  placeholder="••••••••••••••••"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 font-mono" />
              </div>
            </div>
          )}

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Default Index <span className="text-gray-400">(optional, default: *)</span></label>
            <input value={splunkForm.splunkIndex} onChange={e => setSplunkForm(p => ({ ...p, splunkIndex: e.target.value }))}
              placeholder="main  or  security  or leave blank for all"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 font-mono" />
          </div>

          <div className="mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={splunkForm.splunkAllowSelfSignedCert} onChange={e => setSplunkForm(p => ({ ...p, splunkAllowSelfSignedCert: e.target.checked }))}
                className="rounded accent-purple-600" />
              <span className="text-xs font-medium text-gray-600">Allow self-signed SSL certificate</span>
            </label>
            <p className="text-xs text-amber-600 mt-1 ml-5">Warning: Enabling this disables SSL certificate validation for this connector. Only use for on-premises Splunk instances with self-signed certificates.</p>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={saveSplunkConnector} disabled={splunkSaving || (sourceBlocked("splunk") && !splunkEditId)}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg font-semibold text-white disabled:opacity-50 active:scale-95 transition-all"
              style={{ background: "#7C3AED" }}>
              {splunkSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : (splunkEditId ? "Update Connector" : "Save Connector")}
            </button>
            <button onClick={() => { setSplunkFormOpen(false); setSplunkEditId(null); }}
              className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-3">
        {splunkConnectors.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-4">No Splunk connectors configured.{canSecurityInsights && <> Click <strong>Add Splunk Connector</strong> to get started.</>}</div>
        ) : (
          <div className="space-y-2">
            {splunkConnectors.map(c => {
              const syncing  = splunkSyncingIds.has(c.id);
              const deleting = splunkDeleteIds.has(c.id);
              return (
                <div key={c.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border" style={{ background: "#FAF5FF", borderColor: "#E9D5FF" }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800 truncate">{c.displayName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${c.status === "CONNECTED" ? "bg-green-100 text-green-700" : c.status === "ERROR" ? "bg-red-100 text-red-700" : c.status === "SYNCING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{c.status}</span>
                    </div>
                    {c.splunkHost && <div className="text-xs text-gray-500 font-mono truncate">{c.splunkHost}{c.splunkPort ? `:${c.splunkPort}` : ""}</div>}
                    {c.lastSyncAt && <div className="text-xs text-gray-400 mt-0.5">Last sync: {formatUtcDateTime(c.lastSyncAt)}</div>}
                    {c.syncError && <div className="text-xs text-red-500 mt-0.5 truncate" title={c.syncError}>{c.syncError}</div>}
                  </div>
                  {canSecurityInsights && (<>
                    <button onClick={() => syncSplunkConnector(c.id)} disabled={syncing || sourceBlocked("splunk")}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-all ml-2 ${syncing ? "opacity-50 cursor-not-allowed" : "hover:shadow-md active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}
                      style={syncing ? { background: "#FAF5FF", color: "#A78BFA", borderColor: "#E9D5FF" } : { background: "#FAF5FF", color: "#7C3AED", borderColor: "#E9D5FF" }}
                      onMouseEnter={e => { if (!syncing) { e.currentTarget.style.background = "#7C3AED"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#7C3AED"; }}}
                      onMouseLeave={e => { if (!syncing) { e.currentTarget.style.background = "#FAF5FF"; e.currentTarget.style.color = "#7C3AED"; e.currentTarget.style.borderColor = "#E9D5FF"; }}}>
                      {syncing
                        ? <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> Syncing…</>
                        : <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg> Sync Now</>}
                    </button>
                    <button onClick={() => { setSplunkEditId(c.id); setSplunkForm({ displayName: c.displayName, splunkHost: c.splunkHost || "", splunkPort: c.splunkPort || "8089", splunkToken: "", splunkUsername: c.splunkUsername || "", splunkPassword: "", splunkIndex: c.splunkIndex || "", splunkAllowSelfSignedCert: c.splunkAllowSelfSignedCert || false }); setSplunkFormOpen(true); }}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
                    <button onClick={() => deleteSplunkConnector(c.id)} disabled={deleting}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Remove</button>
                  </>)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-lg p-3" style={{ background: "#FAF5FF", border: "1px solid #E9D5FF" }}>
        <div className="text-xs font-semibold mb-1.5" style={{ color: "#7C3AED" }}>Splunk API Access Requirements</div>
        <div className="grid grid-cols-2 gap-1">
          {[
            { scope: "indexes_list_all",    note: "List and read indexes" },
            { scope: "search",              note: "Run one-shot SPL searches" },
            { scope: "list_saved_searches",  note: "Read saved search / alert rules" },
            { scope: "apps_list_all",        note: "Detect installed security apps" },
          ].map(p => (
            <div key={p.scope} className="bg-white rounded px-2 py-1.5 text-xs" style={{ border: "1px solid #E9D5FF" }}>
              <span className="font-semibold font-mono" style={{ color: "#7C3AED" }}>{p.scope}</span>
              <span className="text-gray-400 ml-1.5">— {p.note}</span>
            </div>
          ))}
        </div>
        <p className="text-xs mt-2" style={{ color: "#6D28D9" }}>
          <strong>Token (recommended):</strong> Splunk Web → Settings → Tokens → New Token. Assign the <em>can_delete</em> or <em>power</em> role minimum.
          Alternatively use a dedicated service account with the <em>user</em> role and the capabilities above.
        </p>
      </div>
    </div>
  );

  // ── WizPanel ─────────────────────────────────────────────────────────────────
  const WizPanel = () => (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">Wiz Cloud Security Connector</span>
        {canSecurityInsights && (
          <button disabled={sourceBlocked("wiz")} onClick={() => { setWizEditId(null); setWizForm({ displayName: "", wizClientId: "", wizClientSecret: "", wizApiEndpoint: "https://api.app.wiz.io/graphql" }); setWizFormOpen(v => !v); }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#9333EA" }}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Add Wiz Connector
          </button>
        )}
      </div>

      {canSecurityInsights && wizFormOpen && (
        <div className="px-4 py-4 mb-3 border rounded-xl" style={{ background: "#FAF5FF", borderColor: "#E9D5FF" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name <span className="text-red-500">*</span></label>
              <input value={wizForm.displayName} onChange={e => setWizForm(p => ({ ...p, displayName: e.target.value }))}
                placeholder="e.g. Acme Wiz CSPM"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">API Endpoint <span className="text-gray-400">(optional)</span></label>
              <input value={wizForm.wizApiEndpoint} onChange={e => setWizForm(p => ({ ...p, wizApiEndpoint: e.target.value }))}
                placeholder="https://api.app.wiz.io/graphql"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client ID <span className="text-red-500">*</span></label>
              <input value={wizForm.wizClientId} onChange={e => setWizForm(p => ({ ...p, wizClientId: e.target.value }))}
                placeholder="Service account client ID"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client Secret <span className="text-red-500">*</span></label>
              <input type="password" value={wizForm.wizClientSecret} onChange={e => setWizForm(p => ({ ...p, wizClientSecret: e.target.value }))}
                placeholder="••••••••••••••••••••••••••••••••"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveWizConnector} disabled={wizSaving || (sourceBlocked("wiz") && !wizEditId)}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg font-semibold text-white disabled:opacity-50 active:scale-95 transition-all"
              style={{ background: "#9333EA" }}>
              {wizSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : (wizEditId ? "Update Connector" : "Save Connector")}
            </button>
            <button onClick={() => { setWizFormOpen(false); setWizEditId(null); }}
              className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-3">
        {wizConnectors.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-4">No Wiz connectors configured.{canSecurityInsights && <> Click <strong>Add Wiz Connector</strong> to get started.</>}</div>
        ) : (
          <div className="space-y-2">
            {wizConnectors.map(c => {
              const syncing  = wizSyncingIds.has(c.id);
              const deleting = wizDeleteIds.has(c.id);
              return (
                <div key={c.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border" style={{ background: "#FAF5FF", borderColor: "#E9D5FF" }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800 truncate">{c.displayName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${c.status === "CONNECTED" ? "bg-green-100 text-green-700" : c.status === "ERROR" ? "bg-red-100 text-red-700" : c.status === "SYNCING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{c.status}</span>
                    </div>
                    {c.wizApiEndpoint && <div className="text-xs text-gray-500 font-mono truncate">{c.wizApiEndpoint}</div>}
                    {c.lastSyncAt && <div className="text-xs text-gray-400 mt-0.5">Last sync: {formatUtcDateTime(c.lastSyncAt)}</div>}
                    {c.syncError && <div className="text-xs text-red-500 mt-0.5 truncate" title={c.syncError}>{c.syncError}</div>}
                  </div>
                  {canSecurityInsights && (<>
                    <button onClick={() => syncWizConnector(c.id)} disabled={syncing || sourceBlocked("wiz")}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border font-semibold transition-all ml-2 ${syncing ? "opacity-50 cursor-not-allowed" : "hover:shadow-md active:scale-95"} disabled:opacity-40 disabled:cursor-not-allowed`}
                      style={syncing ? { background: "#FAF5FF", color: "#C084FC", borderColor: "#E9D5FF" } : { background: "#FAF5FF", color: "#9333EA", borderColor: "#E9D5FF" }}
                      onMouseEnter={e => { if (!syncing) { e.currentTarget.style.background = "#9333EA"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#9333EA"; }}}
                      onMouseLeave={e => { if (!syncing) { e.currentTarget.style.background = "#FAF5FF"; e.currentTarget.style.color = "#9333EA"; e.currentTarget.style.borderColor = "#E9D5FF"; }}}>
                      {syncing
                        ? <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> Syncing…</>
                        : <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg> Sync Now</>}
                    </button>
                    <button onClick={() => { setWizEditId(c.id); setWizForm({ displayName: c.displayName, wizClientId: "", wizClientSecret: "", wizApiEndpoint: c.wizApiEndpoint || "https://api.app.wiz.io/graphql" }); setWizFormOpen(true); }}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-semibold transition-all active:scale-95">Edit</button>
                    <button onClick={() => deleteWizConnector(c.id)} disabled={deleting}
                      className="text-xs px-2.5 py-1.5 ml-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 font-semibold transition-all active:scale-95">Remove</button>
                  </>)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-lg p-3" style={{ background: "#FAF5FF", border: "1px solid #E9D5FF" }}>
        <div className="text-xs font-semibold mb-1.5" style={{ color: "#9333EA" }}>Wiz Service Account Setup</div>
        <div className="grid grid-cols-2 gap-1">
          {[
            { scope: "read:issues",           note: "Pull open CSPM findings by severity" },
            { scope: "read:controls",          note: "Control pass/fail compliance posture" },
            { scope: "read:cloud_config_rules", note: "Misconfiguration detection rules" },
            { scope: "read:resources",         note: "Asset inventory and coverage" },
          ].map(p => (
            <div key={p.scope} className="bg-white rounded px-2 py-1.5 text-xs" style={{ border: "1px solid #E9D5FF" }}>
              <span className="font-semibold font-mono" style={{ color: "#9333EA" }}>{p.scope}</span>
              <span className="text-gray-400 ml-1.5">— {p.note}</span>
            </div>
          ))}
        </div>
        <p className="text-xs mt-2" style={{ color: "#6D28D9" }}>
          Create a service account in <strong>Wiz Settings → Service Accounts</strong> with the <em>read:security_scans</em> scope group.
          Copy the Client ID and Client Secret — the secret is shown only once.
        </p>
      </div>
    </div>
  );

  // ── FortinetPanel ────────────────────────────────────────────────────────────
  const FortinetPanel = () => (
    <div className="space-y-4">
      {fortinetSaved && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-xs font-medium flex items-center gap-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
          Fortinet configuration saved successfully.
        </div>
      )}
      {fortinetError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs font-medium">{fortinetError}</div>
      )}
      {!canAdministerOrg && (
        <div className="text-xs text-gray-500 italic">Only an organization administrator can change the Fortinet configuration.</div>
      )}
      {fortinetLoading ? (
        <div className="flex items-center justify-center py-8"><span className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* FortiGate */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7"/></svg>
              <span className="text-xs font-semibold text-gray-700">FortiGate</span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">FortiGate URL</label>
                <input type="url" value={fgUrl} onChange={e => setFgUrl(e.target.value)} placeholder="https://192.168.1.1" disabled={!canAdministerOrg}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono disabled:bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  API Key {fortinetConfig?.fortigateApiKeySet && <span className="ml-1 font-normal text-green-600">· saved</span>}
                </label>
                <div className="relative">
                  <input type={showFgKey ? "text" : "password"} value={fgKey} onChange={e => setFgKey(e.target.value)} disabled={!canAdministerOrg}
                    placeholder={fortinetConfig?.fortigateApiKeySet ? "••••••••••••••• (enter new to replace)" : "FortiGate REST API key"}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono pr-8 disabled:bg-gray-50" />
                  <button type="button" onClick={() => setShowFgKey(p => !p)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
                    {showFgKey
                      ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                      : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">System → Administrators → REST API Admin in FortiOS</p>
              </div>
            </div>
          </div>

          {/* FortiManager */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              <span className="text-xs font-semibold text-gray-700">FortiManager <span className="font-normal text-gray-400">(optional)</span></span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">FortiManager URL</label>
                <input type="url" value={fmgUrl} onChange={e => setFmgUrl(e.target.value)} placeholder="https://fortimanager.example.com" disabled={!canAdministerOrg}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono disabled:bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  API Key {fortinetConfig?.fortimanagerApiKeySet && <span className="ml-1 font-normal text-green-600">· saved</span>}
                </label>
                <div className="relative">
                  <input type={showFmgKey ? "text" : "password"} value={fmgKey} onChange={e => setFmgKey(e.target.value)} disabled={!canAdministerOrg}
                    placeholder={fortinetConfig?.fortimanagerApiKeySet ? "••••••••••••••• (enter new to replace)" : "FortiManager API key"}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono pr-8 disabled:bg-gray-50" />
                  <button type="button" onClick={() => setShowFmgKey(p => !p)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
                    {showFmgKey
                      ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                      : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">ADOM <span className="font-normal text-gray-400">(default: root)</span></label>
                <input type="text" value={fortinetAdom} onChange={e => setFortinetAdom(e.target.value)} placeholder="root" disabled={!canAdministerOrg}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-50" />
              </div>
            </div>
          </div>

          {/* Scan settings + actions */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Scan Settings</span></div>
            <div className="p-4 flex flex-wrap items-center gap-6">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scan Frequency</label>
                <select value={fortinetFrequency} onChange={e => setFortinetFrequency(Number(e.target.value))} disabled={!canAdministerOrg}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-50">
                  <option value={6}>Every 6 hours</option>
                  <option value={12}>Every 12 hours</option>
                  <option value={24}>Every 24 hours</option>
                  <option value={48}>Every 48 hours</option>
                  <option value={168}>Weekly</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer mt-5">
                <input type="checkbox" checked={fortinetEnabled} onChange={e => setFortinetEnabled(e.target.checked)} disabled={!canAdministerOrg}
                  className="rounded accent-orange-500" />
                <span className="text-xs font-medium text-gray-600">Enable automatic scanning</span>
              </label>
              <div className="w-full mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={fortinetAllowSelfSignedCert}
                    onChange={e => setFortinetAllowSelfSignedCert(e.target.checked)}
                    disabled={!canAdministerOrg}
                    className="rounded accent-orange-500" />
                  <span className="text-xs font-medium text-gray-600">Allow self-signed SSL certificate</span>
                </label>
                <p className="text-xs text-amber-600 mt-1 ml-5">
                  Warning: Enabling this disables SSL certificate validation. Only use for on-premises appliances with self-signed certificates.
                </p>
              </div>
            </div>
            {fortinetConfig && (
              <FortinetLastScan lastScannedAt={fortinetConfig.lastScannedAt}
                lastScanStatus={fortinetConfig.lastScanStatus} lastScanMessage={fortinetConfig.lastScanMessage} />
            )}
            {canAdministerOrg && (
              <div className="px-4 pb-4 flex flex-wrap items-center gap-3">
                <button onClick={handleFortinetSave} disabled={fortinetSaving}
                  className="flex items-center gap-1.5 text-xs px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 active:scale-95 transition-all">
                  {fortinetSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : "Save Settings"}
                </button>
                {fortinetConfig && (
                  <button onClick={handleFortinetDelete} disabled={fortinetDeleting}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 border border-red-200 text-red-500 rounded-lg font-semibold hover:bg-red-600 hover:text-white hover:border-red-600 disabled:opacity-50 active:scale-95 transition-all">
                    {fortinetDeleting ? "Removing…" : "Remove Config"}
                  </button>
                )}
                {fortinetConfig && (
                  <>
                    <button onClick={handleFortinetTest} disabled={fortinetTesting}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 border border-orange-200 text-orange-700 rounded-lg font-semibold hover:bg-orange-50 disabled:opacity-50 active:scale-95 transition-all">
                      {fortinetTesting ? "Testing…" : "Test connection"}
                    </button>
                    <button onClick={handleFortinetScan} disabled={fortinetScanning}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 border border-orange-200 text-orange-700 rounded-lg font-semibold hover:bg-orange-50 disabled:opacity-50 active:scale-95 transition-all">
                      Scan now
                    </button>
                  </>
                )}
              </div>
            )}
            {canAdministerOrg && fortinetConfig && fortinetScanStarted && (
              <div className="px-4 pb-3 text-xs text-green-700">Scan started. Results appear here in a few minutes.</div>
            )}
            {canAdministerOrg && fortinetConfig && fortinetTestResult && (
              <FortinetTestResult result={fortinetTestResult} />
            )}
            {fortinetConfig && (
              <FortinetTraceSwitch traceUntil={fortinetConfig.traceUntil} canManage={canFortinetTrace}
                saving={fortinetTraceSaving} onChange={handleFortinetTrace} />
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── SnowflakePanel ───────────────────────────────────────────────────────────
  const SfPermBadge = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
      {ok ? "✓" : "✗"} {label}
    </span>
  );

  const SnowflakePanel = () => (
    <div className="space-y-4">
      {snowflakeSaved && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-xs font-medium flex items-center gap-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
          Snowflake configuration saved successfully.
        </div>
      )}
      {snowflakeError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs font-medium">{snowflakeError}</div>
      )}
      {snowflakeLoading ? (
        <div className="flex items-center justify-center py-8"><span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Connection */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8M8 8h8M8 16h4"/></svg>
              <span className="text-xs font-semibold text-gray-700">Connection</span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Account Identifier <span className="text-red-500">*</span></label>
                <input type="text" value={sfAccountId} onChange={e => setSfAccountId(e.target.value)} disabled={!canSecurityInsights}
                  placeholder="e.g. dlvkpjn-yo59476"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
                <p className="text-xs text-gray-400 mt-1">Found in Snowflake Admin → Accounts</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Username <span className="text-red-500">*</span></label>
                <input type="text" value={sfUsername} onChange={e => setSfUsername(e.target.value)} disabled={!canSecurityInsights}
                  placeholder="e.g. AXARIX_SERVICE"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Service Role</label>
                <input type="text" value={sfServiceRole} onChange={e => setSfServiceRole(e.target.value.toUpperCase())} disabled={!canSecurityInsights}
                  placeholder="e.g. AXARIX_READER"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
                <p className="text-xs text-gray-400 mt-1">Explicitly activates this role on every connection. Required if DEFAULT_ROLE is not set correctly.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Warehouse <span className="text-red-500">*</span></label>
                <input type="text" value={sfWarehouse} onChange={e => setSfWarehouse(e.target.value)} disabled={!canSecurityInsights}
                  placeholder="e.g. COMPUTE_WH"
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
              </div>
              {/* Auth Mode */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-2">Authentication Method</label>
                <div className="flex gap-3">
                  {[{ label: "Password", value: 0 }, { label: "RSA Key-Pair (Recommended)", value: 1 }].map(opt => (
                    <label key={opt.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${sfAuthMode === opt.value ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                      <input type="radio" name="sfAuthMode" value={opt.value} checked={sfAuthMode === opt.value}
                        onChange={() => setSfAuthMode(opt.value)} disabled={!canSecurityInsights} className="accent-blue-500" />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              {sfAuthMode === 0 && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
                  <input type="password" value={sfPassword} onChange={e => setSfPassword(e.target.value)} disabled={!canSecurityInsights}
                    placeholder={snowflakeConfig ? "••••••••••••••• (enter new to replace)" : "Snowflake password"}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
                </div>
              )}
              {sfAuthMode === 1 && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">RSA Private Key (PEM)</label>
                  <textarea value={sfPrivateKey} onChange={e => setSfPrivateKey(e.target.value)} disabled={!canSecurityInsights} rows={5}
                    placeholder={snowflakeConfig
                      ? "Paste new PEM key to replace the saved key…"
                      : "-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----"}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono resize-none disabled:bg-gray-50" />
                  <p className="text-xs text-gray-400 mt-1">Generate with: <span className="font-mono">openssl genrsa 2048 | openssl pkcs8 -topk8 -nocrypt -out rsa_key.p8</span></p>
                </div>
              )}
            </div>
          </div>

          {/* Scan Modes */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                <span className="text-xs font-semibold text-gray-700">Detection Modes</span>
              </div>
              {snowflakeConfig && canSecurityInsights && (
                <button onClick={handleCopySetupSql} disabled={sfCopyingSql}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50 transition-all">
                  {sfCopyingSql ? "Copying…" : <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                    Copy Setup SQL
                  </>}
                </button>
              )}
            </div>
            <div className="p-4 space-y-3">
              {[
                { key: "custom", label: "Custom Table Import", desc: "Import findings from your configured SECURITY_RISKS table. Use this if you maintain your own risk table in Snowflake.", state: sfEnableCustomTable, set: setSfEnableCustomTable },
                { key: "native", label: "Native Risk Detection", desc: "Actively scan SNOWFLAKE.ACCOUNT_USAGE views to detect IAM gaps, privilege sprawl, brute-force attempts, and data exfiltration indicators — no customer table required.", state: sfEnableNativeScan, set: setSfEnableNativeScan },
                { key: "trust", label: "Trust Center Findings", desc: "Pull findings from SNOWFLAKE.TRUST_CENTER.FINDINGS (CIS Benchmark, Threat Intel, AI Security). Requires Enterprise tier and TRUST_CENTER_VIEWER role.", state: sfEnableTrustCenter, set: setSfEnableTrustCenter },
              ].map(mode => (
                <label key={mode.key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${mode.state ? "border-blue-200 bg-blue-50" : "border-gray-100 bg-gray-50"}`}>
                  <input type="checkbox" checked={mode.state} onChange={e => mode.set(e.target.checked)} disabled={!canSecurityInsights}
                    className="mt-0.5 accent-blue-500" />
                  <div>
                    <div className="text-xs font-semibold text-gray-700">{mode.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{mode.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Permission Check */}
          {snowflakeConfig && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/></svg>
                  <span className="text-xs font-semibold text-gray-700">Permission Status</span>
                </div>
                {canSecurityInsights && (
                  <button onClick={handleCheckPermissions} disabled={sfCheckingPerms || sourceBlocked("snowflake")}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg font-medium hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 transition-all">
                    {sfCheckingPerms ? <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />Checking…</> : "Check Permissions"}
                  </button>
                )}
              </div>
              <div className="p-4">
                {sfPermissions ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <SfPermBadge ok={sfPermissions.canConnect} label="Can Connect" />
                      <SfPermBadge ok={sfPermissions.accountUsage} label="ACCOUNT_USAGE" />
                      <SfPermBadge ok={sfPermissions.trustCenter} label="Trust Center" />
                      <SfPermBadge ok={sfPermissions.customTable} label="Custom Table" />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Shows what your Snowflake service account can access. Green means the role has permission — data is only fetched for modes you have enabled above.
                    </p>
                    {!sfPermissions.accountUsage && (
                      <p className="text-xs text-amber-600 mt-1">
                        ACCOUNT_USAGE access is required for native risk detection.{canSecurityInsights && <> Run "Copy Setup SQL" and execute it as ACCOUNTADMIN in your Snowflake account.</>}
                      </p>
                    )}
                    {!sfPermissions.canConnect && (
                      <p className="text-xs text-red-600 mt-1">
                        Cannot connect to Snowflake. Verify Account Identifier, Username, and credentials are correct.
                      </p>
                    )}
                  </div>
                ) : (
                  canSecurityInsights && <p className="text-xs text-gray-400">Click "Check Permissions" to verify which Snowflake views this service account can access.</p>
                )}
              </div>
            </div>
          )}

          {/* Data Source (for custom table mode) */}
          {sfEnableCustomTable && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7"/></svg>
                <span className="text-xs font-semibold text-gray-700">Custom Table Source</span>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Database <span className="text-red-500">*</span></label>
                  <input type="text" value={sfDatabase} onChange={e => setSfDatabase(e.target.value)} disabled={!canSecurityInsights}
                    placeholder="e.g. AXARIS_DB"
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Schema</label>
                  <input type="text" value={sfSchema} onChange={e => setSfSchema(e.target.value)} disabled={!canSecurityInsights}
                    placeholder="PUBLIC"
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Table Name</label>
                  <input type="text" value={sfTableName} onChange={e => setSfTableName(e.target.value)} disabled={!canSecurityInsights}
                    placeholder="SECURITY_RISKS"
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono disabled:bg-gray-50" />
                </div>
              </div>
            </div>
          )}

          {/* Sync Settings + Actions */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Sync Settings</span></div>
            <div className="p-4 flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={sfEnabled} onChange={e => setSfEnabled(e.target.checked)} disabled={!canSecurityInsights}
                  className="rounded accent-blue-500" />
                <span className="text-xs font-medium text-gray-600">Connector active — syncs via "Sync Now" below or the platform-wide Refresh Data action</span>
              </label>
            </div>
            {canSecurityInsights && (
              <div className="px-4 pb-4 flex items-center gap-3">
                <button onClick={handleSnowflakeSave} disabled={snowflakeSaving || (sourceBlocked("snowflake") && !snowflakeConfig)}
                  className="flex items-center gap-1.5 text-xs px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 active:scale-95 transition-all">
                  {snowflakeSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : "Save Settings"}
                </button>
                {snowflakeConfig && (<>
                  <button onClick={handleSnowflakeSync} disabled={snowflakeSyncing || sourceBlocked("snowflake")}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg font-semibold hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 active:scale-95 transition-all">
                    {snowflakeSyncing ? <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />Syncing…</> : <>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                      Sync Now
                    </>}
                  </button>
                  <button onClick={handleSnowflakeDelete} disabled={snowflakeDeleting}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 border border-red-200 text-red-500 rounded-lg font-semibold hover:bg-red-600 hover:text-white hover:border-red-600 disabled:opacity-50 active:scale-95 transition-all">
                    {snowflakeDeleting ? "Removing…" : "Remove Config"}
                  </button>
                </>)}
              </div>
            )}
            <div className="px-4 pb-3 space-y-0.5">
              {snowflakeConfig?.lastSyncAt && (
                <p className="text-xs text-gray-400">Last synced: {formatUtcDateTime(snowflakeConfig.lastSyncAt)}</p>
              )}
              {snowflakeConfig?.syncError && (
                <p className="text-xs text-red-500">Last sync error: {snowflakeConfig.syncError}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ── AzureDevOpsPanel ─────────────────────────────────────────────────────────
  const AzureDevOpsPanel = () => (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-xs text-indigo-800">
        Monitor SDLC governance controls — branch policies, PR approvals, pipeline stability, service connection security, exposed secrets, dependency CVEs, and SAST code coverage. Uses an <strong>Azure DevOps Source Code Connection</strong> whose PAT has read access to Project and Team, Code, Build, Service Connections, and Advanced Security scopes.
      </div>

      {connection?.azDevOpsEnabled && connection?.azDevOpsConnectionId && (
        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
          <span>✓</span>
          <span>Azure DevOps is connected. Controls are pulled on next Security Insights refresh.</span>
        </div>
      )}

      <div>
        <span className="text-xs font-semibold text-gray-700">Azure DevOps Connector Settings</span>
        <div className="mt-2 space-y-3">
          <div className="flex items-center gap-2">
            <input type="checkbox" id="azDevOpsEnabled" checked={connForm.azDevOpsEnabled} onChange={e => updateConnForm("azDevOpsEnabled", e.target.checked)} disabled={!canIntegrations} className="rounded accent-indigo-600" />
            <label htmlFor="azDevOpsEnabled" className="text-xs text-gray-600 font-medium cursor-pointer">Enable Azure DevOps</label>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Source Code Connection</label>
            {sourceCodeConnections.some(c => c.provider === "AzureDevOps") ? (
              <select
                value={connForm.azDevOpsConnectionId ?? ""}
                onChange={e => updateConnForm("azDevOpsConnectionId", e.target.value ? parseInt(e.target.value, 10) : null)}
                disabled={!canIntegrations || !connForm.azDevOpsEnabled}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50">
                <option value="">-- Select a connection --</option>
                {sourceCodeConnections.filter(c => c.provider === "AzureDevOps").map(c => (
                  <option key={c.id} value={c.id}>
                    {c.displayName || c.target}{c.baseUrl ? ` (${c.baseUrl})` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                No Azure DevOps connections configured — add one under Source Code Connections
                (this page), then select it here.
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              The connection's PAT needs: Project and Team (Read), Code (Read), Build (Read),
              Service Connections (Read), Advanced Security (Read). Code (Read) alone is enough
              for AI discovery / secrets scanning — the extra scopes serve these SDLC controls.
            </p>
            {connForm.azDevOpsEnabled && !connForm.azDevOpsConnectionId && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                Azure DevOps is enabled but no connection is selected — controls will not be
                collected until you pick one.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        <div className="font-semibold text-gray-700 mb-1">Controls provided:</div>
        <ul className="list-disc list-inside space-y-0.5 text-gray-500">
          {[
            "Branch protection policies on default branches",
            "Pull request approval gate compliance",
            "Pipeline build stability",
            "Service connection permission security",
            "Exposed secrets in repositories",
            "Critical open-source dependency CVEs",
            "SAST code security coverage",
          ].map(c => <li key={c}>{c}</li>)}
        </ul>
      </div>

      <button onClick={handleSaveConnection} disabled={connSaving || !canIntegrations || msBlocked}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
        style={{ background: "#4F46E5" }}>
        {connSaving ? "Saving…" : "Save Connection"}
      </button>
      {connSaveError && <div className="-mx-4">{connSaveErrorBar}</div>}
    </div>
  );

  // ── KnowBe4Panel ─────────────────────────────────────────────────────────────
  const knowBe4LoggingUntil = parseUtcDate(connection?.knowBe4DiagnosticLoggingUntil);
  const knowBe4LoggingOn = !!knowBe4LoggingUntil && knowBe4LoggingUntil.getTime() > Date.now();
  // Mirrors CanToggleIntegrationDiagnosticLogging (SuperAdmin or SupportAdmin). Optional calls:
  // older useRBAC test mocks in this folder don't define the two functions.
  const canToggleKnowBe4Logging = !!(isSuperAdmin?.() || isSupportAdmin?.());

  const KnowBe4Panel = () => (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
        <strong>KnowBe4 Reporting API</strong> (Platinum, Diamond, SAT Foundation or SAT Advanced subscription required). Generate an API key from your KSAT console under
        <strong> Account Settings → Account Integrations → API</strong>. Data is fetched on every Security Insights refresh.
      </div>

      {/* Status row */}
      {connection?.knowBe4Enabled && connection?.knowBe4ApiKeySet && (
        <KnowBe4SyncStatus region={connection.knowBe4Region} lastSyncAt={connection.knowBe4LastSyncAt}
          syncState={connection.knowBe4SyncState} />
      )}

      {/* Settings form */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">KnowBe4 Connector Settings</span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={connForm.knowBe4Enabled} onChange={e => updateConnForm("knowBe4Enabled", e.target.checked)} disabled={!canIntegrations} className="rounded accent-amber-500" />
              <span className="text-xs text-gray-600 font-medium">Enable KnowBe4</span>
            </label>
            {canIntegrations && (
              <button onClick={handleSaveConnection} disabled={connSaving || !connFormDirty || msBlocked}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {connSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : "Save Settings"}
              </button>
            )}
          </div>
        </div>
        {connSaveErrorBar}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">API Key <span className="text-red-500">*</span></label>
            <input type="password" value={connForm.knowBe4ApiKey} onChange={e => updateConnForm("knowBe4ApiKey", e.target.value)} disabled={!canIntegrations || !connForm.knowBe4Enabled}
              placeholder={connForm.knowBe4ApiKeySet ? "Key saved — enter new key to replace" : "Paste KnowBe4 Reporting API key"}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-gray-50 font-mono" />
            <div className="text-xs text-gray-400 mt-1">KSAT console → Account Settings → Account Integrations → API</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
            <select value={connForm.knowBe4Region} onChange={e => updateConnForm("knowBe4Region", e.target.value)} disabled={!canIntegrations || !connForm.knowBe4Enabled}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-gray-50">
              <option value="us">US (us.api.knowbe4.com)</option>
              <option value="eu">EU (eu.api.knowbe4.com)</option>
              <option value="ca">Canada (ca.api.knowbe4.com)</option>
              <option value="uk">UK (uk.api.knowbe4.com)</option>
              <option value="de">Germany (de.api.knowbe4.com)</option>
            </select>
            <div className="text-xs text-gray-400 mt-1">Match the region where your KSAT account is hosted</div>
          </div>
        </div>
        {canIntegrations && (
          <div className="px-4 pb-4 space-y-2">
            {msBlocked && <UnavailableNote />}
            <button onClick={handleKnowBe4Test} disabled={knowBe4Testing || !connection?.knowBe4ApiKeySet || msBlocked}
              title={!connection?.knowBe4ApiKeySet ? "Save the API key first" : undefined}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
              {knowBe4Testing ? <><span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Testing…</> : "Test Connection"}
            </button>
            {knowBe4TestResult && <KnowBe4TestResult result={knowBe4TestResult} />}
          </div>
        )}
      </div>

      {/* Support-only detailed logging (backend: CanToggleIntegrationDiagnosticLogging) */}
      {connection && canToggleKnowBe4Logging && (
        <div data-testid="knowbe4-diagnostic-logging" className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-semibold text-gray-700">
              Detailed logging (support): {knowBe4LoggingOn ? `On until ${formatUtcDateTime(connection.knowBe4DiagnosticLoggingUntil)}` : "Off"}
            </span>
            <button onClick={() => handleKnowBe4DiagnosticLogging(!knowBe4LoggingOn)} disabled={knowBe4LoggingSaving || msBlocked}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
              {knowBe4LoggingSaving && <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />}
              {knowBe4LoggingOn ? "Turn off" : "Turn on for 24 hours"}
            </button>
          </div>
          <div className="text-xs text-gray-400">Logs KnowBe4 responses with names and emails masked, for troubleshooting.</div>
          {msBlocked && <UnavailableNote />}
          {knowBe4LoggingError && <div className="text-xs text-red-700">Couldn't change detailed logging. Try again.</div>}
        </div>
      )}

      {/* What gets pulled */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Data Collected</span></div>
        <div className="p-3 space-y-1.5">
          {[
            { icon: "🎣", label: "Phishing Risk (Phish-Prone %)", desc: "Average click rate from 5 most recent security tests → GRC control", api: "/v1/phishing/security_tests" },
            { icon: "📚", label: "Training Completion Rate",      desc: "Passed + completed enrollments vs total → compliance evidence",    api: "/v1/training/enrollments" },
            { icon: "⚠️", label: "Average User Risk Score",       desc: "KnowBe4 risk score (0–100) across all active users",               api: "/v1/users" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-2 rounded-lg border border-amber-100 bg-amber-50">
              <span className="text-base flex-shrink-0 mt-0.5">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-800">{item.label}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
              <code className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-mono flex-shrink-0">{item.api}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );


  // ── CloudAppsPanel ──────────────────────────────────────────────────────────
  const CloudAppsPanel = () => (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-xs text-indigo-800">
        <strong>Microsoft Defender for Cloud Apps (MDCA)</strong> has two independent capabilities. <strong>Alerts</strong> reuse
        the existing Microsoft connection's Graph token — no extra credential — but need the Axaris app's
        <strong> SecurityAlert.Read.All</strong> Graph application permission; existing connections must re-grant admin
        consent after it is added. <strong>Discovery</strong> (shadow-IT
        inventory) requires a portal API token — generate one at the Defender portal (security.microsoft.com) under
        <strong> Settings → Cloud Apps → API tokens</strong>, then copy the token and the portal's base URL here.
      </div>

      {/* Status row */}
      {(connection?.cloudAppsAlertsEnabled || (connection?.cloudAppsDiscoveryEnabled && connection?.cloudAppsApiTokenSet)) && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
          <span>✅</span>
          <span>
            {connection?.cloudAppsAlertsEnabled && "Cloud App Alerts enabled"}
            {connection?.cloudAppsAlertsEnabled && connection?.cloudAppsDiscoveryEnabled && " · "}
            {connection?.cloudAppsDiscoveryEnabled && connection?.cloudAppsApiTokenSet && "Cloud App Discovery enabled"}
            {" — signals are pulled on next refresh."}
          </span>
        </div>
      )}

      {/* Alerts settings — toggle lives in the "Enabled Sources" list above (no credential needed) */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">Cloud App Alerts</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${connForm.cloudAppsAlertsEnabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
            {connForm.cloudAppsAlertsEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>
        <div className="p-4 text-xs text-gray-500">
          Pulls MDCA-sourced alerts (data exfiltration, unsanctioned app activity, high-severity incidents) via Microsoft
          Graph <code className="bg-gray-100 px-1 py-0.5 rounded">security/alerts_v2</code>. Requires no separate credential, but
          needs the <strong>SecurityAlert.Read.All</strong> Graph application permission — if the Microsoft connection was
          set up before it was added, re-grant admin consent. Toggle <strong>Cloud App Alerts</strong> in
          <strong> Enabled Sources</strong> above to turn this on or off.
        </div>
      </div>

      {/* Discovery settings */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">Cloud App Discovery (Shadow IT)</span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={connForm.cloudAppsDiscoveryEnabled} onChange={e => updateConnForm("cloudAppsDiscoveryEnabled", e.target.checked)} disabled={!canIntegrations} className="rounded accent-indigo-600" />
              <span className="text-xs text-gray-600 font-medium">Enable</span>
            </label>
            {canIntegrations && (
              <button onClick={handleSaveConnection} disabled={connSaving || !connFormDirty || msBlocked}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {connSaving ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</> : "Save Settings"}
              </button>
            )}
          </div>
        </div>
        {connSaveErrorBar}
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Portal URL <span className="text-red-500">*</span></label>
            <input type="text" value={connForm.cloudAppsPortalUrl} onChange={e => updateConnForm("cloudAppsPortalUrl", e.target.value)} disabled={!canIntegrations || !connForm.cloudAppsDiscoveryEnabled}
              placeholder="https://contoso.us2.portal.cloudappsecurity.com"
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-gray-50 font-mono" />
            <div className="text-xs text-gray-400 mt-1">Defender portal → Settings → Cloud Apps → API tokens (shown alongside the token)</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">API Token <span className="text-red-500">*</span></label>
            <input type="password" value={connForm.cloudAppsApiToken} onChange={e => updateConnForm("cloudAppsApiToken", e.target.value)} disabled={!canIntegrations || !connForm.cloudAppsDiscoveryEnabled}
              placeholder={connForm.cloudAppsApiTokenSet ? "Token saved — enter new token to replace" : "Paste MCAS portal API token"}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-gray-50 font-mono" />
            <div className="text-xs text-gray-400 mt-1">Settings → Cloud Apps → API tokens → Generate token</div>
          </div>
        </div>
      </div>

      {/* What gets pulled */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100"><span className="text-xs font-semibold text-gray-700">Data Collected</span></div>
        <div className="p-3 space-y-1.5">
          {[
            { icon: "🚨", label: "Active Alerts", desc: "Open MDCA alerts (status = new or in progress) → GRC control", api: "security/alerts_v2" },
            { icon: "⚠️", label: "High-Severity Alerts", desc: "Alerts flagged severity = high → priority triage control", api: "security/alerts_v2" },
            { icon: "📤", label: "Data Exfiltration Signals", desc: "Mass download, unusual file share, impossible travel patterns", api: "security/alerts_v2" },
            { icon: "🚫", label: "Unsanctioned App Activity", desc: "Alerts referencing unsanctioned/shadow-IT app usage", api: "security/alerts_v2" },
            { icon: "🗂️", label: "Shadow IT Volume", desc: "Total discovered apps vs. sanctioned ratio", api: "discovery/discovered_apps" },
            { icon: "🔥", label: "High-Risk Discovered Apps", desc: "Discovered apps in the MDCA high-risk score band", api: "discovery/discovered_apps" },
            { icon: "🎯", label: "Unsanctioned High-Risk Apps", desc: "The headline shadow-IT exposure — unsanctioned AND high-risk", api: "discovery/discovered_apps" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-2 rounded-lg border border-indigo-100 bg-indigo-50">
              <span className="text-base flex-shrink-0 mt-0.5">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-800">{item.label}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
              <code className="text-xs text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded font-mono flex-shrink-0">{item.api}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-theme-primary py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Settings className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-content-primary">Integrations</h1>
                <p className="text-sm text-gray-500 mt-0.5">Connect external tools and security data sources to your Axaris AI workspace.</p>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
                <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
              </div>
            </div>
          )}

          {/* ── ITSM & Ticketing section ────────────────────────────────────── */}
          {canITSM && <div className="mb-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2">ITSM &amp; Ticketing</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">

              {/* Jira card */}
              <div data-testid="itsm-card-jira" className="group bg-white rounded-2xl border-2 border-gray-200 p-5 transition-all duration-200 hover:border-blue-400 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#EFF4FF" }}>
                    <svg className="w-6 h-6" style={{ color: "#0052CC" }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-gray-900">Jira</h3>
                      {jiraStatus?.isConfigured
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Connected</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>}
                    </div>
                    {ticketingBlocked && <UnavailableNote className="mb-1" />}
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">Create and track tickets directly from remediations. Requires Atlassian Cloud OAuth.</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => setShowJiraModal(true)} disabled={ticketingBlocked}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                        {jiraStatus?.isConfigured ? "Reconfigure →" : "Configure →"}
                      </button>
                      {jiraStatus?.isConfigured && (
                        <>
                          {jiraStatus.instanceUrl && (
                            <a href={`https://${jiraStatus.instanceUrl}`} target="_blank" rel="noopener noreferrer"
                              className="text-xs font-medium px-3 py-1.5 rounded-lg text-gray-500 hover:text-gray-700 flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" /> Open
                            </a>
                          )}
                          <button onClick={handleDisconnectJira} disabled={disconnecting}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1 disabled:opacity-50">
                            <Trash2 className="w-3 h-3" /> {disconnecting ? "Removing…" : "Disconnect"}
                          </button>
                        </>
                      )}
                    </div>
                    {jiraStatus?.requiresReauthorization && (
                      <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" />
                        <p className="text-xs text-yellow-800">Needs reauthorization.
                          <button onClick={() => setShowJiraModal(true)} disabled={ticketingBlocked} className="ml-1 text-yellow-700 underline disabled:opacity-40 disabled:cursor-not-allowed">Reauthorize</button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ServiceNow card */}
              <div data-testid="itsm-card-servicenow" className="group bg-white rounded-2xl border-2 border-gray-200 p-5 transition-all duration-200 hover:border-green-400 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#EFFFF5" }}>
                    <svg className="w-6 h-6" style={{ color: "#16A34A" }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 21.6c-5.302 0-9.6-4.298-9.6-9.6S6.698 2.4 12 2.4s9.6 4.298 9.6 9.6-4.298 9.6-9.6 9.6zm0-15.6a6 6 0 1 0 0 12 6 6 0 0 0 0-12z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-gray-900">ServiceNow</h3>
                      {serviceNowStatus?.isConfigured
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Connected{serviceNowStatus.authType === "ApiKey" ? " (API Key)" : ""}</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>}
                    </div>
                    {ticketingBlocked && <UnavailableNote className="mb-1" />}
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">Push remediation actions to ServiceNow incidents and change requests automatically.</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => setShowServiceNowModal(true)} disabled={ticketingBlocked}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-600 hover:text-white hover:border-green-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                        {serviceNowStatus?.isConfigured ? "Reconfigure →" : "Configure →"}
                      </button>
                      {serviceNowStatus?.isConfigured && (
                        <>
                          {serviceNowStatus.instanceUrl && (
                            <a href={`https://${serviceNowStatus.instanceUrl}`} target="_blank" rel="noopener noreferrer"
                              className="text-xs font-medium px-3 py-1.5 rounded-lg text-gray-500 hover:text-gray-700 flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" /> Open
                            </a>
                          )}
                          <button onClick={handleDisconnectServiceNow} disabled={disconnectingServiceNow}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1 disabled:opacity-50">
                            <Trash2 className="w-3 h-3" /> {disconnectingServiceNow ? "Removing…" : "Disconnect"}
                          </button>
                        </>
                      )}
                    </div>
                    {serviceNowStatus?.requiresReauthorization && (
                      <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" />
                        <p className="text-xs text-yellow-800">Needs reauthorization.
                          <button onClick={() => setShowServiceNowModal(true)} disabled={ticketingBlocked} className="ml-1 text-yellow-700 underline disabled:opacity-40 disabled:cursor-not-allowed">Reauthorize</button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>}

          {/* ── Security Intelligence section ───────────────────────────────── */}
          {canIntegrations && <div className="mb-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2">Security Intelligence</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

              {/* Microsoft 365 card */}
              {[
                {
                  key: "microsoft",
                  name: "Microsoft 365 / Azure",
                  desc: "Secure Score, Entra ID, Defender for Cloud, and Sentinel signals for GRC compliance.",
                  iconBg: "#EFF6FF",
                  iconColor: "#0078D4",
                  icon: (
                    <svg className="w-6 h-6" viewBox="0 0 21 21" fill="currentColor">
                      <path d="M0 0h10v10H0zm11 0h10v10H11zM0 11h10v10H0zm11 0h10v10H11z"/>
                    </svg>
                  ),
                  badge: connLoading
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium">Loading…</span>
                    : connStatus === "Connected"
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Connected</span>
                      : connStatus === "Pending"
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Pending consent</span>
                        : connStatus === "Error"
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Error</span>
                          : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#0078D4",
                },
                {
                  key: "cloudapps",
                  name: "Defender for Cloud Apps",
                  desc: "MDCA alerts (exfiltration, unsanctioned app activity) and Cloud Discovery shadow-IT inventory.",
                  iconBg: "#EEF2FF",
                  iconColor: "#4F46E5",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 00.5-9.978A6.5 6.5 0 006.5 9.5 4.5 4.5 0 003 15z"/>
                    </svg>
                  ),
                  badge: (connection?.cloudAppsAlertsEnabled || (connection?.cloudAppsDiscoveryEnabled && connection?.cloudAppsApiTokenSet))
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Enabled</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#4F46E5",
                },
                {
                  key: "okta",
                  name: "Okta",
                  desc: "Identity, MFA coverage, SSO application access policies, and user lifecycle controls.",
                  iconBg: "#F0F9FF",
                  iconColor: "#0284C7",
                  icon: (
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1a11 11 0 100 22A11 11 0 0012 1zm0 18a7 7 0 110-14 7 7 0 010 14z"/>
                    </svg>
                  ),
                  badge: oktaConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{oktaConnectors.length} connector{oktaConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#0284C7",
                },
                {
                  key: "aws",
                  name: "AWS Security Hub",
                  desc: "GuardDuty findings, CloudTrail audit logs, S3 public access blocks, and Security Hub standards.",
                  iconBg: "#FFF7ED",
                  iconColor: "#EA580C",
                  icon: (
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 18a4 4 0 01-.11-7.986A5.5 5.5 0 006.613 12H6a4 4 0 000 8h13z"/>
                    </svg>
                  ),
                  badge: awsConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{awsConnectors.length} connector{awsConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#EA580C",
                },
                {
                  key: "gcp",
                  name: "Google Cloud",
                  desc: "Security Command Center findings, threat detection, audit logging, log export, and organization policies.",
                  iconBg: "#EFF6FF",
                  iconColor: "#2563EB",
                  icon: (
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 18a4 4 0 01-.11-7.986A5.5 5.5 0 006.613 12H6a4 4 0 000 8h13z"/>
                    </svg>
                  ),
                  badge: gcpConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{gcpConnectors.length} connector{gcpConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#2563EB",
                },
                {
                  key: "tenable",
                  name: "Tenable.io",
                  desc: "Vulnerability scan findings, asset coverage, patch status, and risk scoring from Tenable.io.",
                  iconBg: "#F0FDFA",
                  iconColor: "#0D9488",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                  ),
                  badge: tenableConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{tenableConnectors.length} connector{tenableConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#0D9488",
                },
                {
                  key: "splunk",
                  name: "Splunk",
                  desc: "SIEM log aggregation, security alerting, authentication monitoring, and threat detection from Splunk Enterprise or Cloud.",
                  iconBg: "#FAF5FF",
                  iconColor: "#7C3AED",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  ),
                  badge: splunkConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{splunkConnectors.length} connector{splunkConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#7C3AED",
                },
                {
                  key: "wiz",
                  name: "Wiz",
                  desc: "Cloud Security Posture Management (CSPM) findings, toxic combinations, and control compliance from Wiz.",
                  iconBg: "#FAF5FF",
                  iconColor: "#9333EA",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z"/>
                    </svg>
                  ),
                  badge: wizConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{wizConnectors.length} connector{wizConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#9333EA",
                },
                ...(showAiIntelligence ? [
                {
                  key: "openai",
                  name: "OpenAI",
                  desc: "AI Intelligence: OpenAI organization inventory — projects, members, API keys, models in use, and spend via the Admin API.",
                  iconBg: "#F0FDFA",
                  iconColor: "#0F766E",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
                    </svg>
                  ),
                  badge: openAiConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{openAiConnectors.length} connector{openAiConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#0F766E",
                },
                {
                  key: "anthropic",
                  name: "Anthropic",
                  desc: "AI Intelligence: Anthropic organization inventory — workspaces and API keys via the Admin API.",
                  iconBg: "#FFF1F2",
                  iconColor: "#BE123C",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
                    </svg>
                  ),
                  badge: anthropicConnectors.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{anthropicConnectors.length} connector{anthropicConnectors.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#BE123C",
                },
                ] : []),
                {
                  key: "knowbe4",
                  name: "KnowBe4 KSAT",
                  desc: "Security awareness training — phishing simulation results, training completion rates, and user risk scores.",
                  iconBg: "#FFFBEB",
                  iconColor: "#D97706",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    </svg>
                  ),
                  badge: connection?.knowBe4Enabled && connection?.knowBe4ApiKeySet
                    ? (connection.knowBe4SyncState && !connection.knowBe4SyncState.success
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Needs attention</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Connected</span>)
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#D97706",
                },
                {
                  key: "fortinet",
                  name: "Fortinet",
                  desc: "FortiGate and FortiManager security signals — firewall policy coverage, threat detections, and device posture.",
                  iconBg: "#FFF7ED",
                  iconColor: "#EA580C",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  ),
                  badge: fortinetConfig
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Configured</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#EA580C",
                },
                {
                  key: "snowflake",
                  name: "Snowflake",
                  desc: "Data warehouse security findings import — pull pre-stored security findings from your Snowflake data warehouse.",
                  iconBg: "#EFF6FF",
                  iconColor: "#2563EB",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8M8 8h8M8 16h4"/>
                    </svg>
                  ),
                  badge: snowflakeConfig
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Configured</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#2563EB",
                },
                {
                  key: "sourcecode",
                  name: "Source Code Connections",
                  desc: "GitHub / GitLab connections with per-connection tokens — used by Secrets Scanning targets and AI Asset Discovery.",
                  iconBg: "#F9FAFB",
                  iconColor: "#111827",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                    </svg>
                  ),
                  badge: sourceCodeConnections.length > 0
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{sourceCodeConnections.length} connection{sourceCodeConnections.length !== 1 ? "s" : ""}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#111827",
                },
                {
                  key: "azdevops",
                  name: "Azure DevOps",
                  desc: "SDLC governance — branch policies, PR approvals, pipeline stability, service connections, secrets, and SAST coverage.",
                  iconBg: "#EEF2FF",
                  iconColor: "#4F46E5",
                  icon: (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                  ),
                  badge: connection?.azDevOpsEnabled && connection?.azDevOpsConnectionId
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Connected</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Not configured</span>,
                  activeColor: "#4F46E5",
                },
              ].map(card => (
                <div key={card.key} data-testid={`integration-card-${card.key}`}
                  className={`group bg-white rounded-2xl border-2 p-5 transition-all duration-200 cursor-pointer
                    ${expandedSection === card.key
                      ? "shadow-xl"
                      : "border-gray-200 hover:shadow-lg"}`}
                  style={expandedSection === card.key ? { borderColor: card.activeColor } : {}}
                  onMouseEnter={e => { if (expandedSection !== card.key) e.currentTarget.style.borderColor = card.activeColor; }}
                  onMouseLeave={e => { if (expandedSection !== card.key) e.currentTarget.style.borderColor = ""; }}
                  onClick={() => toggleSection(card.key)}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: card.iconBg, color: card.iconColor }}>
                      {card.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900">{card.name}</h3>
                        {card.badge}
                      </div>
                      {(sourceBlocked(card.key) || platformBlocked(card.key)) && <UnavailableNote className="mb-1" />}
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{card.desc}</p>
                      <button
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
                        style={expandedSection === card.key
                          ? { background: card.activeColor, color: "#fff", border: `1px solid ${card.activeColor}` }
                          : { background: "#F9FAFB", color: "#374151", border: "1px solid #E5E7EB" }}
                        onClick={e => { e.stopPropagation(); toggleSection(card.key); }}>
                        {expandedSection === card.key ? "Close ×" : "Configure →"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Expanded panel */}
            {expandedSection && (
              <div ref={expandedPanelRef} data-testid="integration-expanded-panel" className="bg-white rounded-2xl border-2 border-indigo-200 shadow-xl p-6 mb-4 transition-all duration-200">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                  <h2 className="text-sm font-bold text-gray-900">
                    Configure {expandedSection === "microsoft" ? "Microsoft 365 / Azure" : expandedSection === "okta" ? "Okta" : expandedSection === "aws" ? "AWS Security Hub" : expandedSection === "gcp" ? "Google Cloud" : expandedSection === "tenable" ? "Tenable.io" : expandedSection === "wiz" ? "Wiz" : expandedSection === "knowbe4" ? "KnowBe4 KSAT" : expandedSection === "cloudapps" ? "Defender for Cloud Apps" : expandedSection === "fortinet" ? "Fortinet" : expandedSection === "azdevops" ? "Azure DevOps" : expandedSection === "snowflake" ? "Snowflake" : expandedSection === "sourcecode" ? "Source Code Connections" : expandedSection === "openai" ? "OpenAI" : expandedSection === "anthropic" ? "Anthropic" : "Splunk"}
                  </h2>
                  <button onClick={() => setExpandedSection(null)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all text-lg font-light">
                    ×
                  </button>
                </div>
                {expandedSection === "microsoft" && MicrosoftPanel()}
                {expandedSection === "okta" && OktaPanel()}
                {expandedSection === "aws" && AwsPanel()}
                {expandedSection === "gcp" && GcpPanel()}
                {expandedSection === "tenable" && TenablePanel()}
                {expandedSection === "splunk" && SplunkPanel()}
                {expandedSection === "wiz" && WizPanel()}
                {expandedSection === "openai" && (
                  <AiProviderConnectorPanel
                    sourceSystem="OPENAI"
                    label="OpenAI"
                    keyField="openAiAdminKey"
                    keyLabel="Admin API Key"
                    keyPlaceholder="sk-admin-…"
                    helpText={"Create an Admin API key at platform.openai.com → Settings → Admin keys (organization Owner only). Admin keys grant organization-wide management access — use a dedicated key for Axaris and rotate it like any credential. Used exclusively by AI Intelligence discovery: read-only inventory of projects, members, API keys, models in use, and 30-day spend. No GRC controls are generated."}
                    extraKeys={{
                      field: "openAiProjectKeys",
                      label: "Project API Keys",
                      placeholder: "sk-proj-…\nsk-proj-…",
                      clearLabel: "Remove all project keys",
                      help: [
                        "Agents are read with project keys, not the admin key. Create an application API key in each OpenAI project you want covered (platform.openai.com → your project → API keys) and grant it api.agents.read. An organization admin key cannot read agents even with full permissions. Agents in projects you do not supply a key for are not visible to Axaris — that is not the same as those projects having no agents.",
                        "When OpenAI identifies a key's project, Axaris places that key's agents in the project and shows the project's owner as their owner. Agents whose project cannot be identified are shown without a project. AI Intelligence shows, for each OpenAI project, whether its agents are visible. New keys are added to the saved ones; choose Replace all saved keys when editing to start over.",
                      ],
                      modeField: "openAiProjectKeysMode",
                      countField: "openAiProjectKeyCount",
                    }}
                    connectors={openAiConnectors}
                    onChanged={fetchConnectors}
                    canManage={canSecurityInsights}
                    unavailable={sourceBlocked("openai")}
                    requestConfirmAsync={requestConfirmAsync}
                  />
                )}
                {expandedSection === "anthropic" && (
                  <AiProviderConnectorPanel
                    sourceSystem="ANTHROPIC"
                    label="Anthropic"
                    keyField="anthropicAdminKey"
                    keyLabel="Admin API Key"
                    keyPlaceholder="sk-ant-admin-…"
                    helpText={"Create an admin key at console.anthropic.com → Settings → Admin keys (organization admin role; unavailable on individual accounts — convert to an organization in Console settings first). Admin keys grant organization-wide management access — use a dedicated key for Axaris. Used exclusively by AI Intelligence discovery: read-only inventory of workspaces and API keys. No GRC controls are generated."}
                    scalarFields={ANTHROPIC_COMPLIANCE_FIELDS}
                    connectors={anthropicConnectors}
                    onChanged={fetchConnectors}
                    canManage={canSecurityInsights}
                    unavailable={sourceBlocked("anthropic")}
                    requestConfirmAsync={requestConfirmAsync}
                  />
                )}
                {expandedSection === "knowbe4" && KnowBe4Panel()}
                {expandedSection === "cloudapps" && CloudAppsPanel()}
                {expandedSection === "fortinet" && FortinetPanel()}
                {expandedSection === "snowflake" && SnowflakePanel()}
                {expandedSection === "azdevops" && AzureDevOpsPanel()}
                {expandedSection === "sourcecode" && (
                  <SourceCodeConnectionsPanel
                    connections={sourceCodeConnections}
                    onChanged={fetchSourceCodeConnections}
                    canManage={canIntegrations}
                  />
                )}
              </div>
            )}
          </div>}

          {/* ── Coming Soon section ─────────────────────────────────────────── */}
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2">Coming Soon</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">

              {/* More coming placeholder */}
              <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 p-5 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                  </div>
                  <p className="text-xs font-medium text-gray-500">More integrations coming</p>
                  <p className="text-xs text-gray-400 mt-1">CrowdStrike, SentinelOne, Qualys and more</p>
                </div>
              </div>

            </div>
          </div>

          {/* ── Integration Assignments section ──────────────────────────────── */}
          {canManageAssignments && (
            <IntegrationAssignmentPanel organizationId={selectedOrg?.organizationId} />
          )}

        </div>
      </div>

      {/* Jira Configuration Modal */}
      {showJiraModal && (
        <JiraConfigurationModal onClose={() => setShowJiraModal(false)} onSuccess={handleJiraConfigured} />
      )}

      {/* ServiceNow Configuration Modal */}
      {showServiceNowModal && (
        <ServiceNowConfigurationModal onClose={() => setShowServiceNowModal(false)} onSuccess={handleServiceNowConfigured} />
      )}

      {/* Single-step "Are you sure?" dialog for destructive connection removals + source disables.
          requireTyping={false}: no typed Confirm — these are reversible by reconnecting. */}
      <ConfirmDeleteModal
        isOpen={!!confirmModal}
        onClose={() => closeConfirmModal(false)}
        onConfirm={() => closeConfirmModal(true)}
        title={confirmModal?.title || ""}
        message={confirmModal?.message || ""}
        confirmLabel="Remove"
        requireTyping={false}
      />
    </>
  );
};

export default IntegrationSettings;
